import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Sort } from '@angular/material/sort';
import { Type } from './sendbird.service';

/**
 * Demo Data Service
 * 
 * This service provides fake/demo data for all backend API endpoints.
 * Used in demo builds to replace all HTTP calls with static data.
 * 
 * All methods simulate async behavior with small delays to mimic real API calls.
 */
@Injectable({
  providedIn: 'root'
})
export class DemoDataService {

  // File storage prefix for localStorage
  private readonly FILE_STORAGE_PREFIX = 'demo_file_';

  // Demo user data - used for authentication
  private readonly DEMO_USER = {
    _id: 'demo_user_123',
    email: 'demo@example.com',
    firstName: 'Demo',
    lastName: 'User',
    name: 'Demo User',
    phone: '+1-555-0123',
    token: 'demo_token_abc123xyz',
    access_token: 'demo_sendbird_token_abc123xyz', // Sendbird access token for demo
    // Set demo user type to PGI-Admin so the app behaves as an admin user
    dup__of_type: 'PGI-Admin', // Matches environment.customKeys.roleAdmin
    mirror_1__1: 'demo_agency_123', // Agency ID
    text7__1: 'demo_user_id_123', // User ID
    agency_name: 'Demo Insurance Agency',
    sms_activated: true,
    hasPhone: true,
    maskedPhone: '******1234',
    business_state: 'California',
    payee_id: 'PAYEE123',
    text9: '2024-01-15', // Contract Date
    people17: 'John Smith', // Business Development Manager
    people8: 'Jane Doe', // Licensing Specialist
    people2: 'Bob Johnson', // Personal Lines Consultant
    people4: 'Alice Williams', // Commercial Business Consultant
    people40: 'Charlie Brown', // Accounting Contact
    people1: 'Diana Prince', // Commissions Contact
    newUserType: 'PGI-Admin',
    is_executive: true, // Enable commission overview in dropdown
    email0: 'demo@example.com',
    resources: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  // Demo handshake tokens (for MFA flow)
  private handshakeTokens: Map<string, any> = new Map();

  // Demo OTP codes (for verification)
  private readonly DEMO_OTP_CODE = '123456';
  private readonly DEMO_SMS_CODE = '654321';

  constructor() {
    // Initialize with some demo data
  }

  /**
   * Simulate async delay for demo API calls
   */
  private simulateDelay<T>(data: T, ms: number = 300): Observable<T> {
    return of(data).pipe(delay(ms));
  }

  // ============================================================================
  // AUTHENTICATION & USER ENDPOINTS
  // ============================================================================

  /**
   * POST /auth/check-status
   * Check if user exists and get status
   */
  checkUserStatus(email: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            exists: true,
            requiresPassword: true,
            requiresAzure: false
          },
          // For demo: return debugOtp when forcePasswordReset is true (first login scenario)
          // This allows auto-filling the code in the verify dialog
          debugOtp: this.DEMO_OTP_CODE
        });
      }, 300);
    });
  }

  /**
   * POST /auth/verify-credentials
   * Verify email and password
   */
  verifyCredentials(email: string, password: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Accept any credentials for demo
        const handshakeToken = `handshake_${Date.now()}`;
        this.handshakeTokens.set(handshakeToken, {
          email,
          password,
          timestamp: Date.now()
        });

        resolve({
          status: true,
          handshakeToken,
          results: {
            requiresSmsActivation: false,
            smsActivated: true,
            hasPhone: true,
            maskedPhone: '******1234'
          }
        });
      }, 500);
    });
  }

  /**
   * POST /auth/verify-azure-user
   * Verify Azure AD user
   */
  verifyAzureUser(handshakeToken: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            smsActivated: true,
            hasPhone: true,
            maskedPhone: '******1234'
          }
        });
      }, 300);
    });
  }

  /**
   * POST /auth/send-verification
   * Send OTP via email or SMS
   */
  sendVerification(handshakeToken: string, channel: 'email' | 'sms'): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Return the appropriate demo code based on channel for auto-fill
        const debugOtp = channel === 'email' ? this.DEMO_OTP_CODE : this.DEMO_SMS_CODE;
        resolve({
          status: true,
          message: `Verification code sent via ${channel}`,
          debugOtp: debugOtp, // Auto-fill code for demo purposes
          results: {
            sent: true,
            channel
          }
        });
      }, 400);
    });
  }

  /**
   * POST /auth/verify-otp
   * Verify OTP code
   */
  verifyOtp(handshakeToken: string, code: string): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Accept demo code or any code for demo purposes
        if (code === this.DEMO_OTP_CODE || code.length >= 4) {
          resolve({
            status: true,
            data: this.DEMO_USER,
            token: this.DEMO_USER.token
          });
        } else {
          reject({
            status: false,
            message: 'Invalid verification code'
          });
        }
      }, 500);
    });
  }

  /**
   * POST /user/update-phone
   * Update user phone number
   */
  updatePhone(handshakeToken: string, phoneNumber: string, updateMonday: boolean = false): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          message: 'Phone number updated successfully',
          debugOtp: this.DEMO_SMS_CODE, // Auto-fill SMS code for demo purposes
          results: {
            phone: phoneNumber,
            maskedPhone: `******${phoneNumber.slice(-4)}`
          }
        });
      }, 300);
    });
  }

  /**
   * POST /auth/skip-sms-activation
   * Skip SMS activation
   */
  skipSmsActivation(handshakeToken: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          data: this.DEMO_USER,
          token: this.DEMO_USER.token
        });
      }, 300);
    });
  }

  /**
   * POST /user/forgot-password/request
   * Request password reset
   */
  requestPasswordReset(email: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          message: 'Password reset code sent to your email',
          debugOtp: this.DEMO_OTP_CODE // Auto-fill code for demo purposes
        });
      }, 300);
    });
  }

  /**
   * POST /user/forgot-password/verify
   * Verify password reset code and set new password
   */
  verifyPasswordReset(email: string, verificationCode: string, newPassword: string): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Accept any code for demo
        if (verificationCode && newPassword) {
          resolve({
            status: true,
            message: 'Password reset successfully'
          });
        } else {
          reject({
            status: false,
            message: 'Invalid verification code'
          });
        }
      }, 500);
    });
  }

  /**
   * POST /user/auth/request
   * Legacy: Send verification code
   */
  sendCode(email: string, password?: string, verificationType?: 'email' | 'sms', phoneNumber?: string, handshakeToken?: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Return the appropriate demo code based on verification type for auto-fill
        const debugOtp = verificationType === 'sms' ? this.DEMO_SMS_CODE : this.DEMO_OTP_CODE;
        resolve({
          status: true,
          message: 'Verification code sent',
          debugOtp: debugOtp, // Auto-fill code for demo purposes
          handshakeToken: handshakeToken || `handshake_${Date.now()}`
        });
      }, 300);
    });
  }

  /**
   * POST /user/auth/request-sms
   * Request SMS code
   */
  sendSmsCode(handshakeToken: string, phoneNumber?: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          message: 'SMS code sent successfully',
          debugOtp: this.DEMO_SMS_CODE, // Auto-fill SMS code for demo purposes
          results: {
            sent: true
          }
        });
      }, 400);
    });
  }

  /**
   * POST /user/auth/verify-sms
   * Verify SMS code
   */
  verifySmsCode(handshakeToken: string, code: string, updatePhoneInDatabase?: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Accept demo code or any 6-digit code
        if (code === this.DEMO_SMS_CODE || (code.length === 6 && /^\d+$/.test(code))) {
          resolve({
            status: true,
            data: this.DEMO_USER,
            token: this.DEMO_USER.token
          });
        } else {
          reject({
            status: false,
            message: 'Invalid SMS code'
          });
        }
      }, 500);
    });
  }

  /**
   * POST /user/auth/token
   * Validate token
   */
  validateToken(token: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          data: this.DEMO_USER
        });
      }, 200);
    });
  }

  /**
   * POST /user/auth/logout
   * Logout user
   */
  logout(userId: string, deviceToken?: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          message: 'Logged out successfully'
        });
      }, 200);
    });
  }

  // ============================================================================
  // PGI / DASHBOARD ENDPOINTS
  // ============================================================================

  /**
   * POST /pgi
   * Get PGI overview/book overview data
   */
  getPGIOverview(filters: any = {}, agencyId?: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Build a PGI overview object that matches what the book-overview component expects
        const results: any = {
          // High-level summary (not heavily used by charts but can be shown elsewhere)
          totalPolicies: 1250,
          writtenPremium: 2450000,
          retentionRate: 0.87,
          growthRate: 0.12,

          // Top summary table (topData)
          top: [
            {
              type: 'Personal',
              value: 1500000,
              growth: 0.08,
              count: 800,
              growth_percentage: 0.05,
              value2: 1400000,
              growth2: 0.06,
              count2: 760,
              growth_percentage2: 0.04,
              count3: 600,
              group: 'Personal'
            },
            {
              type: 'Commercial',
              value: 800000,
              growth: 0.10,
              count: 450,
              growth_percentage: 0.06,
              value2: 750000,
              growth2: 0.07,
              count2: 430,
              growth_percentage2: 0.05,
              count3: 320,
              group: 'Commercial'
            },
            {
              type: 'Total',
              value: 2300000,
              growth: 0.09,
              count: 1250,
              growth_percentage: 0.055,
              value2: 2150000,
              growth2: 0.065,
              count2: 1190,
              growth_percentage2: 0.045,
              count3: 920,
              group: 'Total'
            }
          ],

          // Written premium breakdown used by setWrittenPremiumChart
          // All combinations: type (personal, commercial, other pl (life/financial), other cl (health/benefits), all)
          // group (carrier, lob, city, state, transactiontype, zipcode)
          written_premium: [
            // Personal - Carrier
            { type: 'personal', group: 'carrier', data: [
              { category: 'Allied', value: 450000, percent_of_total: 0.30 },
              { category: 'State Farm', value: 350000, percent_of_total: 0.23 },
              { category: 'Progressive', value: 300000, percent_of_total: 0.20 },
              { category: 'Others', value: 400000, percent_of_total: 0.27 }
            ]},
            // Personal - LOB
            { type: 'personal', group: 'lob', data: [
              { category: 'Auto', value: 600000, percent_of_total: 0.40 },
              { category: 'Homeowners', value: 450000, percent_of_total: 0.30 },
              { category: 'Umbrella', value: 300000, percent_of_total: 0.20 },
              { category: 'Others', value: 150000, percent_of_total: 0.10 }
            ]},
            // Personal - City
            { type: 'personal', group: 'city', data: [
              { category: 'Los Angeles', value: 350000, percent_of_total: 0.23 },
              { category: 'San Francisco', value: 280000, percent_of_total: 0.19 },
              { category: 'San Diego', value: 250000, percent_of_total: 0.17 },
              { category: 'Sacramento', value: 220000, percent_of_total: 0.15 },
              { category: 'Others', value: 400000, percent_of_total: 0.26 }
            ]},
            // Personal - State
            { type: 'personal', group: 'state', data: [
              { category: 'California', value: 800000, percent_of_total: 0.53 },
              { category: 'Texas', value: 350000, percent_of_total: 0.23 },
              { category: 'Florida', value: 200000, percent_of_total: 0.13 },
              { category: 'Others', value: 150000, percent_of_total: 0.10 }
            ]},
            // Personal - Transaction Type
            { type: 'personal', group: 'transactiontype', data: [
              { category: 'New Business', value: 600000, percent_of_total: 0.40 },
              { category: 'Renewal', value: 700000, percent_of_total: 0.47 },
              { category: 'Cancellation', value: 200000, percent_of_total: 0.13 }
            ]},
            // Personal - ZIP Code
            { type: 'personal', group: 'zipcode', data: [
              { category: '90001', value: 120000, percent_of_total: 0.08 },
              { category: '94102', value: 100000, percent_of_total: 0.07 },
              { category: '92101', value: 95000, percent_of_total: 0.06 },
              { category: '95814', value: 85000, percent_of_total: 0.06 },
              { category: 'Others', value: 1100000, percent_of_total: 0.73 }
            ]},
            // Commercial - Carrier
            { type: 'commercial', group: 'carrier', data: [
              { category: 'Allied', value: 250000, percent_of_total: 0.35 },
              { category: 'State Farm', value: 200000, percent_of_total: 0.28 },
              { category: 'Progressive', value: 150000, percent_of_total: 0.21 },
              { category: 'Others', value: 110000, percent_of_total: 0.16 }
            ]},
            // Commercial - LOB
            { type: 'commercial', group: 'lob', data: [
              { category: 'General Liability', value: 280000, percent_of_total: 0.39 },
              { category: 'Commercial Auto', value: 220000, percent_of_total: 0.31 },
              { category: 'Workers Comp', value: 150000, percent_of_total: 0.21 },
              { category: 'Others', value: 60000, percent_of_total: 0.09 }
            ]},
            // Commercial - City
            { type: 'commercial', group: 'city', data: [
              { category: 'Los Angeles', value: 200000, percent_of_total: 0.28 },
              { category: 'San Francisco', value: 180000, percent_of_total: 0.25 },
              { category: 'San Diego', value: 150000, percent_of_total: 0.21 },
              { category: 'Others', value: 180000, percent_of_total: 0.26 }
            ]},
            // Commercial - State
            { type: 'commercial', group: 'state', data: [
              { category: 'California', value: 450000, percent_of_total: 0.63 },
              { category: 'Texas', value: 150000, percent_of_total: 0.21 },
              { category: 'Florida', value: 80000, percent_of_total: 0.11 },
              { category: 'Others', value: 30000, percent_of_total: 0.05 }
            ]},
            // Commercial - Transaction Type
            { type: 'commercial', group: 'transactiontype', data: [
              { category: 'New Business', value: 350000, percent_of_total: 0.49 },
              { category: 'Renewal', value: 300000, percent_of_total: 0.42 },
              { category: 'Cancellation', value: 60000, percent_of_total: 0.09 }
            ]},
            // Commercial - ZIP Code
            { type: 'commercial', group: 'zipcode', data: [
              { category: '90001', value: 80000, percent_of_total: 0.11 },
              { category: '94102', value: 70000, percent_of_total: 0.10 },
              { category: '92101', value: 60000, percent_of_total: 0.08 },
              { category: 'Others', value: 500000, percent_of_total: 0.71 }
            ]},
            // Other PL (Life/Financial) - Carrier
            { type: 'other pl (life/financial)', group: 'carrier', data: [
              { category: 'Allied', value: 180000, percent_of_total: 0.36 },
              { category: 'State Farm', value: 150000, percent_of_total: 0.30 },
              { category: 'Progressive', value: 100000, percent_of_total: 0.20 },
              { category: 'Others', value: 70000, percent_of_total: 0.14 }
            ]},
            // Other PL (Life/Financial) - LOB
            { type: 'other pl (life/financial)', group: 'lob', data: [
              { category: 'Life Insurance', value: 250000, percent_of_total: 0.50 },
              { category: 'Financial Products', value: 200000, percent_of_total: 0.40 },
              { category: 'Others', value: 50000, percent_of_total: 0.10 }
            ]},
            // Other PL (Life/Financial) - City
            { type: 'other pl (life/financial)', group: 'city', data: [
              { category: 'Los Angeles', value: 150000, percent_of_total: 0.30 },
              { category: 'San Francisco', value: 120000, percent_of_total: 0.24 },
              { category: 'Others', value: 230000, percent_of_total: 0.46 }
            ]},
            // Other PL (Life/Financial) - State
            { type: 'other pl (life/financial)', group: 'state', data: [
              { category: 'California', value: 300000, percent_of_total: 0.60 },
              { category: 'Texas', value: 120000, percent_of_total: 0.24 },
              { category: 'Others', value: 80000, percent_of_total: 0.16 }
            ]},
            // Other PL (Life/Financial) - Transaction Type
            { type: 'other pl (life/financial)', group: 'transactiontype', data: [
              { category: 'New Business', value: 250000, percent_of_total: 0.50 },
              { category: 'Renewal', value: 200000, percent_of_total: 0.40 },
              { category: 'Cancellation', value: 50000, percent_of_total: 0.10 }
            ]},
            // Other PL (Life/Financial) - ZIP Code
            { type: 'other pl (life/financial)', group: 'zipcode', data: [
              { category: '90001', value: 60000, percent_of_total: 0.12 },
              { category: '94102', value: 50000, percent_of_total: 0.10 },
              { category: 'Others', value: 390000, percent_of_total: 0.78 }
            ]},
            // Other CL (Health/Benefits) - Carrier
            { type: 'other cl (health/benefits)', group: 'carrier', data: [
              { category: 'Allied', value: 120000, percent_of_total: 0.32 },
              { category: 'State Farm', value: 100000, percent_of_total: 0.27 },
              { category: 'Progressive', value: 80000, percent_of_total: 0.22 },
              { category: 'Others', value: 70000, percent_of_total: 0.19 }
            ]},
            // Other CL (Health/Benefits) - LOB
            { type: 'other cl (health/benefits)', group: 'lob', data: [
              { category: 'Health Insurance', value: 200000, percent_of_total: 0.54 },
              { category: 'Benefits', value: 150000, percent_of_total: 0.41 },
              { category: 'Others', value: 20000, percent_of_total: 0.05 }
            ]},
            // Other CL (Health/Benefits) - City
            { type: 'other cl (health/benefits)', group: 'city', data: [
              { category: 'Los Angeles', value: 100000, percent_of_total: 0.27 },
              { category: 'San Francisco', value: 90000, percent_of_total: 0.24 },
              { category: 'Others', value: 180000, percent_of_total: 0.49 }
            ]},
            // Other CL (Health/Benefits) - State
            { type: 'other cl (health/benefits)', group: 'state', data: [
              { category: 'California', value: 220000, percent_of_total: 0.59 },
              { category: 'Texas', value: 90000, percent_of_total: 0.24 },
              { category: 'Others', value: 60000, percent_of_total: 0.17 }
            ]},
            // Other CL (Health/Benefits) - Transaction Type
            { type: 'other cl (health/benefits)', group: 'transactiontype', data: [
              { category: 'New Business', value: 180000, percent_of_total: 0.49 },
              { category: 'Renewal', value: 150000, percent_of_total: 0.41 },
              { category: 'Cancellation', value: 40000, percent_of_total: 0.10 }
            ]},
            // Other CL (Health/Benefits) - ZIP Code
            { type: 'other cl (health/benefits)', group: 'zipcode', data: [
              { category: '90001', value: 50000, percent_of_total: 0.14 },
              { category: '94102', value: 40000, percent_of_total: 0.11 },
              { category: 'Others', value: 280000, percent_of_total: 0.75 }
            ]},
            // All - Carrier
            { type: 'all', group: 'carrier', data: [
              { category: 'Allied', value: 1000000, percent_of_total: 0.33 },
              { category: 'State Farm', value: 800000, percent_of_total: 0.27 },
              { category: 'Progressive', value: 630000, percent_of_total: 0.21 },
              { category: 'Others', value: 570000, percent_of_total: 0.19 }
            ]},
            // All - LOB
            { type: 'all', group: 'lob', data: [
              { category: 'Auto', value: 600000, percent_of_total: 0.20 },
              { category: 'Homeowners', value: 450000, percent_of_total: 0.15 },
              { category: 'General Liability', value: 280000, percent_of_total: 0.09 },
              { category: 'Commercial Auto', value: 220000, percent_of_total: 0.07 },
              { category: 'Others', value: 1450000, percent_of_total: 0.49 }
            ]},
            // All - City
            { type: 'all', group: 'city', data: [
              { category: 'Los Angeles', value: 800000, percent_of_total: 0.27 },
              { category: 'San Francisco', value: 670000, percent_of_total: 0.22 },
              { category: 'San Diego', value: 550000, percent_of_total: 0.18 },
              { category: 'Others', value: 980000, percent_of_total: 0.33 }
            ]},
            // All - State
            { type: 'all', group: 'state', data: [
              { category: 'California', value: 1770000, percent_of_total: 0.59 },
              { category: 'Texas', value: 710000, percent_of_total: 0.24 },
              { category: 'Florida', value: 360000, percent_of_total: 0.12 },
              { category: 'Others', value: 160000, percent_of_total: 0.05 }
            ]},
            // All - Transaction Type
            { type: 'all', group: 'transactiontype', data: [
              { category: 'New Business', value: 1380000, percent_of_total: 0.46 },
              { category: 'Renewal', value: 1350000, percent_of_total: 0.45 },
              { category: 'Cancellation', value: 270000, percent_of_total: 0.09 }
            ]},
            // All - ZIP Code
            { type: 'all', group: 'zipcode', data: [
              { category: '90001', value: 310000, percent_of_total: 0.10 },
              { category: '94102', value: 260000, percent_of_total: 0.09 },
              { category: '92101', value: 215000, percent_of_total: 0.07 },
              { category: 'Others', value: 2215000, percent_of_total: 0.74 }
            ]}
          ],

          // Policies in force breakdown used by setPoliciesInForceChart
          // All combinations: type (personal, commercial, other pl (life/financial), other cl (health/benefits), all)
          // group (carrier, lob, city, state, transactiontype, zipcode)
          policies_in_force: [
            // Personal - Carrier
            { type: 'personal', group: 'carrier', data: [
              { category: 'Allied', value: 320, percent_of_total: 0.32 },
              { category: 'State Farm', value: 280, percent_of_total: 0.28 },
              { category: 'Progressive', value: 250, percent_of_total: 0.25 },
              { category: 'Others', value: 150, percent_of_total: 0.15 }
            ]},
            // Personal - LOB
            { type: 'personal', group: 'lob', data: [
              { category: 'Auto', value: 450, percent_of_total: 0.45 },
              { category: 'Homeowners', value: 320, percent_of_total: 0.32 },
              { category: 'Umbrella', value: 150, percent_of_total: 0.15 },
              { category: 'Others', value: 80, percent_of_total: 0.08 }
            ]},
            // Personal - City
            { type: 'personal', group: 'city', data: [
              { category: 'Los Angeles', value: 280, percent_of_total: 0.28 },
              { category: 'San Francisco', value: 220, percent_of_total: 0.22 },
              { category: 'San Diego', value: 200, percent_of_total: 0.20 },
              { category: 'Others', value: 300, percent_of_total: 0.30 }
            ]},
            // Personal - State
            { type: 'personal', group: 'state', data: [
              { category: 'California', value: 650, percent_of_total: 0.65 },
              { category: 'Texas', value: 200, percent_of_total: 0.20 },
              { category: 'Florida', value: 100, percent_of_total: 0.10 },
              { category: 'Others', value: 50, percent_of_total: 0.05 }
            ]},
            // Personal - Transaction Type
            { type: 'personal', group: 'transactiontype', data: [
              { category: 'New Business', value: 450, percent_of_total: 0.45 },
              { category: 'Renewal', value: 480, percent_of_total: 0.48 },
              { category: 'Cancellation', value: 70, percent_of_total: 0.07 }
            ]},
            // Personal - ZIP Code
            { type: 'personal', group: 'zipcode', data: [
              { category: '90001', value: 95, percent_of_total: 0.10 },
              { category: '94102', value: 80, percent_of_total: 0.08 },
              { category: '92101', value: 75, percent_of_total: 0.08 },
              { category: 'Others', value: 750, percent_of_total: 0.74 }
            ]},
            // Commercial - Carrier
            { type: 'commercial', group: 'carrier', data: [
              { category: 'Allied', value: 180, percent_of_total: 0.36 },
              { category: 'State Farm', value: 150, percent_of_total: 0.30 },
              { category: 'Progressive', value: 120, percent_of_total: 0.24 },
              { category: 'Others', value: 50, percent_of_total: 0.10 }
            ]},
            // Commercial - LOB
            { type: 'commercial', group: 'lob', data: [
              { category: 'General Liability', value: 200, percent_of_total: 0.40 },
              { category: 'Commercial Auto', value: 160, percent_of_total: 0.32 },
              { category: 'Workers Comp', value: 100, percent_of_total: 0.20 },
              { category: 'Others', value: 40, percent_of_total: 0.08 }
            ]},
            // Commercial - City
            { type: 'commercial', group: 'city', data: [
              { category: 'Los Angeles', value: 160, percent_of_total: 0.32 },
              { category: 'San Francisco', value: 140, percent_of_total: 0.28 },
              { category: 'San Diego', value: 120, percent_of_total: 0.24 },
              { category: 'Others', value: 80, percent_of_total: 0.16 }
            ]},
            // Commercial - State
            { type: 'commercial', group: 'state', data: [
              { category: 'California', value: 360, percent_of_total: 0.72 },
              { category: 'Texas', value: 80, percent_of_total: 0.16 },
              { category: 'Florida', value: 40, percent_of_total: 0.08 },
              { category: 'Others', value: 20, percent_of_total: 0.04 }
            ]},
            // Commercial - Transaction Type
            { type: 'commercial', group: 'transactiontype', data: [
              { category: 'New Business', value: 280, percent_of_total: 0.56 },
              { category: 'Renewal', value: 200, percent_of_total: 0.40 },
              { category: 'Cancellation', value: 20, percent_of_total: 0.04 }
            ]},
            // Commercial - ZIP Code
            { type: 'commercial', group: 'zipcode', data: [
              { category: '90001', value: 60, percent_of_total: 0.12 },
              { category: '94102', value: 55, percent_of_total: 0.11 },
              { category: '92101', value: 50, percent_of_total: 0.10 },
              { category: 'Others', value: 335, percent_of_total: 0.67 }
            ]},
            // Other PL (Life/Financial) - Carrier
            { type: 'other pl (life/financial)', group: 'carrier', data: [
              { category: 'Allied', value: 140, percent_of_total: 0.35 },
              { category: 'State Farm', value: 120, percent_of_total: 0.30 },
              { category: 'Progressive', value: 80, percent_of_total: 0.20 },
              { category: 'Others', value: 60, percent_of_total: 0.15 }
            ]},
            // Other PL (Life/Financial) - LOB
            { type: 'other pl (life/financial)', group: 'lob', data: [
              { category: 'Life Insurance', value: 200, percent_of_total: 0.50 },
              { category: 'Financial Products', value: 160, percent_of_total: 0.40 },
              { category: 'Others', value: 40, percent_of_total: 0.10 }
            ]},
            // Other PL (Life/Financial) - City
            { type: 'other pl (life/financial)', group: 'city', data: [
              { category: 'Los Angeles', value: 120, percent_of_total: 0.30 },
              { category: 'San Francisco', value: 100, percent_of_total: 0.25 },
              { category: 'Others', value: 180, percent_of_total: 0.45 }
            ]},
            // Other PL (Life/Financial) - State
            { type: 'other pl (life/financial)', group: 'state', data: [
              { category: 'California', value: 240, percent_of_total: 0.60 },
              { category: 'Texas', value: 100, percent_of_total: 0.25 },
              { category: 'Others', value: 60, percent_of_total: 0.15 }
            ]},
            // Other PL (Life/Financial) - Transaction Type
            { type: 'other pl (life/financial)', group: 'transactiontype', data: [
              { category: 'New Business', value: 200, percent_of_total: 0.50 },
              { category: 'Renewal', value: 160, percent_of_total: 0.40 },
              { category: 'Cancellation', value: 40, percent_of_total: 0.10 }
            ]},
            // Other PL (Life/Financial) - ZIP Code
            { type: 'other pl (life/financial)', group: 'zipcode', data: [
              { category: '90001', value: 50, percent_of_total: 0.13 },
              { category: '94102', value: 40, percent_of_total: 0.10 },
              { category: 'Others', value: 310, percent_of_total: 0.77 }
            ]},
            // Other CL (Health/Benefits) - Carrier
            { type: 'other cl (health/benefits)', group: 'carrier', data: [
              { category: 'Allied', value: 100, percent_of_total: 0.32 },
              { category: 'State Farm', value: 85, percent_of_total: 0.27 },
              { category: 'Progressive', value: 70, percent_of_total: 0.23 },
              { category: 'Others', value: 55, percent_of_total: 0.18 }
            ]},
            // Other CL (Health/Benefits) - LOB
            { type: 'other cl (health/benefits)', group: 'lob', data: [
              { category: 'Health Insurance', value: 170, percent_of_total: 0.55 },
              { category: 'Benefits', value: 120, percent_of_total: 0.39 },
              { category: 'Others', value: 20, percent_of_total: 0.06 }
            ]},
            // Other CL (Health/Benefits) - City
            { type: 'other cl (health/benefits)', group: 'city', data: [
              { category: 'Los Angeles', value: 80, percent_of_total: 0.26 },
              { category: 'San Francisco', value: 70, percent_of_total: 0.23 },
              { category: 'Others', value: 160, percent_of_total: 0.51 }
            ]},
            // Other CL (Health/Benefits) - State
            { type: 'other cl (health/benefits)', group: 'state', data: [
              { category: 'California', value: 180, percent_of_total: 0.58 },
              { category: 'Texas', value: 80, percent_of_total: 0.26 },
              { category: 'Others', value: 50, percent_of_total: 0.16 }
            ]},
            // Other CL (Health/Benefits) - Transaction Type
            { type: 'other cl (health/benefits)', group: 'transactiontype', data: [
              { category: 'New Business', value: 150, percent_of_total: 0.48 },
              { category: 'Renewal', value: 130, percent_of_total: 0.42 },
              { category: 'Cancellation', value: 30, percent_of_total: 0.10 }
            ]},
            // Other CL (Health/Benefits) - ZIP Code
            { type: 'other cl (health/benefits)', group: 'zipcode', data: [
              { category: '90001', value: 40, percent_of_total: 0.13 },
              { category: '94102', value: 35, percent_of_total: 0.11 },
              { category: 'Others', value: 235, percent_of_total: 0.76 }
            ]},
            // All - Carrier
            { type: 'all', group: 'carrier', data: [
              { category: 'Allied', value: 740, percent_of_total: 0.33 },
              { category: 'State Farm', value: 635, percent_of_total: 0.28 },
              { category: 'Progressive', value: 520, percent_of_total: 0.23 },
              { category: 'Others', value: 315, percent_of_total: 0.14 }
            ]},
            // All - LOB
            { type: 'all', group: 'lob', data: [
              { category: 'Auto', value: 450, percent_of_total: 0.20 },
              { category: 'Homeowners', value: 320, percent_of_total: 0.14 },
              { category: 'General Liability', value: 200, percent_of_total: 0.09 },
              { category: 'Commercial Auto', value: 160, percent_of_total: 0.07 },
              { category: 'Others', value: 1089, percent_of_total: 0.50 }
            ]},
            // All - City
            { type: 'all', group: 'city', data: [
              { category: 'Los Angeles', value: 640, percent_of_total: 0.28 },
              { category: 'San Francisco', value: 530, percent_of_total: 0.24 },
              { category: 'San Diego', value: 440, percent_of_total: 0.20 },
              { category: 'Others', value: 620, percent_of_total: 0.28 }
            ]},
            // All - State
            { type: 'all', group: 'state', data: [
              { category: 'California', value: 1430, percent_of_total: 0.64 },
              { category: 'Texas', value: 380, percent_of_total: 0.17 },
              { category: 'Florida', value: 190, percent_of_total: 0.09 },
              { category: 'Others', value: 230, percent_of_total: 0.10 }
            ]},
            // All - Transaction Type
            { type: 'all', group: 'transactiontype', data: [
              { category: 'New Business', value: 1080, percent_of_total: 0.48 },
              { category: 'Renewal', value: 970, percent_of_total: 0.43 },
              { category: 'Cancellation', value: 180, percent_of_total: 0.08 }
            ]},
            // All - ZIP Code
            { type: 'all', group: 'zipcode', data: [
              { category: '90001', value: 245, percent_of_total: 0.11 },
              { category: '94102', value: 210, percent_of_total: 0.09 },
              { category: '92101', value: 200, percent_of_total: 0.09 },
              { category: 'Others', value: 1625, percent_of_total: 0.71 }
            ]}
          ],

          // Growth table and chart data used by setGrowthChart
          growth: {
            WP: [
              {
                label: 'personalLines',
                group: 'Personal',
                priorYear: 1200000,
                currentYear: 1500000,
                yoYChange: 300000,
                yoYChangePercentage: 0.25
              },
              {
                label: 'commercialLines',
                group: 'Commercial',
                priorYear: 600000,
                currentYear: 800000,
                yoYChange: 200000,
                yoYChangePercentage: 0.33
              },
              {
                label: 'total',
                group: 'Total',
                priorYear: 1800000,
                currentYear: 2300000,
                yoYChange: 500000,
                yoYChangePercentage: 0.28
              }
            ],
            PIF: [
              {
                label: 'personalLines',
                group: 'Personal',
                priorYear: 700,
                currentYear: 800,
                yoYChange: 100,
                yoYChangePercentage: 0.14
              },
              {
                label: 'commercialLines',
                group: 'Commercial',
                priorYear: 400,
                currentYear: 450,
                yoYChange: 50,
                yoYChangePercentage: 0.125
              },
              {
                label: 'total',
                group: 'Total',
                priorYear: 1100,
                currentYear: 1250,
                yoYChange: 150,
                yoYChangePercentage: 0.136
              }
            ]
          },

          growth_chart: [
            {
              label: 'personalLines',
              data: [
                { month: '202401', wp: 200000, pif: 700 },
                { month: '202402', wp: 220000, pif: 710 },
                { month: '202403', wp: 240000, pif: 720 }
              ]
            },
            {
              label: 'commercialLines',
              data: [
                { month: '202401', wp: 120000, pif: 380 },
                { month: '202402', wp: 130000, pif: 390 },
                { month: '202403', wp: 140000, pif: 400 }
              ]
            }
          ],

          // Quotes summary and chart data used by setQuoteChart
          quotes: [
            {
              newBusinessCount: 120,
              quoteCount: 300,
              quotesNotSoldOpportunities: 180,
              conversionRate: 0.4
            }
          ],

          quotes_chart: [
            {
              label: 'new_business',
              data: [
                { month: '202401', value: 30 },
                { month: '202402', value: 35 },
                { month: '202403', value: 40 }
              ]
            },
            {
              label: 'quotes',
              data: [
                { month: '202401', value: 70 },
                { month: '202402', value: 80 },
                { month: '202403', value: 90 }
              ]
            }
          ]
        };

        resolve({
          status: true,
          results
        });
      }, 400);
    });
  }

  /**
   * POST /pgifilter
   * Get PGI filter options
   */
  getPGIOverviewFilter(agencyName: string = '', lob: any = [], state: any = [], productline: any = [], agent: any = [], carrier: any = []): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            filters: {
              Agency: ['All Agencies', 'Demo Insurance Agency', 'Agency 2', 'Agency 3', 'Agency 4', 'Agency 5'],
              LOB: ['All LOBs', 'Homeowners', 'Auto', 'Commercial', 'Umbrella', 'General Liability', 'Workers Comp'],
              AgencyState: ['All States', 'California', 'Texas', 'Florida', 'New York', 'Arizona', 'Nevada', 'Oregon', 'Washington'],
              ProductLine: ['All Product Lines', 'Personal', 'Commercial', 'Life/Financial', 'Health/Benefits'],
              CarrierName: ['All Carriers', 'Allied', 'State Farm', 'Progressive', 'Geico', 'Farmers', 'Travelers', 'Liberty Mutual', 'Nationwide'],
              Term: ['All Terms', '12 Months', '6 Months', '3 Months', 'Annual', 'Monthly'],
              TransactionType: ['All Transaction Types', 'New Business', 'Renewal', 'Endorsement', 'Cancellation', 'Non-Renewal'],
              City: [
                'All Cities', 'Los Angeles', 'San Francisco', 'San Diego', 'Sacramento', 'Fresno',
                'Houston', 'Dallas', 'Austin', 'San Antonio', 'Fort Worth',
                'Miami', 'Tampa', 'Orlando', 'Jacksonville', 'Tallahassee',
                'New York', 'Buffalo', 'Rochester', 'Albany', 'Syracuse',
                'Phoenix', 'Tucson', 'Mesa', 'Chandler', 'Scottsdale',
                'Las Vegas', 'Reno', 'Henderson', 'North Las Vegas', 'Sparks',
                'Portland', 'Eugene', 'Salem', 'Gresham', 'Hillsboro',
                'Seattle', 'Spokane', 'Tacoma', 'Vancouver', 'Bellevue'
              ],
              Agent: ['All Agents', 'John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown', 'Diana Prince', 'Edward Norton']
            }
          }
        });
      }, 300);
    });
  }

  /**
   * POST /opportunitiesFilters
   * Get targeted opportunities filters
   */
  getTargetedFilter(agencyName: string = '', lob: any = [], state: any = [], productline: any = [], agent: any = [], carrier: any = [], dimension: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            totalOpportunities: 150,
            opportunitiesByDimension: {
              'quotes': 85,
              'sold': 65,
              'cross-sell': 45,
              'upsell': 30
            },
            breakdown: this.generateOpportunitiesBreakdown(dimension)
          }
        });
      }, 400);
    });
  }

  /**
   * POST /profitabilityall
   * Get profitability and retention overview
   * Generates dynamic data based on filters to ensure all charts have data
   */
  getProfitabilityRetentionOverview(filters: any): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const currentYear = new Date().getFullYear();
        const priorYear = currentYear - 1;
        const prior2Year = currentYear - 2;
        
        // Generate a seed based on filters to make data vary but be consistent for same filters
        const filterSeed = JSON.stringify(filters || {}).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const random = (seed: number) => {
          const x = Math.sin(seed) * 10000;
          return x - Math.floor(x);
        };
        
        // Helper to generate loss ratio data structure for a carrier
        // Component expects: [{ carrier: 'Name', data: [{ month: 1, value: 0.12 }, ...] }]
        const generateLossRatioData = (carrier: string, base: number, seed: number, months: number = 12): any => {
          const data: any[] = [];
          for (let i = 1; i <= months; i++) {
            const variation = (random(seed + i) - 0.5) * 0.02; // ±1% variation
            data.push({
              month: i,
              value: Math.max(0.05, Math.min(0.35, base + variation))
            });
          }
          return {
            carrier: carrier,
            data: data
          };
        };
        
        // Helper to generate loss ratio data for all carriers
        const generateLossRatioForFilter = (filter: string, base: number, seed: number, type: 'personal' | 'commercial'): any[] => {
          const carriers = type === 'personal' 
            ? ['Allied', 'State Farm', 'Progressive', 'Farmers', 'Travelers']
            : ['Farmers', 'Travelers', 'Allied', 'State Farm', 'Progressive']; // 5 carriers for commercial
          return carriers.map((carrier, idx) => 
            generateLossRatioData(carrier, base + (random(seed + idx) * 0.03), seed + idx * 100, 12)
          );
        };
        
        // Helper to generate retention data for a carrier
        const generateRetentionData = (carrier: string, seed: number) => {
          const baseRetention = 0.90 + (random(seed) * 0.08); // 90-98% retention
          return [
            { lob: 'Homeowners', percentage: baseRetention + (random(seed + 1) * 0.03) },
            { lob: 'Auto', percentage: baseRetention + (random(seed + 2) * 0.03) },
            { lob: 'Umbrella', percentage: baseRetention + (random(seed + 3) * 0.03) }
          ];
        };
        
        // Helper to generate commercial retention data
        const generateCommercialRetentionData = (carrier: string, seed: number) => {
          const baseRetention = 0.88 + (random(seed) * 0.08); // 88-96% retention
          return [
            { lob: 'General Liability', percentage: baseRetention + (random(seed + 1) * 0.03) },
            { lob: 'Commercial Auto', percentage: baseRetention + (random(seed + 2) * 0.03) },
            { lob: 'Workers Comp', percentage: baseRetention + (random(seed + 3) * 0.03) }
          ];
        };
        
        const carriers = ['Allied', 'State Farm', 'Progressive', 'Farmers', 'Travelers'];
        const allCarriers = ['All Carriers', ...carriers];
        
        // Generate retention donut data for all carriers
        const generateRetentionDonutData = (yearType: 'current' | 'prior' | 'prior2', type: 'personal' | 'commercial') => {
          const yearOffset = yearType === 'current' ? 0 : yearType === 'prior' ? 1 : 2;
          return allCarriers.map((carrier, idx) => ({
            carrier: carrier,
            data: type === 'personal' 
              ? generateRetentionData(carrier, filterSeed + idx + yearOffset * 10)
              : generateCommercialRetentionData(carrier, filterSeed + idx + yearOffset * 10)
          }));
        };
        
        resolve({
          status: true,
          results: {
            profitability: {
              personal: {
                summary: [
                  {
                    carrier: 'Allied',
                    data: [
                      ['Homeowners', 1250000, 0.15, 187500],
                      ['Auto', 980000, 0.12, 117600],
                      ['Umbrella', 320000, 0.18, 57600],
                      ['Total', 2550000, 0.14, 362700]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'State Farm',
                    data: [
                      ['Homeowners', 1100000, 0.14, 154000],
                      ['Auto', 850000, 0.11, 93500],
                      ['Umbrella', 280000, 0.16, 44800],
                      ['Total', 2230000, 0.13, 292300]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'Progressive',
                    data: [
                      ['Homeowners', 950000, 0.13, 123500],
                      ['Auto', 1200000, 0.10, 120000],
                      ['Umbrella', 250000, 0.15, 37500],
                      ['Total', 2400000, 0.12, 281000]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'Farmers',
                    data: [
                      ['Homeowners', 1050000, 0.16, 168000],
                      ['Auto', 920000, 0.13, 119600],
                      ['Umbrella', 290000, 0.17, 49300],
                      ['Total', 2260000, 0.15, 336900]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'Travelers',
                    data: [
                      ['Homeowners', 980000, 0.14, 137200],
                      ['Auto', 1100000, 0.11, 121000],
                      ['Umbrella', 270000, 0.16, 43200],
                      ['Total', 2350000, 0.13, 301400]
                    ],
                    expanded: false
                  }
                ],
                headers: ['Line of Business', 'Written Premium', 'Loss Ratio', 'Losses'],
                loss_ratio_ytd: {
                  total: generateLossRatioForFilter('total', 0.12, filterSeed, 'personal'),
                  homeowners: generateLossRatioForFilter('homeowners', 0.15, filterSeed + 100, 'personal'),
                  auto: generateLossRatioForFilter('auto', 0.10, filterSeed + 200, 'personal'),
                  property: generateLossRatioForFilter('property', 0.16, filterSeed + 300, 'personal'),
                  umbrella: generateLossRatioForFilter('umbrella', 0.18, filterSeed + 400, 'personal'),
                  other: generateLossRatioForFilter('other', 0.14, filterSeed + 500, 'personal')
                },
                loss_ratio_prior: {
                  total: generateLossRatioForFilter('total', 0.13, filterSeed + 1000, 'personal'),
                  homeowners: generateLossRatioForFilter('homeowners', 0.16, filterSeed + 1100, 'personal'),
                  auto: generateLossRatioForFilter('auto', 0.11, filterSeed + 1200, 'personal'),
                  property: generateLossRatioForFilter('property', 0.17, filterSeed + 1300, 'personal'),
                  umbrella: generateLossRatioForFilter('umbrella', 0.19, filterSeed + 1400, 'personal'),
                  other: generateLossRatioForFilter('other', 0.15, filterSeed + 1500, 'personal')
                }
              },
              commercial: {
                summary: [
                  {
                    carrier: 'Farmers',
                    data: [
                      ['General Liability', 850000, 0.20, 170000],
                      ['Commercial Auto', 650000, 0.18, 117000],
                      ['Workers Comp', 420000, 0.25, 105000],
                      ['Total', 1920000, 0.20, 392000]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'Travelers',
                    data: [
                      ['General Liability', 720000, 0.19, 136800],
                      ['Commercial Auto', 580000, 0.17, 98600],
                      ['Workers Comp', 380000, 0.24, 91200],
                      ['Total', 1680000, 0.19, 326600]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'Allied',
                    data: [
                      ['General Liability', 780000, 0.21, 163800],
                      ['Commercial Auto', 620000, 0.19, 117800],
                      ['Workers Comp', 400000, 0.26, 104000],
                      ['Total', 1800000, 0.21, 385600]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'State Farm',
                    data: [
                      ['General Liability', 700000, 0.19, 133000],
                      ['Commercial Auto', 600000, 0.17, 102000],
                      ['Workers Comp', 360000, 0.23, 82800],
                      ['Total', 1660000, 0.19, 317800]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'Progressive',
                    data: [
                      ['General Liability', 680000, 0.18, 122400],
                      ['Commercial Auto', 640000, 0.16, 102400],
                      ['Workers Comp', 340000, 0.22, 74800],
                      ['Total', 1660000, 0.18, 299600]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'Liberty Mutual',
                    data: [
                      ['General Liability', 750000, 0.20, 150000],
                      ['Commercial Auto', 610000, 0.18, 109800],
                      ['Workers Comp', 390000, 0.24, 93600],
                      ['Total', 1750000, 0.20, 353400]
                    ],
                    expanded: false
                  },
                  {
                    carrier: 'Chubb',
                    data: [
                      ['General Liability', 720000, 0.19, 136800],
                      ['Commercial Auto', 590000, 0.17, 100300],
                      ['Workers Comp', 370000, 0.23, 85100],
                      ['Total', 1680000, 0.19, 322200]
                    ],
                    expanded: false
                  }
                ],
                headers: ['Line of Business', 'Written Premium', 'Loss Ratio', 'Losses'],
                loss_ratio_ytd: {
                  total: generateLossRatioForFilter('total', 0.18, filterSeed + 2000, 'commercial'),
                  homeowners: generateLossRatioForFilter('homeowners', 0.21, filterSeed + 2050, 'commercial'),
                  general_liability: generateLossRatioForFilter('general_liability', 0.19, filterSeed + 2100, 'commercial'),
                  commercial_auto: generateLossRatioForFilter('commercial_auto', 0.17, filterSeed + 2200, 'commercial'),
                  workers_comp: generateLossRatioForFilter('workers_comp', 0.24, filterSeed + 2300, 'commercial'),
                  umbrella: generateLossRatioForFilter('umbrella', 0.22, filterSeed + 2350, 'commercial'),
                  property: generateLossRatioForFilter('property', 0.20, filterSeed + 2400, 'commercial'),
                  other: generateLossRatioForFilter('other', 0.19, filterSeed + 2500, 'commercial')
                },
                loss_ratio_prior: {
                  total: generateLossRatioForFilter('total', 0.19, filterSeed + 3000, 'commercial'),
                  homeowners: generateLossRatioForFilter('homeowners', 0.22, filterSeed + 3050, 'commercial'),
                  general_liability: generateLossRatioForFilter('general_liability', 0.20, filterSeed + 3100, 'commercial'),
                  commercial_auto: generateLossRatioForFilter('commercial_auto', 0.18, filterSeed + 3200, 'commercial'),
                  workers_comp: generateLossRatioForFilter('workers_comp', 0.25, filterSeed + 3300, 'commercial'),
                  umbrella: generateLossRatioForFilter('umbrella', 0.23, filterSeed + 3350, 'commercial'),
                  property: generateLossRatioForFilter('property', 0.21, filterSeed + 3400, 'commercial'),
                  other: generateLossRatioForFilter('other', 0.20, filterSeed + 3500, 'commercial')
                }
              }
            },
            retention: {
              carrier_selection: allCarriers,
              personal: {
                summary: {
                  headers: ['Month', 'New Business', 'Renewals', 'Non-Renewals', 'Retention Rate'],
                  data: allCarriers.map((carrier, idx) => {
                    const baseNew = 40 + (random(filterSeed + idx) * 20);
                    const baseRenew = 170 + (random(filterSeed + idx + 10) * 30);
                    const baseNonRenew = 10 + (random(filterSeed + idx + 20) * 10);
                    const retention = baseRenew / (baseRenew + baseNonRenew);
                    return {
                      carrier: carrier,
                      data: [
                        ['Jan', Math.round(baseNew + random(filterSeed + idx + 30) * 10), Math.round(baseRenew + random(filterSeed + idx + 40) * 10), Math.round(baseNonRenew + random(filterSeed + idx + 50) * 5), Math.round(retention * 100) / 100],
                        ['Feb', Math.round(baseNew + random(filterSeed + idx + 60) * 10), Math.round(baseRenew + random(filterSeed + idx + 70) * 10), Math.round(baseNonRenew + random(filterSeed + idx + 80) * 5), Math.round(retention * 100) / 100],
                        ['Mar', Math.round(baseNew + random(filterSeed + idx + 90) * 10), Math.round(baseRenew + random(filterSeed + idx + 100) * 10), Math.round(baseNonRenew + random(filterSeed + idx + 110) * 5), Math.round(retention * 100) / 100],
                        ['Total', Math.round(baseNew * 3), Math.round(baseRenew * 3), Math.round(baseNonRenew * 3), Math.round(retention * 100) / 100]
                      ]
                    };
                  })
                },
                r12_donut: {
                  options: [`${currentYear}`, `${priorYear}`, `${prior2Year}`],
                  current: generateRetentionDonutData('current', 'personal'),
                  prior: generateRetentionDonutData('prior', 'personal'),
                  prior2: generateRetentionDonutData('prior2', 'personal')
                },
                ye_donut: {
                  options: [`${currentYear}`, `${priorYear}`, `${prior2Year}`],
                  current: generateRetentionDonutData('current', 'personal'),
                  prior: generateRetentionDonutData('prior', 'personal'),
                  prior2: generateRetentionDonutData('prior2', 'personal')
                }
              },
              commercial: {
                summary: {
                  headers: ['Month', 'New Business', 'Renewals', 'Non-Renewals', 'Retention Rate'],
                  data: allCarriers.map((carrier, idx) => {
                    const baseNew = 20 + (random(filterSeed + idx + 200) * 15);
                    const baseRenew = 85 + (random(filterSeed + idx + 210) * 20);
                    const baseNonRenew = 6 + (random(filterSeed + idx + 220) * 5);
                    const retention = baseRenew / (baseRenew + baseNonRenew);
                    return {
                      carrier: carrier,
                      data: [
                        ['Jan', Math.round(baseNew + random(filterSeed + idx + 230) * 8), Math.round(baseRenew + random(filterSeed + idx + 240) * 8), Math.round(baseNonRenew + random(filterSeed + idx + 250) * 3), Math.round(retention * 100) / 100],
                        ['Feb', Math.round(baseNew + random(filterSeed + idx + 260) * 8), Math.round(baseRenew + random(filterSeed + idx + 270) * 8), Math.round(baseNonRenew + random(filterSeed + idx + 280) * 3), Math.round(retention * 100) / 100],
                        ['Mar', Math.round(baseNew + random(filterSeed + idx + 290) * 8), Math.round(baseRenew + random(filterSeed + idx + 300) * 8), Math.round(baseNonRenew + random(filterSeed + idx + 310) * 3), Math.round(retention * 100) / 100],
                        ['Total', Math.round(baseNew * 3), Math.round(baseRenew * 3), Math.round(baseNonRenew * 3), Math.round(retention * 100) / 100]
                      ]
                    };
                  })
                },
                r12_donut: {
                  options: [`${currentYear}`, `${priorYear}`, `${prior2Year}`],
                  current: generateRetentionDonutData('current', 'commercial'),
                  prior: generateRetentionDonutData('prior', 'commercial'),
                  prior2: generateRetentionDonutData('prior2', 'commercial')
                },
                ye_donut: {
                  options: [`${currentYear}`, `${priorYear}`, `${prior2Year}`],
                  current: generateRetentionDonutData('current', 'commercial'),
                  prior: generateRetentionDonutData('prior', 'commercial'),
                  prior2: generateRetentionDonutData('prior2', 'commercial')
                }
              }
            }
          }
        });
      }, 400);
    });
  }

  /**
   * POST /commissionsAll
   * Get commission overview
   */
  getCommissionOverview(): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            ytd_gross: 2450000,
            net_payout: 1960000,
            ytd_additional: 490000,
            commissions_by_carrier: {
              legend: 'Commission',
              data: [
                { label: 'State Farm', value: 850000 },
                { label: 'Progressive', value: 650000 },
                { label: 'Allied', value: 450000 },
                { label: 'Farmers', value: 300000 },
                { label: 'Travelers', value: 200000 }
              ]
            },
            commissions_yoy_monthly: {
              legend: ['Prior Year', 'Current Year'],
              data: [
                { label: 1, prior: 180000, current: 195000 },
                { label: 2, prior: 175000, current: 210000 },
                { label: 3, prior: 190000, current: 225000 },
                { label: 4, prior: 185000, current: 220000 },
                { label: 5, prior: 200000, current: 235000 },
                { label: 6, prior: 195000, current: 250000 },
                { label: 7, prior: 210000, current: 240000 },
                { label: 8, prior: 205000, current: 255000 },
                { label: 9, prior: 220000, current: 245000 },
                { label: 10, prior: 215000, current: 260000 },
                { label: 11, prior: 230000, current: 250000 },
                { label: 12, prior: 225000, current: 265000 }
              ]
            },
            commissions_by_carrier_type: {
              legend: 'Commission',
              data: [
                { label: 'Direct', value: 1500000 },
                { label: 'Agency', value: 800000 },
                { label: 'Broker', value: 150000 }
              ]
            },
            commissions_by_lob: {
              legend: 'Commission',
              data: [
                { label: 'Auto', value: 1200000 },
                { label: 'Home', value: 800000 },
                { label: 'Commercial', value: 350000 },
                { label: 'Life', value: 100000 }
              ]
            },
            commissions_by_sublob: {
              legend: 'Commission',
              data: [
                { label: 'Personal Auto', value: 1000000 },
                { label: 'Commercial Auto', value: 200000 },
                { label: 'Homeowners', value: 600000 },
                { label: 'Renters', value: 200000 },
                { label: 'General Liability', value: 250000 },
                { label: 'Workers Comp', value: 100000 }
              ]
            }
          }
        });
      }, 400);
    });
  }

  /**
   * POST /opportunitiesAll
   * Get targeted opportunities overview
   */
  getPGITargetedOpportunities(): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            opportunities: {
              // Telematics opportunity
              telematics: {
                label: 'Telematics',
                strategy: 'Telematics Strategy',
                green: {
                  label: 'Sold with',
                  percentage: 0.35, // 35%
                  count: 52
                },
                red: {
                  label: 'Sold without',
                  percentage: 0.40, // 40%
                  count: 60
                },
                yellow: {
                  label: 'Unknown',
                  percentage: 0.25, // 25%
                  count: 38
                }
              },
              // Multiline opportunity
              multiline: {
                label: 'Multi-Line Policies',
                strategy: 'Multi-Line Strategy',
                green: {
                  label: 'Multiline',
                  percentage: 0.45, // 45%
                  count: 68
                },
                red: {
                  label: 'Monoline',
                  percentage: 0.30, // 30%
                  count: 45
                },
                yellow: {
                  label: 'Unknown',
                  percentage: 0.25, // 25%
                  count: 37
                }
              },
              // Umbrella opportunity
              umbrella: {
                label: 'Umbrella Coverage',
                strategy: 'Umbrella Strategy',
                green: {
                  label: 'With Umbrella',
                  percentage: 0.30, // 30%
                  count: 45
                },
                red: {
                  label: 'Without Umbrella',
                  percentage: 0.50, // 50%
                  count: 75
                },
                yellow: {
                  label: 'Unknown',
                  percentage: 0.20, // 20%
                  count: 30
                }
              },
              // Premium Weight - Top 20 Accounts
              premium_weight: {
                labels: [
                  'Account 1', 'Account 2', 'Account 3', 'Account 4', 'Account 5',
                  'Account 6', 'Account 7', 'Account 8', 'Account 9', 'Account 10',
                  'Account 11', 'Account 12', 'Account 13', 'Account 14', 'Account 15',
                  'Account 16', 'Account 17', 'Account 18', 'Account 19', 'Account 20'
                ],
                percentage: [
                  0.15, 0.12, 0.10, 0.08, 0.07,
                  0.06, 0.05, 0.04, 0.04, 0.03,
                  0.03, 0.03, 0.02, 0.02, 0.02,
                  0.02, 0.02, 0.02, 0.02, 0.02
                ],
                values: [
                  367500, 294000, 245000, 196000, 171500,
                  147000, 122500, 98000, 98000, 73500,
                  73500, 73500, 49000, 49000, 49000,
                  49000, 49000, 49000, 49000, 49000
                ]
              },
              // New Business Cancellations
              nb_cancellations: {
                r12: {
                  labels: ['Policy Cancelled', 'Non-Renewal', 'Lapse', 'Other'],
                  percentage: [0.40, 0.30, 0.20, 0.10],
                  values: [120, 90, 60, 30]
                },
                ytd: {
                  labels: ['Policy Cancelled', 'Non-Renewal', 'Lapse', 'Other'],
                  percentage: [0.35, 0.35, 0.20, 0.10],
                  values: [70, 70, 40, 20]
                }
              },
              // Quotes Sold Profile
              quotes_sold: {
                past_30_days: {
                  labels: ['Sold', 'Not Sold', 'Pending'],
                  percentage: [0.45, 0.40, 0.15],
                  values: [90, 80, 30]
                },
                r12: {
                  labels: ['Sold', 'Not Sold', 'Pending'],
                  percentage: [0.43, 0.42, 0.15],
                  values: [645, 630, 225]
                },
                ytd: {
                  labels: ['Sold', 'Not Sold', 'Pending'],
                  percentage: [0.44, 0.41, 0.15],
                  values: [220, 205, 75]
                }
              },
              // Winback data - format: [month, count (policies), premium]
              winback: [
                ['January', 25, 50000],
                ['February', 30, 60000],
                ['March', 28, 56000],
                ['April', 32, 64000],
                ['May', 35, 70000],
                ['June', 40, 80000],
                ['July', 38, 76000],
                ['August', 42, 84000],
                ['September', 45, 90000],
                ['October', 40, 80000],
                ['November', 35, 70000],
                ['December', 30, 60000]
              ]
            }
          }
        });
      }, 400);
    });
  }

  /**
   * POST /opportunitiesDetails
   * Get opportunities details with pagination
   */
  getOpportunitiesDetails(pageIndex: number, pageSize: number, type: string, dimension: string, sort: Sort, filters?: any): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allDetails = this.generateOpportunitiesDetails(type, dimension, filters);
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        const paginated = allDetails.slice(start, end);

        // Apply sorting if provided
        if (sort && sort.active) {
          paginated.sort((a, b) => {
            const aVal = a[sort.active];
            const bVal = b[sort.active];
            const direction = sort.direction === 'desc' ? -1 : 1;
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
            return 0;
          });
        }

        resolve({
          status: true,
          results: {
            data: paginated,
            total: allDetails.length,
            page: pageIndex,
            pageSize: pageSize,
            totalPages: Math.ceil(allDetails.length / pageSize)
          }
        });
      }, 400);
    });
  }

  /**
   * POST /overviewdetails
   * Get policies/quotes overview details
   */
  getPGIPoliciesOverviewDetails(pageSize: number, pageNum: number, sort: Sort, selected: any, dataType: string = 'policies', dimension: string = 'sold'): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allDetails = this.generatePoliciesDetails(dataType, dimension, selected);
        const start = pageNum * pageSize;
        const end = start + pageSize;
        const paginated = allDetails.slice(start, end);

        // Apply sorting if provided
        if (sort && sort.active) {
          paginated.sort((a, b) => {
            const aVal = a[sort.active];
            const bVal = b[sort.active];
            const direction = sort.direction === 'desc' ? -1 : 1;
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
            return 0;
          });
        }

        resolve({
          status: true,
          results: {
            data: paginated,
            total: allDetails.length,
            page: pageNum,
            pageSize: pageSize,
            totalPages: Math.ceil(allDetails.length / pageSize)
          }
        });
      }, 400);
    });
  }

  /**
   * POST /productionall
   * Get production data overview
   */
  getProductionData(): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            home: {
              // Home Price (Coverage A Limits)
              home_price: {
                labels: [
                  '$0-$100K', '$100K-$200K', '$200K-$300K', '$300K-$400K', 
                  '$400K-$500K', '$500K-$600K', '$600K-$700K', '$700K-$800K',
                  '$800K-$900K', '$900K-$1M', '$1M+'
                ],
                values: [45, 120, 180, 150, 110, 85, 60, 45, 30, 20, 15],
                percentage: [0.05, 0.13, 0.19, 0.16, 0.12, 0.09, 0.06, 0.05, 0.03, 0.02, 0.02]
              },
              // Year Built
              year_built: {
                labels: [
                  'Before 1950', '1950-1959', '1960-1969', '1970-1979',
                  '1980-1989', '1990-1999', '2000-2009', '2010-2019', '2020+'
                ],
                values: [25, 35, 45, 60, 80, 120, 150, 180, 100],
                percentage: [0.03, 0.04, 0.05, 0.07, 0.09, 0.13, 0.16, 0.19, 0.11]
              },
              // Coverage Profile (stacked chart with red/green/yellow)
              coverage_profile: {
                underinsured_cov_a: {
                  label: 'Underinsured Coverage A',
                  green: { percentage: 0.60, count: 540 },
                  red: { percentage: 0.25, count: 225 },
                  yellow: { percentage: 0.15, count: 135 }
                },
                underinsured_cov_c: {
                  label: 'Underinsured Coverage C',
                  green: { percentage: 0.55, count: 495 },
                  red: { percentage: 0.30, count: 270 },
                  yellow: { percentage: 0.15, count: 135 }
                },
                underinsured_cov_d: {
                  label: 'Underinsured Coverage D',
                  green: { percentage: 0.50, count: 450 },
                  red: { percentage: 0.35, count: 315 },
                  yellow: { percentage: 0.15, count: 135 }
                }
              },
              // Fire Protection Class
              fire_protection_class: {
                labels: ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'],
                values: [150, 180, 200, 150, 120, 80, 50, 30, 20, 10],
                percentage: [0.17, 0.20, 0.22, 0.17, 0.13, 0.09, 0.06, 0.03, 0.02, 0.01]
              },
              // Roof Age
              roof_age: {
                labels: ['0-5 years', '6-10 years', '11-15 years', '16-20 years', '21-25 years', '26+ years'],
                percentage: [0.25, 0.20, 0.18, 0.15, 0.12, 0.10],
                values: [225, 180, 162, 135, 108, 90]
              },
              // Roof Type (Construction Type)
              roof_type: {
                labels: ['Asphalt Shingle', 'Metal', 'Tile', 'Wood Shake', 'Flat/Built-up', 'Other'],
                percentage: [0.45, 0.20, 0.15, 0.10, 0.05, 0.05],
                values: [405, 180, 135, 90, 45, 45]
              }
            },
            auto: {
              // Dial charts (gauge/donut charts with pointers)
              dial_mileage: {
                split: [0.60, 0.30, 0.10], // [green, yellow, red] percentages
                labels: ['Favorable (>8k)', 'Typical (4k-8k)', 'Low (<4k)'],
                pointer: 0.65, // Pointer position (0-1, represents 65%)
                value: [600, 300, 100] // Counts for each category
              },
              dial_address: {
                split: [0.75, 0.20, 0.05], // [green, yellow, red]
                labels: ['Match', 'Mismatch', 'Unknown'],
                pointer: 0.80, // 80% match
                value: 750 // Total count
              },
              dial_unlisted_spouse: {
                split: [0.70, 0.25, 0.05], // [green, yellow, red]
                labels: ['Listed', 'Unlisted', 'Unknown'],
                pointer: 0.72, // 72% listed
                value: 700
              },
              dial_excluded_drivers: {
                split: [0.85, 0.10, 0.05], // [green, yellow, red]
                labels: ['No Excluded', 'Has Excluded', 'Unknown'],
                pointer: 0.87, // 87% no excluded
                value: 850
              },
              dial_cars_drivers: {
                split: [0.65, 0.25, 0.10], // [green, yellow, red]
                labels: ['Favorable Ratio', 'Typical Ratio', 'Unfavorable Ratio'],
                pointer: 0.68, // 68% favorable
                value: 650
              },
              dial_quick_endorsement: {
                split: [0.80, 0.15, 0.05], // [green, yellow, red]
                labels: ['Has Quick Endorsement', 'No Quick Endorsement', 'Unknown'],
                pointer: 0.82, // 82% has quick endorsement
                value: 800
              },
              // Bar charts with red/green/yellow
              rejected_um_uim: {
                red: {
                  label: 'Rejected',
                  percentage: 0.15,
                  count: 150
                },
                green: {
                  label: 'Accepted',
                  percentage: 0.75,
                  count: 750
                },
                yellow: {
                  label: 'Unknown',
                  percentage: 0.10,
                  count: 100
                },
                label: 'UM / UIM Coverage'
              },
              no_prior_insurance: {
                red: {
                  label: 'No Prior Insurance',
                  percentage: 0.12,
                  count: 120
                },
                green: {
                  label: 'Has Prior Insurance',
                  percentage: 0.83,
                  count: 830
                },
                yellow: {
                  label: 'Unknown',
                  percentage: 0.05,
                  count: 50
                },
                label: 'Prior Insurance'
              },
              // State minimum limits chart
              minimum_state_limits: {
                labels: ['green', 'yellow', 'red'],
                count: [850, 100, 50],
                percentage: [0.85, 0.10, 0.05]
              },
              // Pie charts
              education_level: {
                labels: [
                  'High School', 'Some College', 'Bachelor\'s Degree',
                  'Master\'s Degree', 'Doctorate', 'Unknown'
                ],
                count: [300, 250, 200, 150, 50, 50],
                percentage: [0.30, 0.25, 0.20, 0.15, 0.05, 0.05]
              },
              occupation: {
                labels: [
                  'Professional', 'Manager', 'Sales', 'Service',
                  'Construction', 'Transportation', 'Other', 'Unknown'
                ],
                count: [200, 180, 150, 120, 100, 80, 100, 70],
                percentage: [0.20, 0.18, 0.15, 0.12, 0.10, 0.08, 0.10, 0.07]
              },
              liability_limits: {
                labels: [
                  '$25K/$50K', '$50K/$100K', '$100K/$300K',
                  '$250K/$500K', '$500K/$500K', '$1M/$1M', 'Unknown'
                ],
                count: [50, 100, 250, 300, 200, 80, 20],
                percentage: [0.05, 0.10, 0.25, 0.30, 0.20, 0.08, 0.02]
              },
              length_prior_insurance: {
                labels: [
                  '0-1 years', '1-3 years', '3-5 years',
                  '5-10 years', '10+ years', 'Unknown'
                ],
                count: [100, 150, 200, 300, 200, 50],
                percentage: [0.10, 0.15, 0.20, 0.30, 0.20, 0.05]
              }
            }
          }
        });
      }, 400);
    });
  }

  /**
   * POST /productionDetails
   * Get production details with pagination
   */
  getProductionDetails(pageIndex: number, pageSize: number, postData: any, sort: Sort): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allDetails = this.generateProductionDetails(postData);
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        const paginated = allDetails.slice(start, end);

        // Apply sorting if provided
        if (sort && sort.active) {
          paginated.sort((a, b) => {
            const aVal = a[sort.active];
            const bVal = b[sort.active];
            const direction = sort.direction === 'desc' ? -1 : 1;
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
            return 0;
          });
        }

        resolve({
          status: true,
          results: {
            data: paginated,
            total_records: allDetails.length,
            total: allDetails.length,
            page: pageIndex,
            pageSize: pageSize,
            totalPages: Math.ceil(allDetails.length / pageSize)
          }
        });
      }, 400);
    });
  }

  /**
   * POST /profitabilityfilter
   * Get profitability filters
   */
  getProfitabilityFilters(agencyName: string = ''): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            agencies: ['Demo Insurance Agency', 'Agency 2', 'Agency 3']
          }
        });
      }, 300);
    });
  }

  /**
   * POST /pgi/userlist
   * Get users list
   */
  getUsers(agency: string, productLine: string[], carrier: string[], state: string[], lob: string[], agencyId?: string): Promise<string[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve([
          'John Smith',
          'Jane Doe',
          'Bob Johnson',
          'Alice Williams',
          'Charlie Brown',
          'Diana Prince'
        ]);
      }, 300);
    });
  }

  /**
   * POST /usersNotify
   * Get users by filters for notifications
   */
  getUsersByFilters(role: string = '', states: any = [], accessPoints: any = [], carriers: any = []): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            users: [
              { id: '1', name: 'John Smith', email: 'john@example.com', role: 'Agent' },
              { id: '2', name: 'Jane Doe', email: 'jane@example.com', role: 'Agent' },
              { id: '3', name: 'Bob Johnson', email: 'bob@example.com', role: 'Agency Admin' }
            ]
          }
        });
      }, 300);
    });
  }

  /**
   * GET /resources/business?topic=...
   * Get business resources by topic
   */
  getResourcesByTopic(topic: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Special handling for podcasts - return HTML content
        if (topic === 'podcasts') {
          resolve({
            status: true,
            results: [
              {
                text: `
                  <div class="podcasts-container">
                    <h2>Insurance Industry Podcasts</h2>
                    <p>Stay informed with the latest insights, trends, and best practices in the insurance industry.</p>
                    
                    <div class="podcast-list">
                      <div class="podcast-item">
                        <h3>Episode 1: Maximizing Agency Growth Through Technology</h3>
                        <p class="podcast-meta">Published: January 15, 2024 | Duration: 45 minutes</p>
                        <p class="podcast-description">
                          Join industry experts as they discuss how modern technology can help insurance agencies 
                          scale their operations, improve customer service, and increase profitability. Learn about 
                          the latest tools and strategies being used by top-performing agencies.
                        </p>
                        <div class="podcast-links">
                          <a href="#" class="podcast-link">Listen on Spotify</a>
                          <a href="#" class="podcast-link">Listen on Apple Podcasts</a>
                          <a href="#" class="podcast-link">Download Transcript</a>
                        </div>
                      </div>
                      
                      <div class="podcast-item">
                        <h3>Episode 2: Building Strong Client Relationships in the Digital Age</h3>
                        <p class="podcast-meta">Published: January 8, 2024 | Duration: 38 minutes</p>
                        <p class="podcast-description">
                          Discover effective strategies for maintaining and strengthening client relationships in an 
                          increasingly digital marketplace. Our guests share real-world examples of how they've 
                          successfully adapted their communication and service delivery methods.
                        </p>
                        <div class="podcast-links">
                          <a href="#" class="podcast-link">Listen on Spotify</a>
                          <a href="#" class="podcast-link">Listen on Apple Podcasts</a>
                          <a href="#" class="podcast-link">Download Transcript</a>
                        </div>
                      </div>
                      
                      <div class="podcast-item">
                        <h3>Episode 3: Understanding Market Trends and Opportunities</h3>
                        <p class="podcast-meta">Published: January 1, 2024 | Duration: 52 minutes</p>
                        <p class="podcast-description">
                          Get insights into current market trends, emerging opportunities, and how to position your 
                          agency for success. Industry analysts break down the numbers and share their predictions 
                          for the coming year.
                        </p>
                        <div class="podcast-links">
                          <a href="#" class="podcast-link">Listen on Spotify</a>
                          <a href="#" class="podcast-link">Listen on Apple Podcasts</a>
                          <a href="#" class="podcast-link">Download Transcript</a>
                        </div>
                      </div>
                      
                      <div class="podcast-item">
                        <h3>Episode 4: Risk Management Best Practices</h3>
                        <p class="podcast-meta">Published: December 25, 2023 | Duration: 41 minutes</p>
                        <p class="podcast-description">
                          Learn about effective risk management strategies that can help protect your agency and your 
                          clients. Experts discuss common pitfalls and how to avoid them while building a robust 
                          risk management framework.
                        </p>
                        <div class="podcast-links">
                          <a href="#" class="podcast-link">Listen on Spotify</a>
                          <a href="#" class="podcast-link">Listen on Apple Podcasts</a>
                          <a href="#" class="podcast-link">Download Transcript</a>
                        </div>
                      </div>
                      
                      <div class="podcast-item">
                        <h3>Episode 5: Sales Techniques That Work</h3>
                        <p class="podcast-meta">Published: December 18, 2023 | Duration: 47 minutes</p>
                        <p class="podcast-description">
                          Top sales professionals share their proven techniques for closing deals and building a 
                          successful sales pipeline. From initial contact to policy renewal, learn the strategies 
                          that drive results.
                        </p>
                        <div class="podcast-links">
                          <a href="#" class="podcast-link">Listen on Spotify</a>
                          <a href="#" class="podcast-link">Listen on Apple Podcasts</a>
                          <a href="#" class="podcast-link">Download Transcript</a>
                        </div>
                      </div>
                    </div>
                    
                    <div class="podcast-subscribe">
                      <h3>Subscribe to Our Podcast</h3>
                      <p>Never miss an episode! Subscribe on your favorite platform:</p>
                      <div class="subscribe-links">
                        <a href="#" class="subscribe-link">Apple Podcasts</a>
                        <a href="#" class="subscribe-link">Spotify</a>
                        <a href="#" class="subscribe-link">Google Podcasts</a>
                        <a href="#" class="subscribe-link">RSS Feed</a>
                      </div>
                    </div>
                  </div>
                  
                  <style>
                    .podcasts-container {
                      width: 100%;
                      margin: 0;
                      padding: 20px;
                      padding-left: 20px;
                      margin-left: -2vh;
                    }
                    .podcasts-container h2 {
                      color: #34577f;
                      margin-bottom: 10px;
                    }
                    .podcast-list {
                      margin-top: 30px;
                    }
                    .podcast-item {
                      background: #f8f9fa;
                      border-left: 4px solid #34577f;
                      padding: 20px;
                      margin-bottom: 25px;
                      border-radius: 4px;
                    }
                    .podcast-item h3 {
                      color: #34577f;
                      margin-top: 0;
                      margin-bottom: 10px;
                    }
                    .podcast-meta {
                      color: #6c757d;
                      font-size: 0.9em;
                      margin-bottom: 15px;
                    }
                    .podcast-description {
                      color: #495057;
                      line-height: 1.6;
                      margin-bottom: 15px;
                    }
                    .podcast-links {
                      display: flex;
                      gap: 15px;
                      flex-wrap: wrap;
                    }
                    .podcast-link {
                      color: #34577f;
                      text-decoration: none;
                      padding: 8px 16px;
                      border: 1px solid #34577f;
                      border-radius: 4px;
                      transition: background-color 0.3s;
                    }
                    .podcast-link:hover {
                      background-color: #34577f;
                      color: white;
                    }
                    .podcast-subscribe {
                      margin-top: 40px;
                      padding: 20px;
                      background: #e9ecef;
                      border-radius: 4px;
                    }
                    .podcast-subscribe h3 {
                      color: #34577f;
                      margin-top: 0;
                    }
                    .subscribe-links {
                      display: flex;
                      gap: 15px;
                      flex-wrap: wrap;
                      margin-top: 15px;
                    }
                    .subscribe-link {
                      color: #34577f;
                      text-decoration: none;
                      padding: 10px 20px;
                      background: white;
                      border: 1px solid #34577f;
                      border-radius: 4px;
                      transition: background-color 0.3s;
                    }
                    .subscribe-link:hover {
                      background-color: #34577f;
                      color: white;
                    }
                  </style>
                `
              }
            ]
          });
        } else if (topic === 'newsletter') {
          // Special handling for newsletter - return HTML content
          resolve({
            status: true,
            results: [
              {
                text: `
                  <div class="newsletter-container">
                    <h2>Insurance Industry Newsletter</h2>
                    <p class="newsletter-intro">Stay up-to-date with the latest news, insights, and updates from the insurance industry.</p>
                    
                    <div class="newsletter-list">
                      <div class="newsletter-item">
                        <div class="newsletter-header">
                          <h3>January 2024 Newsletter</h3>
                          <span class="newsletter-date">Published: January 20, 2024</span>
                        </div>
                        <div class="newsletter-content">
                          <h4>Industry Highlights</h4>
                          <ul>
                            <li>New regulations affecting property insurance policies</li>
                            <li>Technology trends shaping the future of insurance</li>
                            <li>Market analysis: Q4 2023 performance review</li>
                            <li>Upcoming industry events and conferences</li>
                          </ul>
                          
                          <h4>Featured Article: Digital Transformation in Insurance</h4>
                          <p>
                            The insurance industry continues to evolve with digital transformation initiatives. 
                            Agencies that embrace technology are seeing significant improvements in customer satisfaction, 
                            operational efficiency, and revenue growth. This month's featured article explores the key 
                            technologies driving this transformation and how agencies can leverage them for success.
                          </p>
                          
                          <h4>Best Practices</h4>
                          <p>
                            Learn from top-performing agencies as they share their strategies for client retention, 
                            cross-selling opportunities, and building a strong referral network. Discover actionable 
                            tips you can implement in your agency today.
                          </p>
                          
                          <div class="newsletter-actions">
                            <a href="#" class="newsletter-link">Read Full Newsletter</a>
                            <a href="#" class="newsletter-link">Download PDF</a>
                          </div>
                        </div>
                      </div>
                      
                      <div class="newsletter-item">
                        <div class="newsletter-header">
                          <h3>December 2023 Newsletter</h3>
                          <span class="newsletter-date">Published: December 15, 2023</span>
                        </div>
                        <div class="newsletter-content">
                          <h4>Year-End Review</h4>
                          <p>
                            As we close out 2023, we reflect on the major trends and developments that shaped the 
                            insurance industry this year. From regulatory changes to technological advancements, 
                            this comprehensive review covers everything you need to know.
                          </p>
                          
                          <h4>2024 Predictions</h4>
                          <ul>
                            <li>Emerging risks and coverage opportunities</li>
                            <li>Market conditions and pricing trends</li>
                            <li>Technology adoption rates</li>
                            <li>Regulatory landscape changes</li>
                          </ul>
                          
                          <h4>Success Stories</h4>
                          <p>
                            Read about agencies that achieved exceptional growth in 2023. These case studies provide 
                            valuable insights into what works and how you can apply similar strategies to your business.
                          </p>
                          
                          <div class="newsletter-actions">
                            <a href="#" class="newsletter-link">Read Full Newsletter</a>
                            <a href="#" class="newsletter-link">Download PDF</a>
                          </div>
                        </div>
                      </div>
                      
                      <div class="newsletter-item">
                        <div class="newsletter-header">
                          <h3>November 2023 Newsletter</h3>
                          <span class="newsletter-date">Published: November 18, 2023</span>
                        </div>
                        <div class="newsletter-content">
                          <h4>Market Updates</h4>
                          <p>
                            This month's newsletter covers significant market movements, carrier updates, and new 
                            product launches. Stay informed about changes that could impact your clients and your business.
                          </p>
                          
                          <h4>Training & Development</h4>
                          <ul>
                            <li>Upcoming webinars and training sessions</li>
                            <li>Certification programs and continuing education</li>
                            <li>Professional development resources</li>
                            <li>Industry best practices workshops</li>
                          </ul>
                          
                          <h4>Client Communication Tips</h4>
                          <p>
                            Effective communication is key to building strong client relationships. This article provides 
                            practical advice on improving your communication strategies, from initial contact through 
                            policy renewal and beyond.
                          </p>
                          
                          <div class="newsletter-actions">
                            <a href="#" class="newsletter-link">Read Full Newsletter</a>
                            <a href="#" class="newsletter-link">Download PDF</a>
                          </div>
                        </div>
                      </div>
                      
                      <div class="newsletter-item">
                        <div class="newsletter-header">
                          <h3>October 2023 Newsletter</h3>
                          <span class="newsletter-date">Published: October 20, 2023</span>
                        </div>
                        <div class="newsletter-content">
                          <h4>Industry News</h4>
                          <ul>
                            <li>Major carrier announcements and policy changes</li>
                            <li>Regulatory updates affecting insurance professionals</li>
                            <li>Technology partnerships and innovations</li>
                            <li>Industry awards and recognition programs</li>
                          </ul>
                          
                          <h4>Sales Strategies</h4>
                          <p>
                            Discover proven sales techniques that top agents use to close more deals and build their 
                            client base. This comprehensive guide covers everything from prospecting to closing and 
                            everything in between.
                          </p>
                          
                          <h4>Risk Management Focus</h4>
                          <p>
                            Learn about emerging risks and how to help your clients protect themselves. This month's 
                            focus includes cyber liability, climate-related risks, and evolving business exposures.
                          </p>
                          
                          <div class="newsletter-actions">
                            <a href="#" class="newsletter-link">Read Full Newsletter</a>
                            <a href="#" class="newsletter-link">Download PDF</a>
                          </div>
                        </div>
                      </div>
                      
                      <div class="newsletter-item">
                        <div class="newsletter-header">
                          <h3>September 2023 Newsletter</h3>
                          <span class="newsletter-date">Published: September 15, 2023</span>
                        </div>
                        <div class="newsletter-content">
                          <h4>Quarterly Review</h4>
                          <p>
                            Q3 2023 brought significant changes to the insurance landscape. This newsletter provides 
                            a comprehensive review of the quarter's most important developments and their implications 
                            for insurance professionals.
                          </p>
                          
                          <h4>Product Spotlight</h4>
                          <ul>
                            <li>New insurance products and coverage options</li>
                            <li>Enhanced policy features and benefits</li>
                            <li>Specialty lines and niche markets</li>
                            <li>Bundling opportunities for clients</li>
                          </ul>
                          
                          <h4>Community Spotlight</h4>
                          <p>
                            Meet industry leaders and learn from their experiences. This month's community spotlight 
                            features interviews with successful agency owners and their journeys to building thriving 
                            insurance businesses.
                          </p>
                          
                          <div class="newsletter-actions">
                            <a href="#" class="newsletter-link">Read Full Newsletter</a>
                            <a href="#" class="newsletter-link">Download PDF</a>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div class="newsletter-subscribe">
                      <h3>Subscribe to Our Newsletter</h3>
                      <p>Get the latest industry news, insights, and updates delivered directly to your inbox.</p>
                      <div class="subscribe-form">
                        <input type="email" placeholder="Enter your email address" class="email-input">
                        <button class="subscribe-button">Subscribe</button>
                      </div>
                      <p class="subscribe-note">By subscribing, you agree to receive monthly newsletters. You can unsubscribe at any time.</p>
                    </div>
                  </div>
                  
                  <style>
                    .newsletter-container {
                      width: 100%;
                      margin: 0;
                      padding: 20px;
                      padding-left: 20px;
                      margin-left: -2vh;
                    }
                    .newsletter-container h2 {
                      color: #34577f;
                      margin-bottom: 10px;
                    }
                    .newsletter-intro {
                      color: #6c757d;
                      font-size: 1.1em;
                      margin-bottom: 30px;
                    }
                    .newsletter-list {
                      margin-top: 30px;
                    }
                    .newsletter-item {
                      background: #f8f9fa;
                      border-left: 4px solid #34577f;
                      padding: 25px;
                      margin-bottom: 30px;
                      border-radius: 4px;
                    }
                    .newsletter-header {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      margin-bottom: 20px;
                      flex-wrap: wrap;
                    }
                    .newsletter-header h3 {
                      color: #34577f;
                      margin: 0;
                    }
                    .newsletter-date {
                      color: #6c757d;
                      font-size: 0.9em;
                    }
                    .newsletter-content h4 {
                      color: #34577f;
                      margin-top: 20px;
                      margin-bottom: 10px;
                    }
                    .newsletter-content ul {
                      color: #495057;
                      line-height: 1.8;
                      margin-bottom: 15px;
                    }
                    .newsletter-content p {
                      color: #495057;
                      line-height: 1.6;
                      margin-bottom: 15px;
                    }
                    .newsletter-actions {
                      display: flex;
                      gap: 15px;
                      flex-wrap: wrap;
                      margin-top: 20px;
                    }
                    .newsletter-link {
                      color: #34577f;
                      text-decoration: none;
                      padding: 8px 16px;
                      border: 1px solid #34577f;
                      border-radius: 4px;
                      transition: background-color 0.3s;
                    }
                    .newsletter-link:hover {
                      background-color: #34577f;
                      color: white;
                    }
                    .newsletter-subscribe {
                      margin-top: 50px;
                      padding: 30px;
                      background: #e9ecef;
                      border-radius: 4px;
                      text-align: center;
                    }
                    .newsletter-subscribe h3 {
                      color: #34577f;
                      margin-top: 0;
                      margin-bottom: 10px;
                    }
                    .subscribe-form {
                      display: flex;
                      gap: 10px;
                      justify-content: center;
                      margin-top: 20px;
                      flex-wrap: wrap;
                    }
                    .email-input {
                      padding: 10px 15px;
                      border: 1px solid #ced4da;
                      border-radius: 4px;
                      font-size: 1em;
                      min-width: 250px;
                    }
                    .subscribe-button {
                      padding: 10px 25px;
                      background: #34577f;
                      color: white;
                      border: none;
                      border-radius: 4px;
                      font-size: 1em;
                      cursor: pointer;
                      transition: background-color 0.3s;
                    }
                    .subscribe-button:hover {
                      background-color: #2a4563;
                    }
                    .subscribe-note {
                      font-size: 0.85em;
                      color: #6c757d;
                      margin-top: 15px;
                      margin-bottom: 0;
                    }
                  </style>
                `
              }
            ]
          });
        } else if (topic === 'telematics_sales' || topic === 'monoline-Multiline-Policies') {
          // Business Development Strategy Guide content
          const isTelematics = topic === 'telematics_sales';
          const topicName = isTelematics ? 'Telematics Sales' : 'Monoline-Multiline Policies';
          
          resolve({
            status: true,
            results: [
              {
                topic: topic,
                text: `
                  <div class="business-strategy-container">
                    <h1>Business Development Strategy Guide - ${topicName}</h1>
                    
                    <section id="introduction">
                      <h2>Introduction</h2>
                      <p>
                        This comprehensive guide provides strategic insights and actionable recommendations for developing 
                        your business in the ${topicName} market. Whether you're looking to expand your client base, 
                        increase policy retention, or explore new revenue opportunities, this guide offers proven strategies 
                        and best practices.
                      </p>
                      <p>
                        The insurance landscape continues to evolve, and staying ahead requires a combination of market knowledge, 
                        client relationship management, and strategic planning. This guide will help you navigate these 
                        opportunities effectively.
                      </p>
                    </section>
                    
                    <section id="market-overview">
                      <h2>Market Overview</h2>
                      <p>
                        The ${topicName} market presents significant opportunities for growth. Understanding current market 
                        trends, customer needs, and competitive landscape is essential for developing an effective business strategy.
                      </p>
                      <h3>Key Market Trends</h3>
                      <ul>
                        <li>Increasing demand for ${isTelematics ? 'usage-based insurance products' : 'comprehensive coverage options'}</li>
                        <li>Growing adoption of technology-driven solutions</li>
                        <li>Shift toward personalized insurance products</li>
                        <li>Rising importance of data analytics in risk assessment</li>
                        <li>Expanding market segments and niche opportunities</li>
                      </ul>
                      <h3>Market Size and Growth Potential</h3>
                      <p>
                        The ${topicName} market has shown consistent growth over the past several years, with projections 
                        indicating continued expansion. This growth is driven by several factors including regulatory changes, 
                        technological advancements, and evolving customer expectations.
                      </p>
                    </section>
                    
                    <section id="target-audience">
                      <h2>Target Audience Identification</h2>
                      <p>
                        Success in the ${topicName} market requires a clear understanding of your target audience. 
                        Identifying and segmenting potential clients allows for more effective marketing and sales strategies.
                      </p>
                      <h3>Primary Target Segments</h3>
                      <ul>
                        <li><strong>High-Value Clients:</strong> Clients with significant insurance needs and premium potential</li>
                        <li><strong>Tech-Savvy Consumers:</strong> Early adopters of new insurance technologies and products</li>
                        <li><strong>Business Owners:</strong> Commercial clients requiring comprehensive coverage solutions</li>
                        <li><strong>Growing Families:</strong> Households with evolving insurance needs</li>
                        <li><strong>Young Professionals:</strong> Emerging market segment with long-term potential</li>
                      </ul>
                      <h3>Client Profiling</h3>
                      <p>
                        Develop detailed client profiles based on demographics, risk profiles, purchasing behavior, and 
                        communication preferences. This information will guide your marketing efforts and help you tailor 
                        your approach to each segment.
                      </p>
                    </section>
                    
                    <section id="sales-strategies">
                      <h2>Sales Strategies and Techniques</h2>
                      <p>
                        Effective sales strategies are crucial for converting prospects into clients and maximizing revenue 
                        from existing relationships. This section outlines proven approaches for the ${topicName} market.
                      </p>
                      <h3>Prospecting Methods</h3>
                      <ul>
                        <li><strong>Referral Programs:</strong> Leverage existing client relationships to generate new leads</li>
                        <li><strong>Digital Marketing:</strong> Utilize online channels including social media, email campaigns, and content marketing</li>
                        <li><strong>Networking Events:</strong> Participate in industry conferences, trade shows, and local business events</li>
                        <li><strong>Partnership Development:</strong> Build relationships with complementary businesses and service providers</li>
                        <li><strong>Community Engagement:</strong> Establish your presence in local communities through sponsorships and involvement</li>
                      </ul>
                      <h3>Consultative Selling Approach</h3>
                      <p>
                        Adopt a consultative selling approach that focuses on understanding client needs and providing 
                        tailored solutions. This method builds trust and leads to higher client satisfaction and retention rates.
                      </p>
                      <h3>Value Proposition Development</h3>
                      <p>
                        Clearly articulate the unique value your agency provides. This includes competitive pricing, 
                        comprehensive coverage options, exceptional customer service, and specialized expertise in the 
                        ${topicName} market.
                      </p>
                    </section>
                    
                    <section id="product-knowledge">
                      <h2>Product Knowledge and Expertise</h2>
                      <p>
                        Deep product knowledge is essential for effectively serving clients in the ${topicName} market. 
                        Stay current with product features, coverage options, and industry developments.
                      </p>
                      <h3>Core Product Understanding</h3>
                      <ul>
                        <li>Comprehensive coverage options and policy features</li>
                        <li>Pricing structures and premium calculation methods</li>
                        <li>Claims processes and support services</li>
                        <li>Policy terms, conditions, and exclusions</li>
                        <li>Add-on products and coverage enhancements</li>
                      </ul>
                      <h3>Continuing Education</h3>
                      <p>
                        Invest in ongoing education and training to maintain your expertise. Attend industry seminars, 
                        complete certification programs, and stay informed about regulatory changes and market developments.
                      </p>
                    </section>
                    
                    <section id="client-relationships">
                      <h2>Building Strong Client Relationships</h2>
                      <p>
                        Long-term success in the ${topicName} market depends on building and maintaining strong client 
                        relationships. Focus on communication, service excellence, and proactive client management.
                      </p>
                      <h3>Communication Best Practices</h3>
                      <ul>
                        <li>Regular check-ins and policy reviews</li>
                        <li>Proactive communication about policy changes and updates</li>
                        <li>Responsive customer service and support</li>
                        <li>Clear and transparent communication about coverage and pricing</li>
                        <li>Personalized communication based on client preferences</li>
                      </ul>
                      <h3>Service Excellence</h3>
                      <p>
                        Deliver exceptional service at every touchpoint. This includes prompt response times, accurate 
                        information, efficient processes, and going above and beyond to meet client needs.
                      </p>
                      <h3>Retention Strategies</h3>
                      <p>
                        Implement strategies to retain existing clients, including renewal incentives, loyalty programs, 
                        and proactive risk management support. Retaining clients is often more cost-effective than 
                        acquiring new ones.
                      </p>
                    </section>
                    
                    <section id="technology-tools">
                      <h2>Technology and Tools</h2>
                      <p>
                        Leverage technology to enhance your efficiency, improve client service, and gain competitive 
                        advantages in the ${topicName} market.
                      </p>
                      <h3>Essential Tools</h3>
                      <ul>
                        <li><strong>CRM Systems:</strong> Manage client relationships and track interactions</li>
                        <li><strong>Analytics Platforms:</strong> Analyze market trends and client behavior</li>
                        <li><strong>Communication Tools:</strong> Facilitate efficient client communication</li>
                        <li><strong>Document Management:</strong> Organize and access client information quickly</li>
                        <li><strong>Marketing Automation:</strong> Streamline marketing campaigns and lead nurturing</li>
                      </ul>
                      <h3>Digital Transformation</h3>
                      <p>
                        Embrace digital transformation initiatives that improve operational efficiency and enhance the 
                        client experience. This includes online portals, mobile applications, and automated processes.
                      </p>
                    </section>
                    
                    <section id="measuring-success">
                      <h2>Measuring Success and KPIs</h2>
                      <p>
                        Track key performance indicators (KPIs) to measure your progress and identify areas for improvement 
                        in your ${topicName} business development efforts.
                      </p>
                      <h3>Key Metrics</h3>
                      <ul>
                        <li><strong>New Client Acquisition:</strong> Number of new clients acquired per period</li>
                        <li><strong>Policy Retention Rate:</strong> Percentage of policies renewed</li>
                        <li><strong>Revenue Growth:</strong> Increase in premium volume and revenue</li>
                        <li><strong>Client Satisfaction:</strong> Feedback scores and satisfaction ratings</li>
                        <li><strong>Conversion Rates:</strong> Percentage of leads converted to clients</li>
                        <li><strong>Average Policy Value:</strong> Average premium per policy</li>
                      </ul>
                      <h3>Performance Review</h3>
                      <p>
                        Conduct regular performance reviews to assess your progress against goals, identify successful 
                        strategies, and adjust your approach as needed. Use data-driven insights to guide decision-making.
                      </p>
                    </section>
                    
                    <section id="best-practices">
                      <h2>Best Practices and Recommendations</h2>
                      <p>
                        Follow these best practices to maximize your success in the ${topicName} market:
                      </p>
                      <ul>
                        <li>Maintain a client-centric approach in all interactions</li>
                        <li>Stay current with industry trends and regulatory changes</li>
                        <li>Invest in ongoing education and professional development</li>
                        <li>Build strong relationships with carriers and partners</li>
                        <li>Leverage technology to improve efficiency and service</li>
                        <li>Focus on quality over quantity in client acquisition</li>
                        <li>Develop a strong value proposition and brand identity</li>
                        <li>Implement systematic follow-up and nurturing processes</li>
                        <li>Seek feedback from clients and continuously improve</li>
                        <li>Build a strong referral network</li>
                      </ul>
                    </section>
                    
                    <section id="conclusion">
                      <h2>Conclusion</h2>
                      <p>
                        Success in the ${topicName} market requires a strategic approach, deep product knowledge, 
                        strong client relationships, and continuous improvement. By following the strategies and best 
                        practices outlined in this guide, you can build a thriving business in this dynamic market.
                      </p>
                      <p>
                        Remember that business development is an ongoing process. Stay adaptable, continue learning, 
                        and always prioritize your clients' needs. With dedication and the right strategies, you can 
                        achieve significant growth and success in the ${topicName} market.
                      </p>
                    </section>
                    
                    <style>
                      .business-strategy-container {
                        width: 100%;
                        margin: 0 auto;
                        padding: 20px;
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        color: #333;
                      }
                      .business-strategy-container h1 {
                        color: #34577f;
                        border-bottom: 3px solid #34577f;
                        padding-bottom: 10px;
                        margin-bottom: 30px;
                      }
                      .business-strategy-container h2 {
                        color: #34577f;
                        margin-top: 40px;
                        margin-bottom: 20px;
                        padding-top: 20px;
                        border-top: 2px solid #e9ecef;
                      }
                      .business-strategy-container h3 {
                        color: #34577f;
                        margin-top: 25px;
                        margin-bottom: 15px;
                      }
                      .business-strategy-container p {
                        margin-bottom: 15px;
                        text-align: justify;
                      }
                      .business-strategy-container ul {
                        margin-bottom: 20px;
                        padding-left: 30px;
                      }
                      .business-strategy-container li {
                        margin-bottom: 10px;
                      }
                      .business-strategy-container strong {
                        color: #34577f;
                      }
                      section {
                        margin-bottom: 40px;
                      }
                    </style>
                `
              }
            ]
          });
        } else {
          // Default structure for other topics
          resolve({
            status: true,
            results: [
              {
                topic: topic,
                text: `<div class="business-strategy-container"><h1>Business Development Strategy Guide</h1><p>Content for topic: ${topic}</p><p>This is a placeholder for business strategy content. Please contact your administrator for specific guidance related to this topic.</p></div>`
              }
            ]
          });
        }
      }, 300);
    });
  }

  /**
   * POST /orchestrators/downloadWinback
   * Get winback download list
   */
  getWinbackDownloadList(month: number): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            month,
            downloads: [
              { id: '1', name: `Winback_Report_${month}_2024.xlsx`, url: 'assets/data/demo-winback.xlsx' },
              { id: '2', name: `Winback_Summary_${month}_2024.pdf`, url: 'assets/data/demo-winback.pdf' }
            ]
          }
        });
      }, 300);
    });
  }

  // ============================================================================
  // NOTIFICATIONS & MAINTENANCE
  // ============================================================================

  /**
   * GET /maintenance/scheduled
   * Get scheduled maintenance info
   */
  getScheduledMaintenance(): Observable<any> {
    // Return null for no maintenance, or return maintenance info
    return of(null).pipe(delay(200));
    // Uncomment below to show maintenance banner:
    // return of({
    //   status: true,
    //   results: {
    //     scheduledDate: '2025-12-25',
    //     scheduledTime: '02:00',
    //     startTime: '2025-12-25T02:00:00Z',
    //     endTime: '2025-12-25T03:00:00Z',
    //     duration: 60,
    //     expectedIssues: ['Service unavailable', 'API downtime']
    //   }
    // }).pipe(delay(200));
  }

  /**
   * GET /health
   * Health check endpoint
   */
  getHealth(): Observable<any> {
    return of({
      status: 'ok',
      timestamp: new Date().toISOString()
    }).pipe(delay(100));
  }

  /**
   * POST /notify/topic/v1
   * Send notification by topic
   */
  sendNotificationByTopic(topics: any, userId: string, payload: any, title: string, body: string, priority: string, pushedBy: string, recipients: any, role: string, state: string, carrier: string, agencyName: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // No-op for demo - just return success
        console.log('[DEMO] Notification sent:', { topics, title, body });
        resolve({
          status: true,
          message: 'Notification sent successfully'
        });
      }, 200);
    });
  }

  /**
   * POST /notify/users
   * Send notification to specific users
   */
  sendNotifications(userIds: string[], payload: any, title: string, body: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // No-op for demo - just return success
        console.log('[DEMO] Notifications sent to users:', userIds);
        resolve({
          status: true,
          message: 'Notifications sent successfully'
        });
      }, 200);
    });
  }

  /**
   * POST /notify/history
   * Get notification history for a user
   */
  getNotificationHistory(userId: string, page: number = 0, count: number = 10): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          // Get stored notifications from localStorage (if any)
          const storageKey = `demo_notifications_${userId}`;
          const storedNotifications = JSON.parse(localStorage.getItem(storageKey) || '[]');
          
          // Default demo notifications
          const defaultNotifications = [
            {
              id: 'notif_001',
              title: 'New Message Received',
              body: 'You have received a new message in the General Discussion channel.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 3600000) / 1000) // 1 hour ago
              }
            },
            {
              id: 'notif_002',
              title: 'Channel Update',
              body: 'A new member has joined the Support Channel.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 7200000) / 1000) // 2 hours ago
              }
            },
            {
              id: 'notif_003',
              title: 'High Priority Alert',
              body: 'Important system update: Scheduled maintenance will occur tonight at 2 AM.',
              priority: true,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 8640000) / 1000) // ~2.4 hours ago
              }
            },
            {
              id: 'notif_004',
              title: 'New Channel Created',
              body: 'A new private channel "Project Discussion" has been created.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 17280000) / 1000) // ~4.8 hours ago
              }
            },
            {
              id: 'notif_005',
              title: 'Message Reply',
              body: 'John Smith replied to your message in the Announcements channel.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 25920000) / 1000) // ~7.2 hours ago
              }
            },
            {
              id: 'notif_006',
              title: 'System Notification',
              body: 'Your profile has been updated successfully.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 34560000) / 1000) // ~9.6 hours ago
              }
            },
            {
              id: 'notif_007',
              title: 'Channel Invitation',
              body: 'You have been invited to join the "Team Collaboration" channel.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 43200000) / 1000) // 12 hours ago
              }
            },
            {
              id: 'notif_008',
              title: 'Urgent: Action Required',
              body: 'Please review and approve the pending channel request.',
              priority: true,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 51840000) / 1000) // ~14.4 hours ago
              }
            },
            {
              id: 'notif_009',
              title: 'New Message',
              body: 'You have 3 unread messages in the Private Channel.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 60480000) / 1000) // ~16.8 hours ago
              }
            },
            {
              id: 'notif_010',
              title: 'Weekly Summary',
              body: 'Your weekly activity summary is now available. Check your dashboard for details.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 69120000) / 1000) // ~19.2 hours ago
              }
            },
            {
              id: 'notif_011',
              title: 'Channel Moderator Assignment',
              body: 'You have been assigned as a moderator for the General Discussion channel.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 77760000) / 1000) // ~21.6 hours ago
              }
            },
            {
              id: 'notif_012',
              title: 'File Upload Notification',
              body: 'A new file has been uploaded to the Support Channel.',
              priority: false,
              userId: userId,
              createdAt: {
                _seconds: Math.floor((Date.now() - 86400000) / 1000) // 24 hours ago
              }
            }
          ];
          
          // Merge stored notifications with default ones (stored ones first, then defaults)
          const allNotifications = [...storedNotifications, ...defaultNotifications];
          
          // Sort by createdAt (newest first)
          allNotifications.sort((a, b) => {
            const aTime = a.createdAt?._seconds || 0;
            const bTime = b.createdAt?._seconds || 0;
            return bTime - aTime;
          });
          
          // Paginate
          const startIndex = page * count;
          const endIndex = startIndex + count;
          const paginatedNotifications = allNotifications.slice(startIndex, endIndex);
          const total = allNotifications.length;
          
          resolve({
            status: true,
            results: {
              results: paginatedNotifications,
              pagination: {
                total: total,
                page: page,
                count: count,
                pages: Math.ceil(total / count)
              }
            }
          });
        } catch (error: any) {
          console.error('[DemoDataService] getNotificationHistory error:', error);
          resolve({
            status: true,
            results: {
              results: [],
              pagination: {
                total: 0,
                page: page,
                count: count,
                pages: 0
              }
            }
          });
        }
      }, 300);
    });
  }

  /**
   * POST /notify/alert/search
   * Get alerts/notifications for admin/agency admin users
   */
  getAlertsDemo(page: number = 0, count: number = 10, role: string = 'admin'): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          console.log('[DemoDataService] getAlertsDemo called with:', { page, count, role });
          // Get stored alerts from localStorage (if any)
          const storageKey = 'demo_alerts';
          const storedAlerts = JSON.parse(localStorage.getItem(storageKey) || '[]');
          console.log('[DemoDataService] Stored alerts from localStorage:', storedAlerts.length);
          
          // Default demo alerts - Example records as specified
          const defaultAlerts = [
            {
              id: 'alert_001',
              title: 'New Business Policies',
              body: 'Cancelled policies exceeded 50',
              priority: false,
              pushedBy: {
                _id: 'admin_001',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agency Owner' }
              ],
              recipientsemails: 'owner1@agency.com, owner2@agency.com',
              createdAt: {
                _seconds: Math.floor(new Date('2026-01-28').getTime() / 1000) // Jan 28 2026
              }
            },
            {
              id: 'alert_002',
              title: 'Loss Ratio',
              body: 'Allied Loss Ratio exceeded 14%',
              priority: false,
              pushedBy: {
                _id: 'admin_002',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Allied Agents & Owners' }
              ],
              recipientsemails: 'allied.agent1@example.com, allied.owner@example.com',
              createdAt: {
                _seconds: Math.floor(new Date('2026-01-20').getTime() / 1000) // Jan 20 2026
              }
            },
            {
              id: 'alert_003',
              title: 'Retention Rate',
              body: 'Commercial Line Retention Rate YTD dropped below 90%',
              priority: false,
              pushedBy: {
                _id: 'admin_003',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Commercial Line Agent' }
              ],
              recipientsemails: '',
              createdAt: {
                _seconds: Math.floor(new Date('2026-01-15').getTime() / 1000) // Jan 15 2026
              }
            },
            {
              id: 'alert_006',
              title: 'Loss Ratio Alert',
              body: 'Progressive Loss Ratio YTD exceeded 13% threshold',
              priority: true,
              pushedBy: {
                _id: 'admin_001',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Progressive Agent' },
                { role: 'Agency Owner' }
              ],
              recipientsemails: 'progressive.manager@example.com',
              createdAt: {
                _seconds: Math.floor(new Date('2026-01-12').getTime() / 1000) // Jan 12 2026
              }
            },
            {
              id: 'alert_007',
              title: 'Commission by Carrier',
              body: 'State Farm Commission YTD dropped below $800,000',
              priority: false,
              pushedBy: {
                _id: 'admin_002',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'State Farm Agent' },
                { role: 'Agency Owner' }
              ],
              recipientsemails: 'statefarm.admin@example.com, statefarm.agent@example.com',
              createdAt: {
                _seconds: Math.floor(new Date('2026-01-08').getTime() / 1000) // Jan 8 2026
              }
            },
            {
              id: 'alert_008',
              title: 'Commission by LOB',
              body: 'Auto Line Commission YTD exceeded $1,200,000 milestone',
              priority: false,
              pushedBy: {
                _id: 'admin_004',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' }
              ],
              recipientsemails: '',
              createdAt: {
                _seconds: Math.floor(new Date('2026-01-03').getTime() / 1000) // Jan 3 2026
              }
            },
            {
              id: 'alert_009',
              title: 'Targeted Opportunities',
              body: 'Multiline opportunities - Monoline policies count exceeded 50',
              priority: true,
              pushedBy: {
                _id: 'admin_001',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' }
              ],
              recipientsemails: 'opportunities.team@example.com',
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-30').getTime() / 1000) // Dec 30 2025
              }
            },
            {
              id: 'alert_010',
              title: 'Production Characteristics',
              body: 'Auto policies with high premium increased by 20% in Texas',
              priority: false,
              pushedBy: {
                _id: 'admin_001',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' },
                { states: ['TX'] }
              ],
              recipientsemails: 'texas.agents@example.com',
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-25').getTime() / 1000) // Dec 25 2025
              }
            },
            {
              id: 'alert_011',
              title: 'Retention Rate',
              body: 'Home Line Retention Rate YTD dropped below 91%',
              priority: false,
              pushedBy: {
                _id: 'admin_003',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' },
                { states: ['CA', 'NY', 'FL'] }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-22').getTime() / 1000) // Dec 22 2025
              }
            },
            {
              id: 'alert_012',
              title: 'Commission by Sub-LOB',
              body: 'Commercial Auto Commission YTD fell below $200,000',
              priority: true,
              pushedBy: {
                _id: 'admin_001',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Commercial Line Agent' }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-18').getTime() / 1000) // Dec 18 2025
              }
            },
            {
              id: 'alert_013',
              title: 'Loss Ratio Alert',
              body: 'State Farm Loss Ratio YTD exceeded 12% threshold',
              priority: true,
              pushedBy: {
                _id: 'admin_001',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'State Farm Agent' },
                { role: 'Agency Owner' }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2026-01-10').getTime() / 1000) // Jan 10 2026
              }
            },
            {
              id: 'alert_014',
              title: 'Retention Rate',
              body: 'Personal Auto Retention Rate dropped below 88%',
              priority: true,
              pushedBy: {
                _id: 'admin_002',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' },
                { carriers: ['Progressive', 'State Farm'] }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2026-01-05').getTime() / 1000) // Jan 5 2026
              }
            },
            {
              id: 'alert_015',
              title: 'Commission Alert',
              body: 'Commission by Carrier Type - Direct channel commission dropped 10%',
              priority: false,
              pushedBy: {
                _id: 'admin_003',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' },
                { carriers: ['Allied'] }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-28').getTime() / 1000) // Dec 28 2025
              }
            },
            {
              id: 'alert_016',
              title: 'Production Characteristics',
              body: 'Home policies with high premium increased by 25% in California',
              priority: false,
              pushedBy: {
                _id: 'admin_004',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' },
                { states: ['CA'] }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-20').getTime() / 1000) // Dec 20 2025
              }
            },
            {
              id: 'alert_017',
              title: 'Loss Ratio Alert',
              body: 'Progressive Loss Ratio Prior Year exceeded 15%',
              priority: true,
              pushedBy: {
                _id: 'admin_001',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Progressive Agent' },
                { role: 'Agency Owner' }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-15').getTime() / 1000) // Dec 15 2025
              }
            },
            {
              id: 'alert_018',
              title: 'Targeted Opportunities',
              body: 'Telematics opportunities - Sold without device count exceeded 70',
              priority: false,
              pushedBy: {
                _id: 'admin_002',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-10').getTime() / 1000) // Dec 10 2025
              }
            },
            {
              id: 'alert_019',
              title: 'Win-Back Opportunities',
              body: 'Win-back policies in December exceeded 45 with premium over $90,000',
              priority: false,
              pushedBy: {
                _id: 'admin_003',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agency Owner' },
                { role: 'Agent' }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-12-05').getTime() / 1000) // Dec 5 2025
              }
            },
            {
              id: 'alert_020',
              title: 'Commission by LOB',
              body: 'Commercial Line Commission YTD dropped below $300,000',
              priority: true,
              pushedBy: {
                _id: 'admin_001',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Commercial Line Agent' },
                { carriers: ['Travelers', 'Farmers'] }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-11-28').getTime() / 1000) // Nov 28 2025
              }
            },
            {
              id: 'alert_021',
              title: 'Retention Rate',
              body: 'Homeowners Retention Rate YTD fell below 92%',
              priority: false,
              pushedBy: {
                _id: 'admin_002',
                name: 'System Administrator',
                email: 'admin@example.com'
              },
              recipients: [
                { role: 'Agent' },
                { states: ['TX', 'FL'] }
              ],
              createdAt: {
                _seconds: Math.floor(new Date('2025-11-20').getTime() / 1000) // Nov 20 2025
              }
            }
          ];
          
          // Merge stored alerts with default ones (stored ones first, then defaults)
          const allAlerts = [...storedAlerts, ...defaultAlerts];
          
          // Sort by createdAt (newest first)
          allAlerts.sort((a, b) => {
            const aTime = a.createdAt?._seconds || 0;
            const bTime = b.createdAt?._seconds || 0;
            return bTime - aTime;
          });
          
          // Paginate
          const startIndex = page * count;
          const endIndex = startIndex + count;
          const paginatedAlerts = allAlerts.slice(startIndex, endIndex);
          const total = allAlerts.length;
          
          console.log('[DemoDataService] Returning alerts:', {
            total,
            page,
            count,
            returned: paginatedAlerts.length,
            firstAlert: paginatedAlerts[0]
          });
          
          resolve({
            status: true,
            results: {
              results: paginatedAlerts,
              pagination: {
                total: total,
                page: page,
                count: count,
                pages: Math.ceil(total / count)
              }
            }
          });
        } catch (error: any) {
          console.error('[DemoDataService] getAlertsDemo error:', error);
          resolve({
            status: true,
            results: {
              results: [],
              pagination: {
                total: 0,
                page: page,
                count: count,
                pages: 0
              }
            }
          });
        }
      }, 300);
    });
  }

  /**
   * Save a new alert to localStorage
   * Called when creating a new alert through the compose dialog
   */
  saveAlert(alertData: any): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          const storageKey = 'demo_alerts';
          const existingAlerts = JSON.parse(localStorage.getItem(storageKey) || '[]');
          
          // Extract data from the payload structure
          // The data comes as: { userIds, notification: { title, body }, data: { title, body, priority, role, states, carriers, access_type } }
          const payload = alertData.data || {};
          const notification = alertData.notification || {};
          
          const title = payload.title || notification.title || alertData.title || '';
          const body = payload.body || notification.body || alertData.body || '';
          const priority = payload.priority === 'true' || payload.priority === true || alertData.priority === 'true' || alertData.priority === true;
          const schedule = payload.schedule || alertData.schedule || 'Schedule 1';
          
          // Calculate trigger date/time based on schedule
          let triggerDate: Date;
          if (schedule === 'Schedule 2') {
            // Tomorrow at 1 PM
            triggerDate = new Date();
            triggerDate.setDate(triggerDate.getDate() + 1);
            triggerDate.setHours(13, 0, 0, 0); // 1 PM
          } else {
            // Schedule 1: Today + 3 hours
            triggerDate = new Date();
            triggerDate.setHours(triggerDate.getHours() + 3);
          }
          
          // Build recipients array from the payload data
          const recipients: any[] = [];
          if (payload.role) {
            recipients.push({ role: payload.role });
          }
          if (payload.states && payload.states.length > 0) {
            const statesArray = Array.isArray(payload.states) ? payload.states : [payload.states];
            recipients.push({ states: statesArray });
          }
          if (payload.carriers && payload.carriers.length > 0) {
            const carriersArray = Array.isArray(payload.carriers) ? payload.carriers : [payload.carriers];
            recipients.push({ carriers: carriersArray });
          }
          if (payload.access_type && payload.access_type.length > 0) {
            const accessArray = Array.isArray(payload.access_type) ? payload.access_type : [payload.access_type];
            recipients.push({ accessPoint: accessArray });
          }
          
          // If no recipients from payload, create a default one
          if (recipients.length === 0) {
            recipients.push({ role: 'Agent' });
          }
          
          // Get recipientsemails from payload
          const recipientsemails = payload.recipientsemails || alertData.recipientsemails || '';
          
          // Create new alert object
          const newAlert = {
            id: `alert_${Date.now()}`,
            title: title,
            body: body,
            priority: priority,
            pushedBy: {
              _id: alertData.userId || alertData.pushedBy?._id || 'current_user',
              name: alertData.pushedBy?.name || 'Current User',
              email: alertData.pushedBy?.email || 'user@example.com'
            },
            recipients: recipients,
            recipientsemails: recipientsemails,
            createdAt: {
              _seconds: Math.floor(triggerDate.getTime() / 1000)
            }
          };
          
          // Add to beginning of array (newest first)
          existingAlerts.unshift(newAlert);
          
          // Save back to localStorage
          localStorage.setItem(storageKey, JSON.stringify(existingAlerts));
          
          console.log('[DemoDataService] Saved new alert:', newAlert);
          
          resolve({
            status: true,
            message: 'Alert saved successfully',
            alert: newAlert
          });
        } catch (error: any) {
          console.error('[DemoDataService] Error saving alert:', error);
          resolve({
            status: false,
            message: 'Failed to save alert',
            error: error.message
          });
        }
      }, 300);
    });
  }

  // ============================================================================
  // CHAT / SENDBIRD ENDPOINTS
  // ============================================================================

  /**
   * POST /sendbird/user/createGroupChannel
   * Create a group channel
   */
  createGroupChannel(userIds: string[], name: string, operatorIds: string[] = []): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const channelUrl = `group_channel_${Date.now()}`;
        resolve({
          status: true,
          results: {
            channel_url: channelUrl,
            name,
            members: userIds.map(id => ({ userId: id })),
            operators: operatorIds
          }
        });
      }, 300);
    });
  }

  /**
   * POST /sendbird/user/listgroupchannel
   * List group channels
   */
  listGroupChannels(userId?: string, limit: number = 20, token?: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            channels: [
              {
                channel_url: 'channel_1',
                name: 'General Discussion',
                members: [{ userId: 'demo_user_123' }, { userId: 'user_2' }],
                last_message: {
                  message: 'Hello everyone!',
                  timestamp: Date.now() - 3600000
                },
                unread_count: 2
              },
              {
                channel_url: 'channel_2',
                name: 'Support',
                members: [{ userId: 'demo_user_123' }, { userId: 'user_3' }],
                last_message: {
                  message: 'How can I help?',
                  timestamp: Date.now() - 7200000
                },
                unread_count: 0
              }
            ],
            next: null
          }
        });
      }, 300);
    });
  }

  /**
   * POST /sendbird/user/listmessages
   * List messages in a channel
   */
  listMessages(channelUrl: string, lastTimestamp: number, limit: number): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            messages: [
              {
                message_id: 'msg_1',
                message: 'Hello!',
                user_id: 'user_2',
                timestamp: Date.now() - 3600000,
                type: 'user'
              },
              {
                message_id: 'msg_2',
                message: 'Hi there!',
                user_id: 'demo_user_123',
                timestamp: Date.now() - 3500000,
                type: 'user'
              }
            ],
            next: null
          }
        });
      }, 300);
    });
  }

  /**
   * POST /sendbird/user/sendmessage
   * Send a message
   */
  sendMessage(channelUrl: string, message: string, messageType: string, userId: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            message_id: `msg_${Date.now()}`,
            message,
            user_id: userId,
            timestamp: Date.now(),
            type: messageType
          }
        });
      }, 300);
    });
  }

  /**
   * POST /channel/get
   * Get channel preferences
   */
  getChannelPreferences(userId: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            channels: [
              { channelId: 'channel_1', dnd: false },
              { channelId: 'channel_2', dnd: true }
            ]
          }
        });
      }, 200);
    });
  }

  /**
   * POST /channel/add
   * Add channel preferences
   */
  addChannelPreferences(userId: string, channelId: string, dnd: boolean): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          message: 'Channel preferences updated'
        });
      }, 200);
    });
  }

  /**
   * POST /channel/pinned/get
   * Get pinned messages
   */
  getPinnedMessage(channelId: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          results: {
            pinned_messages: []
          }
        });
      }, 200);
    });
  }

  /**
   * POST /channel/pinned/add
   * Add pinned message
   */
  addPinnedMessage(channelId: string, messageId: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          message: 'Message pinned successfully'
        });
      }, 200);
    });
  }

  /**
   * POST /channel/pinned/delete
   * Delete pinned message
   */
  deletePinnedMessage(channelId: string, messageId: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: true,
          message: 'Message unpinned successfully'
        });
      }, 200);
    });
  }

  // Other Sendbird endpoints - all return success for demo
  updateMessage(data: any, url?: string, customType?: string): Promise<any> {
    return Promise.resolve({ status: true, message: 'Message updated' });
  }

  updateMessageMetadata(channelUrl: string, messageId: string, metadata: any): Promise<any> {
    return Promise.resolve({ status: true, message: 'Metadata updated' });
  }

  createSendbirdUser(userId: string, profile: string, name: string): Promise<any> {
    return Promise.resolve({ status: true, results: { userId, profile, name } });
  }

  registerOperatorsGroupChannel(operatorIds: string[], channelUrl: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          console.log('[DemoDataService] registerOperatorsGroupChannel called with:', { operatorIds, channelUrl });
          // Get existing operators from localStorage
          const storageKey = `demo_operators_${channelUrl}`;
          console.log('[DemoDataService] Storage key:', storageKey);
          const existingOperators = JSON.parse(localStorage.getItem(storageKey) || '[]');
          console.log('[DemoDataService] Existing operators:', existingOperators);
          
          // Get user data to resolve nicknames
          const contactList = this.getAllContactListSync();
          const allUsers = [...(contactList.agents || []), ...(contactList.admins || [])];
          
          // Helper function to get user nickname from userId
          const getUserNickname = (userId: string): string => {
            const user = allUsers.find(u => {
              const contactName = u.ContactName || '';
              const generatedId = `${u.Role === 'Agency Admin' ? 'admin' : 'agent'}_${contactName.toLowerCase().replace(/\s+/g, '_')}`;
              return generatedId === userId;
            });
            if (user) {
              return user.ContactName;
            }
            if (userId.startsWith('agent_') || userId.startsWith('admin_')) {
              return userId.replace(/^(agent_|admin_)/, '').replace(/_/g, ' ');
            }
            return userId;
          };
          
          const getUserRole = (userId: string): string => {
            if (userId.startsWith('admin_')) {
              return 'Agency Admin';
            }
            return 'Agent';
          };
          
          // Add new operators (avoid duplicates)
          const existingOperatorIds = existingOperators.map((op: any) => op.user_id || op.userId);
          const newOperators = operatorIds
            .filter(userId => !existingOperatorIds.includes(userId))
            .map((userId: string) => {
              const nickname = getUserNickname(userId);
              return {
                user_id: userId, // Primary field - component uses this for comparison
                userId: userId, // Support both formats
                nickname: nickname,
                metaData: { 
                  role: getUserRole(userId),
                  agency: 'Demo Insurance Agency'
                },
                plainProfileUrl: 'assets/images/profile.png'
              };
            });
          
          const updatedOperators = [...existingOperators, ...newOperators];
          
          // Ensure all existing operators also have the correct format
          const formattedOperators = updatedOperators.map((op: any) => ({
            ...op,
            user_id: op.user_id || op.userId, // Ensure user_id is always present
            userId: op.userId || op.user_id,
            nickname: op.nickname || op.user_id || op.userId
          }));
          
          // Save back to localStorage
          localStorage.setItem(storageKey, JSON.stringify(formattedOperators));
          
          // Verify it was saved
          const verifySaved = JSON.parse(localStorage.getItem(storageKey) || '[]');
          console.log('[DemoDataService] Registered', newOperators.length, 'operators for channel', channelUrl);
          console.log('[DemoDataService] Storage key used:', storageKey);
          console.log('[DemoDataService] Total operators for channel:', formattedOperators.length, formattedOperators);
          console.log('[DemoDataService] Verified saved operators:', verifySaved);
          resolve({ status: true, message: 'Operators registered' });
        } catch (error: any) {
          console.error('[DemoDataService] registerOperatorsGroupChannel error:', error);
          resolve({ status: true, message: 'Operators registered' }); // Still resolve to not break the flow
        }
      }, 200);
    });
  }

  unregisterGroupChannel(operatorIds: string[], channelUrl: string, deleteAll: boolean): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          const storageKey = `demo_operators_${channelUrl}`;
          
          if (deleteAll) {
            // Remove all operators
            localStorage.removeItem(storageKey);
            console.log('[DemoDataService] Removed all operators from channel', channelUrl);
          } else {
            // Remove specific operators
            const existingOperators = JSON.parse(localStorage.getItem(storageKey) || '[]');
            const updatedOperators = existingOperators.filter((op: any) => {
              const opId = op.user_id || op.userId;
              return !operatorIds.includes(opId);
            });
            
            localStorage.setItem(storageKey, JSON.stringify(updatedOperators));
            console.log('[DemoDataService] Removed', operatorIds.length, 'operators from channel', channelUrl);
          }
          
          resolve({ status: true, message: 'Operators unregistered' });
        } catch (error: any) {
          console.error('[DemoDataService] unregisterGroupChannel error:', error);
          resolve({ status: true, message: 'Operators unregistered' }); // Still resolve to not break the flow
        }
      }, 200);
    });
  }

  listOperatorsGroupChannel(limit: number, channelUrl: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          console.log('[DemoDataService] listOperatorsGroupChannel called with:', { limit, channelUrl });
          // Get operators from localStorage
          const storageKey = `demo_operators_${channelUrl}`;
          console.log('[DemoDataService] Storage key for retrieval:', storageKey);
          const operators = JSON.parse(localStorage.getItem(storageKey) || '[]');
          
          console.log('[DemoDataService] Raw operators from storage:', operators);
          
          // Debug: Check all localStorage keys that start with demo_operators_
          const allKeys = Object.keys(localStorage).filter(key => key.startsWith('demo_operators_'));
          console.log('[DemoDataService] All operator storage keys:', allKeys);
          if (allKeys.length > 0) {
            allKeys.forEach(key => {
              const value = JSON.parse(localStorage.getItem(key) || '[]');
              console.log(`[DemoDataService] Key ${key} has ${value.length} operators:`, value);
            });
          }
          
          // Ensure operators have both user_id and userId for compatibility
          // The component expects user_id for comparison, so make sure it's present
          const formattedOperators = operators.map((op: any) => {
            const userId = op.user_id || op.userId;
            return {
              ...op,
              user_id: userId, // Primary field for component comparison
              userId: userId, // Also include for compatibility
              nickname: op.nickname || userId, // Ensure nickname exists
              metaData: op.metaData || {},
              plainProfileUrl: op.plainProfileUrl || 'assets/images/profile.png'
            };
          });
          
          console.log('[DemoDataService] Retrieved', formattedOperators.length, 'operators for channel', channelUrl, ':', formattedOperators);
          resolve({ 
            status: true, 
            results: { 
              operators: formattedOperators.slice(0, limit),
              next: null // No pagination for demo
            } 
          });
        } catch (error: any) {
          console.error('[DemoDataService] listOperatorsGroupChannel error:', error);
          resolve({ status: true, results: { operators: [], next: null } });
        }
      }, 200);
    });
  }

  inviteMember(userIds: string[], channelUrl: string, inviterId: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          // Get existing channels from localStorage
          const storageKey = 'demo_channels';
          const channels = JSON.parse(localStorage.getItem(storageKey) || '[]');
          
          // Find the channel
          const channelIndex = channels.findIndex((ch: any) => ch.url === channelUrl);
          
          if (channelIndex !== -1) {
            // Get user data to resolve nicknames
            const contactList = this.getAllContactListSync();
            const allUsers = [...(contactList.agents || []), ...(contactList.admins || [])];
            
            // Helper function to get user nickname from userId
            const getUserNickname = (userId: string): string => {
              const user = allUsers.find(u => {
                const contactName = u.ContactName || '';
                const generatedId = `${u.Role === 'Agency Admin' ? 'admin' : 'agent'}_${contactName.toLowerCase().replace(/\s+/g, '_')}`;
                return generatedId === userId;
              });
              if (user) {
                return user.ContactName;
              }
              if (userId.startsWith('agent_') || userId.startsWith('admin_')) {
                return userId.replace(/^(agent_|admin_)/, '').replace(/_/g, ' ');
              }
              return userId;
            };
            
            const getUserRole = (userId: string): string => {
              if (userId.startsWith('admin_')) {
                return 'Agency Admin';
              }
              return 'Agent';
            };
            
            // Add new members to the channel
            const existingMemberIds = channels[channelIndex].members.map((m: any) => m.userId);
            const newMembers = userIds
              .filter(userId => !existingMemberIds.includes(userId))
              .map((userId: string) => ({
                userId: userId,
                nickname: getUserNickname(userId),
                metaData: { 
                  role: getUserRole(userId),
                  agency: 'Demo Insurance Agency'
                },
                plainProfileUrl: 'assets/images/profile.png'
              }));
            
            channels[channelIndex].members = [...channels[channelIndex].members, ...newMembers];
            channels[channelIndex].memberCount = channels[channelIndex].members.length;
            
            // Save back to localStorage
            localStorage.setItem(storageKey, JSON.stringify(channels));
            
            console.log('[DemoDataService] Invited', newMembers.length, 'members to channel', channelUrl);
          }
          
          resolve({ status: true, message: 'Members invited' });
        } catch (error: any) {
          console.error('[DemoDataService] inviteMember error:', error);
          resolve({ status: true, message: 'Members invited' }); // Still resolve to not break the flow
        }
      }, 200);
    });
  }

  leaveChannel(userIds: string[], channelUrl: string, reason: string): Promise<any> {
    return Promise.resolve({ status: true, message: 'Left channel' });
  }

  registerUserDevice(userId: string, tokenType: string, gcmRegToken: string): Promise<any> {
    return Promise.resolve({ status: true, message: 'Device registered' });
  }

  unregisterUserDevice(userId: string, tokenType: string, token: string): Promise<any> {
    return Promise.resolve({ status: true, message: 'Device unregistered' });
  }

  updateGroupChannel(name: string, customType: string, channelUrl: string): Promise<any> {
    return Promise.resolve({ status: true, message: 'Channel updated' });
  }

  listMembersGroups(limit: number, channelUrl: string): Promise<any> {
    return Promise.resolve({ status: true, results: { members: [] } });
  }

  deleteGroupChannel(channelUrl: string): Promise<any> {
    return Promise.resolve({ status: true, message: 'Channel deleted' });
  }

  getMessage(channelUrl: string, messageId: string): Promise<any> {
    return Promise.resolve({ status: true, results: { message: {} } });
  }

  // ============================================================================
  // SENDBIRD SDK DEMO DATA (for methods that use SDK directly, not HTTP API)
  // ============================================================================

  /**
   * Get demo users for search (Agency Admins and Agents)
   * Used by SendbirdService.fetchUsersBySearchText()
   * Uses the same users as getAllContactList() to ensure consistency
   */
  getSendbirdUsersBySearchTextDemo(searchText: string, limit: number = 20): Promise<any[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Get the same users from getAllContactList
        const contactListData = this.getAllContactListSync();
        
        // Convert agents and admins to Sendbird format
        const demoUsers: any[] = [];
        
        // Add agents
        if (contactListData.agents) {
          contactListData.agents.forEach((agent: any, index: number) => {
            // Generate userId from ContactName (lowercase, replace spaces with underscores)
            const userId = `agent_${agent.ContactName.toLowerCase().replace(/\s+/g, '_')}`;
            demoUsers.push({
              userId: userId,
              nickname: agent.ContactName,
              plainProfileUrl: 'assets/images/profile.png',
              metaData: { 
                role: agent.Role || 'Agent', 
                agency: agent.Agency || 'Demo Insurance Agency',
                email: agent.MainAgencyEmail || agent.PersonalEmail
              }
            });
          });
        }
        
        // Add admins
        if (contactListData.admins) {
          contactListData.admins.forEach((admin: any, index: number) => {
            // Generate userId from ContactName (lowercase, replace spaces with underscores)
            const userId = `admin_${admin.ContactName.toLowerCase().replace(/\s+/g, '_')}`;
            demoUsers.push({
              userId: userId,
              nickname: admin.ContactName,
              plainProfileUrl: 'assets/images/profile.png',
              metaData: { 
                role: admin.Role || 'Agency Admin', 
                agency: admin.Agency || 'Demo Insurance Agency',
                email: admin.MainAgencyEmail || admin.PersonalEmail
              }
            });
          });
        }

        // Filter by search text (case-insensitive, starts with)
        let filteredUsers = demoUsers;
        if (searchText && searchText.trim()) {
          const searchLower = searchText.toLowerCase().trim();
          filteredUsers = demoUsers.filter(user => 
            user.nickname.toLowerCase().startsWith(searchLower) ||
            user.userId.toLowerCase().startsWith(searchLower) ||
            (user.metaData?.email && user.metaData.email.toLowerCase().startsWith(searchLower))
          );
        }

        // Limit results
        const limitedUsers = filteredUsers.slice(0, limit);
        resolve(limitedUsers);
      }, 200);
    });
  }

  /**
   * Synchronous version of getAllContactList for internal use
   * Returns the same data structure without Promise wrapper
   */
  private getAllContactListSync(): any {
    return {
      agents: [
        {
          ContactName: 'John Smith',
          Agency: 'Demo Insurance Agency',
          MainAgencyEmail: 'contact@demoagency.com',
          Role: 'Agent',
          BusinessEmail: 'john.smith.business@demoagency.com',
          PersonalEmail: 'john.smith@example.com',
          Type: 'Agent'
        },
        {
          ContactName: 'Jane Doe',
          Agency: 'Demo Insurance Agency',
          MainAgencyEmail: 'contact@demoagency.com',
          Role: 'Agent',
          BusinessEmail: 'jane.doe.business@demoagency.com',
          PersonalEmail: 'jane.doe@example.com',
          Type: 'Agent'
        },
        {
          ContactName: 'Bob Johnson',
          Agency: 'Agency 2',
          MainAgencyEmail: 'contact@agency2.com',
          Role: 'Agent',
          BusinessEmail: 'bob.johnson.business@agency2.com',
          PersonalEmail: 'bob.johnson@example.com',
          Type: 'Agent'
        },
        {
          ContactName: 'Alice Williams',
          Agency: 'Agency 2',
          MainAgencyEmail: 'contact@agency2.com',
          Role: 'Agent',
          BusinessEmail: 'alice.williams.business@agency2.com',
          PersonalEmail: 'alice.williams@example.com',
          Type: 'Agent'
        },
        {
          ContactName: 'Charlie Brown',
          Agency: 'Agency 3',
          MainAgencyEmail: 'contact@agency3.com',
          Role: 'Agent',
          BusinessEmail: 'charlie.brown.business@agency3.com',
          PersonalEmail: 'charlie.brown@example.com',
          Type: 'Agent'
        },
        {
          ContactName: 'Diana Prince',
          Agency: 'Demo Insurance Agency',
          MainAgencyEmail: 'contact@demoagency.com',
          Role: 'Agent',
          BusinessEmail: 'diana.prince.business@demoagency.com',
          PersonalEmail: 'diana.prince@example.com',
          Type: 'Agent'
        },
        {
          ContactName: 'Edward Norton',
          Agency: 'Agency 4',
          MainAgencyEmail: 'contact@agency4.com',
          Role: 'Agent',
          BusinessEmail: 'edward.norton.business@agency4.com',
          PersonalEmail: 'edward.norton@example.com',
          Type: 'Agent'
        },
        {
          ContactName: 'Frank Miller',
          Agency: 'Agency 5',
          MainAgencyEmail: 'contact@agency5.com',
          Role: 'Agent',
          BusinessEmail: 'frank.miller.business@agency5.com',
          PersonalEmail: 'frank.miller@example.com',
          Type: 'Agent'
        }
      ],
      admins: [
        {
          ContactName: 'Admin User 1',
          Agency: 'Demo Insurance Agency',
          MainAgencyEmail: 'contact@demoagency.com',
          Role: 'Agency Admin',
          BusinessEmail: 'admin1.business@demoagency.com',
          PersonalEmail: 'admin1@example.com',
          Type: 'Admin'
        },
        {
          ContactName: 'Admin User 2',
          Agency: 'Agency 2',
          MainAgencyEmail: 'contact@agency2.com',
          Role: 'Agency Admin',
          BusinessEmail: 'admin2.business@agency2.com',
          PersonalEmail: 'admin2@example.com',
          Type: 'Admin'
        },
        {
          ContactName: 'Admin User 3',
          Agency: 'Agency 3',
          MainAgencyEmail: 'contact@agency3.com',
          Role: 'Agency Admin',
          BusinessEmail: 'admin3.business@agency3.com',
          PersonalEmail: 'admin3@example.com',
          Type: 'Admin'
        },
        {
          ContactName: 'Admin User 4',
          Agency: 'Demo Insurance Agency',
          MainAgencyEmail: 'contact@demoagency.com',
          Role: 'Agency Admin',
          BusinessEmail: 'admin4.business@demoagency.com',
          PersonalEmail: 'admin4@example.com',
          Type: 'Admin'
        },
        {
          ContactName: 'Admin User 5',
          Agency: 'Agency 4',
          MainAgencyEmail: 'contact@agency4.com',
          Role: 'Agency Admin',
          BusinessEmail: 'admin5.business@agency4.com',
          PersonalEmail: 'admin5@example.com',
          Type: 'Admin'
        }
      ]
    };
  }

  /**
   * Get demo admin users for Sendbird SDK
   * Used by SendbirdService.getAdmins()
   */
  getSendbirdAdminsDemo(limit: number = 100): Promise<any[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          // Get admins from the same contact list as users page for consistency
          const contactList = this.getAllContactListSync();
          const adminUsers = contactList.admins || [];
          
          // Convert to Sendbird admin format
          const admins = adminUsers.map((admin: any) => {
            const userId = `admin_${admin.ContactName.toLowerCase().replace(/\s+/g, '_')}`;
            return {
              userId: userId,
              user_id: userId, // Support both formats for compatibility
              nickname: admin.ContactName,
              profileUrl: 'assets/images/profile.png',
              plainProfileUrl: 'assets/images/profile.png',
              metaData: {
                role: 'PGI-Admin',
                agency: admin.Agency || 'Demo Insurance Agency'
              }
            };
          });
          
          resolve(admins.slice(0, limit));
        } catch (error: any) {
          const errorMessage = error?.message || error?.toString() || 'Unknown error in getSendbirdAdminsDemo';
          console.error('[DemoDataService] getSendbirdAdminsDemo error:', errorMessage, error);
          // Resolve with empty array instead of rejecting to prevent unhandled promise rejection
          resolve([]);
        }
      }, 300);
    });
  }

  /**
   * Create a demo channel and store it in localStorage
   * Used by SendbirdService.createChannel()
   */
  createSendbirdChannelDemo(channel: any): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          // Get existing channels from localStorage
          const storageKey = 'demo_channels';
          const existingChannels = JSON.parse(localStorage.getItem(storageKey) || '[]');
          
          // Get user data to resolve nicknames
          const contactList = this.getAllContactListSync();
          const allUsers = [...(contactList.agents || []), ...(contactList.admins || [])];
          
          // Helper function to get user nickname from userId
          const getUserNickname = (userId: string): string => {
            // Try to find in contact list
            const user = allUsers.find(u => {
              const contactName = u.ContactName || '';
              const generatedId = `${u.Role === 'Agency Admin' ? 'admin' : 'agent'}_${contactName.toLowerCase().replace(/\s+/g, '_')}`;
              return generatedId === userId;
            });
            if (user) {
              return user.ContactName;
            }
            // Fallback: try to extract from userId
            if (userId.startsWith('agent_') || userId.startsWith('admin_')) {
              return userId.replace(/^(agent_|admin_)/, '').replace(/_/g, ' ');
            }
            return userId;
          };
          
          // Helper function to get user role from userId
          const getUserRole = (userId: string): string => {
            if (userId.startsWith('admin_')) {
              return 'Agency Admin';
            }
            return 'Agent';
          };
          
          // Create channel object with resolved member nicknames
          const newChannel = {
            url: channel.channelUrl || `channel_${Date.now()}`,
            name: channel.name || 'New Channel',
            isPublic: channel.isPublic || false,
            isDistinct: channel.isDistinct || false,
            customType: channel.type || 'Active',
            createdAt: Date.now(),
            data: channel.data || JSON.stringify({ description: '' }),
            members: (channel.members || []).map((userId: string) => ({
              userId: userId,
              nickname: getUserNickname(userId),
              metaData: { 
                role: getUserRole(userId),
                agency: 'Demo Insurance Agency'
              },
              plainProfileUrl: 'assets/images/profile.png'
            })),
            memberCount: channel.members?.length || 0,
            unreadMessageCount: 0,
            lastMessage: null,
            isSuper: channel.isSuper || false
          };
          
          // Add to existing channels
          existingChannels.push(newChannel);
          
          // Save back to localStorage
          localStorage.setItem(storageKey, JSON.stringify(existingChannels));
          
          console.log('[DemoDataService] Created channel:', newChannel.url, 'with', newChannel.memberCount, 'members');
          resolve(newChannel);
        } catch (error: any) {
          console.error('[DemoDataService] createSendbirdChannelDemo error:', error);
          // Still resolve with a basic channel object
          resolve({
            url: channel.channelUrl || `channel_${Date.now()}`,
            name: channel.name || 'New Channel',
            isPublic: channel.isPublic || false,
            customType: channel.type || 'Active',
            createdAt: Date.now(),
            members: [],
            memberCount: 0
          });
        }
      }, 300);
    });
  }

  /**
   * Get demo channels for Sendbird SDK
   * Used by SendbirdService.getChannels()
   * Merges static demo channels with user-created channels from localStorage
   */
  getSendbirdChannelsDemo(searchText: string = '', limit: number = 20, type: string = '', status: string[] = []): Promise<any[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          // Get user-created channels from localStorage
          const storageKey = 'demo_channels';
          const userCreatedChannels = JSON.parse(localStorage.getItem(storageKey) || '[]');
          
          const staticChannels = [
              {
                url: 'channel_general_001',
                name: 'General Discussion',
                isPublic: true,
                isDistinct: false,
                customType: 'Active',
                createdAt: Date.now() - 86400000 * 7, // 7 days ago
                members: [
                  { userId: 'demo_user_123', nickname: 'Demo User', metaData: { role: 'PGI-Admin' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_002', nickname: 'User 2', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_003', nickname: 'User 3', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
                ],
                memberCount: 3,
                unreadMessageCount: 2,
                lastMessage: {
                  message: 'Hello everyone!',
                  createdAt: Date.now() - 3600000
                }
              },
              {
                url: 'channel_support_002',
                name: 'Support Channel',
                isPublic: true,
                isDistinct: false,
                customType: 'Active',
                createdAt: Date.now() - 86400000 * 5, // 5 days ago
                members: [
                  { userId: 'demo_user_123', nickname: 'Demo User', metaData: { role: 'PGI-Admin' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_004', nickname: 'User 4', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
                ],
                memberCount: 2,
                unreadMessageCount: 0,
                lastMessage: {
                  message: 'How can I help?',
                  createdAt: Date.now() - 7200000
                }
              },
              {
                url: 'channel_private_003',
                name: 'Private Channel',
                isPublic: false,
                isDistinct: false,
                customType: 'Active',
                createdAt: Date.now() - 86400000 * 3, // 3 days ago
                members: [
                  { userId: 'demo_user_123', nickname: 'Demo User', metaData: { role: 'PGI-Admin' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_005', nickname: 'User 5', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
                ],
                memberCount: 2,
                unreadMessageCount: 1,
                lastMessage: {
                  message: 'Private discussion',
                  createdAt: Date.now() - 1800000
                }
              },
              {
                url: 'channel_announcements_004',
                name: 'Announcements',
                isPublic: true,
                isDistinct: false,
                customType: 'Active',
                createdAt: Date.now() - 86400000 * 10, // 10 days ago
                members: [
                  { userId: 'demo_user_123', nickname: 'Demo User', metaData: { role: 'PGI-Admin' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_002', nickname: 'User 2', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_003', nickname: 'User 3', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_004', nickname: 'User 4', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
                ],
                memberCount: 4,
                unreadMessageCount: 0,
                lastMessage: {
                  message: 'New policy update',
                  createdAt: Date.now() - 86400000
                }
              },
              {
                url: 'channel_archived_005',
                name: 'Archived Project Discussion',
                isPublic: true,
                isDistinct: false,
                customType: 'Archived',
                createdAt: Date.now() - 86400000 * 30, // 30 days ago
                members: [
                  { userId: 'demo_user_123', nickname: 'Demo User', metaData: { role: 'PGI-Admin' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_002', nickname: 'User 2', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
                ],
                memberCount: 2,
                unreadMessageCount: 0,
                lastMessage: {
                  message: 'Project completed',
                  createdAt: Date.now() - 86400000 * 25
                }
              },
              {
                url: 'channel_private_archived_006',
                name: 'Old Private Discussion',
                isPublic: false,
                isDistinct: false,
                customType: 'Archived',
                createdAt: Date.now() - 86400000 * 20, // 20 days ago
                members: [
                  { userId: 'demo_user_123', nickname: 'Demo User', metaData: { role: 'PGI-Admin' }, plainProfileUrl: 'assets/images/profile.png' },
                  { userId: 'user_005', nickname: 'User 5', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
                ],
                memberCount: 2,
                unreadMessageCount: 0,
                lastMessage: {
                  message: 'Discussion closed',
                  createdAt: Date.now() - 86400000 * 18
                }
              }
            ];

            // Merge static channels with user-created channels
            // User-created channels should appear first (newest first)
            const allChannels = [...userCreatedChannels, ...staticChannels];
            
            // Sort by createdAt (newest first)
            allChannels.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

            // Filter by search text
            let filtered = searchText
              ? allChannels.filter(ch => ch.name.toLowerCase().includes(searchText.toLowerCase()))
              : allChannels;

            // Filter by type (only if not 'All')
            if (type && type !== 'All') {
              if (type === 'Public' || type === Type.PUBLIC) {
                filtered = filtered.filter(ch => ch.isPublic === true);
              } else if (type === 'Private' || type === Type.PRIVATE) {
                filtered = filtered.filter(ch => ch.isPublic === false);
              }
            }

            // Filter by status (only if status array is provided and not empty)
            if (status && Array.isArray(status) && status.length > 0) {
              filtered = filtered.filter(ch => {
                const channelStatus = ch.customType || 'Active'; // Default to 'Active' if not set
                return status.includes(channelStatus);
              });
            }

            // Ensure all channels have required properties
            filtered = filtered.map(ch => ({
              ...ch,
              members: ch.members || []
            }));

            resolve(filtered.slice(0, limit));
          } catch (error: any) {
            const errorMessage = error?.message || error?.toString() || 'Unknown error in getSendbirdChannelsDemo';
            console.error('[DemoDataService] getSendbirdChannelsDemo error:', errorMessage, error);
            // Resolve with empty array instead of rejecting to prevent unhandled promise rejection
            resolve([]);
          }
        }, 300);
    });
  }

  /**
   * Get demo channels pending approval for Sendbird SDK
   * Used by SendbirdService.getChannelsPendingApproval()
   */
  getSendbirdChannelsPendingApprovalDemo(limit: number = 20): Promise<any[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
            const pendingChannels = [
              {
                url: 'channel_pending_001',
                name: 'New Channel Request 1',
                isPublic: true,
                isDistinct: false,
                customType: 'Pending',
                createdAt: Date.now() - 86400000, // 1 day ago
                members: [
                  { userId: 'user_006', nickname: 'User 6', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
                ],
                memberCount: 1,
                unreadMessageCount: 0,
                lastMessage: null
              },
              {
                url: 'channel_pending_002',
                name: 'New Channel Request 2',
                isPublic: false,
                isDistinct: false,
                customType: 'Pending',
                createdAt: Date.now() - 43200000, // 12 hours ago
                members: [
                  { userId: 'user_007', nickname: 'User 7', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
                ],
                memberCount: 1,
                unreadMessageCount: 0,
                lastMessage: null
              }
            ];
            // Ensure all channels have required properties
            const safeChannels = pendingChannels.map(ch => ({
              ...ch,
              members: ch.members || []
            }));
            resolve(safeChannels.slice(0, limit));
          } catch (error: any) {
            const errorMessage = error?.message || error?.toString() || 'Unknown error in getSendbirdChannelsPendingApprovalDemo';
            console.error('[DemoDataService] getSendbirdChannelsPendingApprovalDemo error:', errorMessage, error);
            // Resolve with empty array instead of rejecting to prevent unhandled promise rejection
            resolve([]);
          }
        }, 300);
    });
  }

  /**
   * Get demo channel by URL for Sendbird SDK
   * Used by SendbirdService.getChannel()
   */
  getSendbirdChannelDemo(channelUrl: string): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
            // Return a channel matching the URL, or a default one
            // Map channel URLs to specific names for better demo experience
            const channelNames: { [key: string]: string } = {
              'channel_general_001': 'General Discussion',
              'channel_support_002': 'Support Channel',
              'channel_private_003': 'Private Channel',
              'channel_announcements_004': 'Announcements'
            };
            
            const channelName = channelNames[channelUrl] || 'Demo Channel';
            
            const channel = {
              url: channelUrl,
              name: channelName,
              isPublic: true,
              isDistinct: false,
              customType: 'Active',
              createdAt: Date.now() - 86400000,
              members: [
                { userId: 'demo_user_123', nickname: 'Demo User', metaData: { role: 'PGI-Admin' }, plainProfileUrl: 'assets/images/profile.png' },
                { userId: 'user_002', nickname: 'User 2', metaData: { role: 'Agent' }, plainProfileUrl: 'assets/images/profile.png' }
              ],
              memberCount: 2,
              unreadMessageCount: 0,
              lastMessage: {
                message: 'Demo message',
                createdAt: Date.now() - 3600000
              },
              metaData: {},
              // Add data property as JSON string with sentiment and last_updated
              data: JSON.stringify({
                sentiment: 'positive',
                last_updated: new Date().toISOString()
              })
            };
            // Ensure channel has required properties and add methods that components expect
            const safeChannel = {
              ...channel,
              members: channel.members || [],
              // Add methods that Sendbird GroupChannel objects have
              join: async () => {
                // Mock join method - resolves successfully
                return Promise.resolve();
              },
              leave: async () => {
                // Mock leave method - resolves successfully
                return Promise.resolve();
              },
              sendUserMessage: (params: any) => {
                // Mock sendUserMessage method - returns object with onSucceeded/onFailed (Sendbird SDK pattern)
                const messageId = `msg_${Date.now()}`;
                
                // Calculate timestamp that's after all existing messages (both demo and stored)
                let newTimestamp = Date.now();
                try {
                  const storageKey = `demo_messages_${channelUrl}`;
                  const storedMessages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  
                  // Find the latest timestamp from stored messages
                  let latestTimestamp = 0;
                  if (storedMessages.length > 0) {
                    latestTimestamp = Math.max(...storedMessages.map((msg: any) => msg.createdAt || 0));
                  }
                  
                  // Also check demo messages - they use baseTime (1 hour ago) as reference
                  // The newest demo message would be around baseTime - 300000 (5 minutes ago from baseTime)
                  // So we need to ensure new messages are after both stored and demo messages
                  const baseTime = Date.now() - 3600000; // 1 hour ago (same as demo messages base)
                  const newestDemoMessageTime = baseTime - 300000; // Approx 5 minutes before baseTime
                  
                  // Use the maximum of: current time, latest stored message + 1s, newest demo message + 1s
                  // Add a small random offset (0-999ms) to ensure uniqueness even for rapid messages
                  const randomOffset = Math.floor(Math.random() * 1000);
                  newTimestamp = Math.max(
                    Date.now(),
                    latestTimestamp + 1000 + randomOffset,
                    newestDemoMessageTime + 1000 + randomOffset
                  );
                } catch (e) {
                  // If there's an error, just use current time
                  console.warn('[DemoDataService] Failed to calculate timestamp:', e);
                }
                
                const message = {
                  messageId: messageId,
                  message: params.message || '',
                  messageType: 'user',
                  customType: params.customType || '',
                  createdAt: newTimestamp,
                  sender: {
                    userId: 'demo_user_123',
                    nickname: 'Demo User',
                    plainProfileUrl: 'assets/images/profile.png',
                    metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                  },
                  data: params.data || '',
                  mentionedUsers: params.mentionedUserIds || [],
                  mentionedUserIds: params.mentionedUserIds || [],
                  channelUrl: channelUrl,
                  reactions: [],
                  parentMessageId: params.parentMessageId || undefined // Set parentMessageId for replies
                };
                
                // Store message in localStorage for demo persistence
                try {
                  const storageKey = `demo_messages_${channelUrl}`;
                  const existingMessages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  existingMessages.unshift(message); // Add to beginning (newest first)
                  // Keep only last 100 messages per channel to avoid storage bloat
                  const trimmedMessages = existingMessages.slice(0, 100);
                  localStorage.setItem(storageKey, JSON.stringify(trimmedMessages));
                } catch (e) {
                  console.warn('[DemoDataService] Failed to save message to localStorage:', e);
                }
                
                // Return object with onSucceeded and onFailed methods (Sendbird SDK pattern)
                const result: any = {
                  onSucceeded: (callback: (message: any) => void) => {
                    setTimeout(() => callback(message), 100); // Simulate async
                    return result; // Return self for chaining
                  },
                  onFailed: (errorCallback: (error: any) => void) => {
                    // No-op, message always succeeds in demo
                    return result; // Return self for chaining
                  }
                };
                return result;
              },
              sendFileMessage: (params: any) => {
                // Mock sendFileMessage method - returns object with onSucceeded/onFailed (Sendbird SDK pattern)
                const messageId = `msg_file_${Date.now()}`;
                
                // Calculate timestamp that's after all existing messages (both demo and stored)
                let newTimestamp = Date.now();
                try {
                  const storageKey = `demo_messages_${channelUrl}`;
                  const storedMessages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  
                  // Find the latest timestamp from stored messages
                  let latestTimestamp = 0;
                  if (storedMessages.length > 0) {
                    latestTimestamp = Math.max(...storedMessages.map((msg: any) => msg.createdAt || 0));
                  }
                  
                  // Also check demo messages - they use baseTime (1 hour ago) as reference
                  // The newest demo message would be around baseTime - 300000 (5 minutes ago from baseTime)
                  // So we need to ensure new messages are after both stored and demo messages
                  const baseTime = Date.now() - 3600000; // 1 hour ago (same as demo messages base)
                  const newestDemoMessageTime = baseTime - 300000; // Approx 5 minutes before baseTime
                  
                  // Use the maximum of: current time, latest stored message + 1s, newest demo message + 1s
                  // Add a small random offset (0-999ms) to ensure uniqueness even for rapid messages
                  const randomOffset = Math.floor(Math.random() * 1000);
                  newTimestamp = Math.max(
                    Date.now(),
                    latestTimestamp + 1000 + randomOffset,
                    newestDemoMessageTime + 1000 + randomOffset
                  );
                } catch (e) {
                  // If there's an error, just use current time
                  console.warn('[DemoDataService] Failed to calculate timestamp:', e);
                }
                
                // Parse file metadata from data if it's a JSON string
                let fileData: any = {};
                try {
                  if (params.data && typeof params.data === 'string') {
                    fileData = JSON.parse(params.data);
                  } else if (params.data) {
                    fileData = params.data;
                  }
                } catch (e) {
                  // data is not JSON, use as is
                }
                
                // Get file info - prefer fileUrl from params, fallback to data
                const fileUrl = params.fileUrl || params.url || fileData.url || '';
                const fileName = fileData.fileName || params.fileName || 'file';
                const fileType = fileData.fileType || params.mimeType || 'application/octet-stream';
                const fileId = fileData.fileId || '';
                
                // If we have a fileId, try to get the file from storage to ensure URL is valid
                let finalUrl = fileUrl;
                if (fileId && fileId.startsWith('demo_file_')) {
                  const storedFile = this.getFile(fileId);
                  if (storedFile) {
                    finalUrl = storedFile.url;
                  }
                }
                
                const message = {
                  messageId: messageId,
                  message: params.message || '',
                  messageType: 'file',
                  customType: params.customType || fileData.customType || '',
                  createdAt: newTimestamp,
                  sender: {
                    userId: 'demo_user_123',
                    nickname: 'Demo User',
                    plainProfileUrl: 'assets/images/profile.png',
                    metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                  },
                  data: params.data || JSON.stringify({ fileName, fileType, fileId, url: finalUrl }),
                  mentionedUsers: params.mentionedUserIds || [],
                  mentionedUserIds: params.mentionedUserIds || [],
                  channelUrl: channelUrl,
                  reactions: [],
                  parentMessageId: params.parentMessageId || undefined, // Set parentMessageId for replies
                  url: finalUrl,
                  plainUrl: finalUrl, // Some components use plainUrl
                  name: fileName,
                  type: fileType,
                  size: fileData.size || 0
                };
                
                // Store message in localStorage for demo persistence
                try {
                  const storageKey = `demo_messages_${channelUrl}`;
                  const existingMessages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  existingMessages.unshift(message);
                  const trimmedMessages = existingMessages.slice(0, 100);
                  localStorage.setItem(storageKey, JSON.stringify(trimmedMessages));
                } catch (e) {
                  console.warn('[DemoDataService] Failed to save file message to localStorage:', e);
                }
                
                // Return object with onSucceeded and onFailed methods
                const result: any = {
                  onSucceeded: (callback: (message: any) => void) => {
                    setTimeout(() => callback(message), 100);
                    return result; // Return self for chaining
                  },
                  onFailed: (errorCallback: (error: any) => void) => {
                    // No-op, message always succeeds in demo
                    return result; // Return self for chaining
                  }
                };
                return result;
              },
              markAsRead: async () => {
                // Mock markAsRead method
                return Promise.resolve();
              },
              getTypingUsers: () => {
                // Mock getTypingUsers method - returns empty array
                return [];
              },
              startTyping: () => {
                // Mock startTyping method - no-op for demo
                return;
              },
              endTyping: () => {
                // Mock endTyping method - no-op for demo
                return;
              },
              addReaction: async (message: any, reaction: string) => {
                // Mock addReaction method - updates message reactions in localStorage and message object
                try {
                  // Initialize reactions array if it doesn't exist
                  if (!message.reactions) {
                    message.reactions = [];
                  }
                  
                  // Check if reaction already exists
                  let existingReaction = message.reactions.find((r: any) => r.key === reaction);
                  if (!existingReaction) {
                    // Create new reaction object with proper format
                    existingReaction = {
                      key: reaction,
                      userIds: ['demo_user_123'],
                      updatedAt: Date.now(),
                      count: 1,
                      hasCurrentUserReacted: true,
                      sampledUserInfoList: [{
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png'
                      }]
                    };
                    message.reactions.push(existingReaction);
                  } else {
                    // Add user to reaction if not already there
                    if (!existingReaction.userIds.includes('demo_user_123')) {
                      existingReaction.userIds.push('demo_user_123');
                      existingReaction.count = existingReaction.userIds.length;
                      existingReaction.hasCurrentUserReacted = true;
                      if (!existingReaction.sampledUserInfoList) {
                        existingReaction.sampledUserInfoList = [];
                      }
                      existingReaction.sampledUserInfoList.push({
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png'
                      });
                    }
                  }
                  
                  // Update message reactions in localStorage - ensure message is saved even if it's a demo message
                  const storageKey = `demo_messages_${channelUrl}`;
                  const messages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  const messageIndex = messages.findIndex((msg: any) => msg.messageId === message.messageId);
                  if (messageIndex !== -1) {
                    // Update existing message with reactions (deep copy to avoid reference issues)
                    messages[messageIndex].reactions = JSON.parse(JSON.stringify(message.reactions));
                    localStorage.setItem(storageKey, JSON.stringify(messages));
                  } else {
                    // Message not in localStorage (it's a demo message), add it with reactions
                    const messageCopy = JSON.parse(JSON.stringify(message));
                    messageCopy.reactions = JSON.parse(JSON.stringify(message.reactions)); // Deep copy
                    messages.unshift(messageCopy);
                    const trimmedMessages = messages.slice(0, 100);
                    localStorage.setItem(storageKey, JSON.stringify(trimmedMessages));
                  }
                  
                  return Promise.resolve();
                } catch (e) {
                  console.warn('[DemoDataService] Failed to add reaction:', e);
                  return Promise.resolve();
                }
              },
              deleteReaction: async (message: any, reaction: string) => {
                // Mock deleteReaction method - removes reaction from message in localStorage and message object
                try {
                  if (!message.reactions) {
                    return Promise.resolve();
                  }
                  
                  const reactionIndex = message.reactions.findIndex((r: any) => r.key === reaction);
                  if (reactionIndex !== -1) {
                    const reactionObj = message.reactions[reactionIndex];
                    const userIdIndex = reactionObj.userIds ? reactionObj.userIds.indexOf('demo_user_123') : -1;
                    if (userIdIndex !== -1) {
                      reactionObj.userIds.splice(userIdIndex, 1);
                      reactionObj.count = reactionObj.userIds.length;
                      reactionObj.hasCurrentUserReacted = false;
                      
                      // Remove user from sampled list
                      if (reactionObj.sampledUserInfoList) {
                        const userInfoIndex = reactionObj.sampledUserInfoList.findIndex((u: any) => u.userId === 'demo_user_123');
                        if (userInfoIndex !== -1) {
                          reactionObj.sampledUserInfoList.splice(userInfoIndex, 1);
                        }
                      }
                      
                      // If no users left, remove the reaction
                      if (reactionObj.userIds.length === 0) {
                        message.reactions.splice(reactionIndex, 1);
                      }
                    }
                  }
                  
                  // Update message reactions in localStorage - also check demo messages
                  const storageKey = `demo_messages_${channelUrl}`;
                  const messages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  const messageIndex = messages.findIndex((msg: any) => msg.messageId === message.messageId);
                  if (messageIndex !== -1) {
                    // Update the stored message with the new reactions
                    messages[messageIndex].reactions = JSON.parse(JSON.stringify(message.reactions)); // Deep copy
                    localStorage.setItem(storageKey, JSON.stringify(messages));
                  } else {
                    // Message not in stored messages, add it with reactions
                    const messageCopy = JSON.parse(JSON.stringify(message));
                    messageCopy.reactions = JSON.parse(JSON.stringify(message.reactions)); // Deep copy
                    messages.unshift(messageCopy);
                    const trimmedMessages = messages.slice(0, 100);
                    localStorage.setItem(storageKey, JSON.stringify(trimmedMessages));
                  }
                  
                  return Promise.resolve();
                } catch (e) {
                  console.warn('[DemoDataService] Failed to delete reaction:', e);
                  return Promise.resolve();
                }
              },
              updateUserMessage: async (messageId: string, params: any) => {
                // Mock updateUserMessage method - updates message in localStorage
                try {
                  const storageKey = `demo_messages_${channelUrl}`;
                  const messages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  const messageIndex = messages.findIndex((msg: any) => msg.messageId === messageId);
                  
                  if (messageIndex !== -1) {
                    // Update message text
                    messages[messageIndex].message = params.message || messages[messageIndex].message;
                    messages[messageIndex].updatedAt = Date.now();
                    messages[messageIndex].mentionedUserIds = params.mentionedUserIds || messages[messageIndex].mentionedUserIds || [];
                    messages[messageIndex].mentionedUsers = params.mentionedUserIds || messages[messageIndex].mentionedUsers || [];
                    
                    localStorage.setItem(storageKey, JSON.stringify(messages));
                    
                    // Return updated message
                    return Promise.resolve(messages[messageIndex]);
                  } else {
                    // Message not found in localStorage, return a mock updated message
                    return Promise.resolve({
                      messageId: messageId,
                      message: params.message,
                      updatedAt: Date.now(),
                      mentionedUserIds: params.mentionedUserIds || []
                    });
                  }
                } catch (e) {
                  console.warn('[DemoDataService] Failed to update message:', e);
                  return Promise.reject(e);
                }
              },
              deleteMessage: async (message: any) => {
                // Mock deleteMessage method - removes message from localStorage
                try {
                  const storageKey = `demo_messages_${channelUrl}`;
                  const messages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  const messageIndex = messages.findIndex((msg: any) => msg.messageId === message.messageId);
                  
                  if (messageIndex !== -1) {
                    messages.splice(messageIndex, 1);
                    localStorage.setItem(storageKey, JSON.stringify(messages));
                  }
                  
                  return Promise.resolve();
                } catch (e) {
                  console.warn('[DemoDataService] Failed to delete message:', e);
                  return Promise.reject(e);
                }
              },
              createPreviousMessageListQuery: () => {
                // Mock createPreviousMessageListQuery method
                return {
                  limit: 30,
                  reverse: false,
                  load: async () => {
                    return [];
                  }
                };
              },
              createMessageSearchQuery: (params: any) => {
                // Mock createMessageSearchQuery method
                return {
                  next: async () => {
                    return [];
                  },
                  hasNext: false
                };
              },
              getMessagesByTimestamp: async (lastTimestamp: number, params: any) => {
                // Mock getMessagesByTimestamp method - returns different demo messages per channel
                // Use a fixed base time for demo messages to ensure consistency
                // Base time is set to 1 hour ago from current time, so demo messages are in the past
                // and new user messages will always appear after them
                const baseTime = Date.now() - 3600000; // 1 hour ago
                const now = baseTime;
                const currentChannelUrl = channelUrl; // Capture channelUrl from closure
                
                // Define different message sets for different channels
                // Use static message IDs (not based on timestamps) to ensure they don't change on refresh
                const channelMessages: { [key: string]: any[] } = {
                  'channel_general_001': [
                    {
                      messageId: 'msg_gen_001',
                      message: 'Hello everyone! Welcome to the General Discussion channel. Feel free to share ideas and collaborate here.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 7200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_002',
                      message: 'Thanks for the welcome! Looking forward to collaborating with everyone.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6900000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_003',
                      message: 'Has anyone reviewed the latest policy updates? I have some questions about the new guidelines.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6600000,
                      sender: {
                        userId: 'user_003',
                        nickname: 'User 3',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_004',
                      message: 'Yes, I have. The new guidelines are quite comprehensive. @Demo User, what are your thoughts on the implementation timeline?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6300000,
                      sender: {
                        userId: 'user_004',
                        nickname: 'User 4',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: ['demo_user_123'],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_005',
                      message: 'I agree. The updates address several concerns we had. Let\'s schedule a meeting to discuss implementation details.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_006',
                      message: 'Sounds good! I\'ll send out a calendar invite for next week.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5700000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_007',
                      message: 'I\'ve been working on a new proposal for streamlining our workflow. Should I share it here?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5400000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_008',
                      message: 'That would be great! Please share it when you\'re ready.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5100000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_009',
                      message: 'Has anyone tried the new reporting feature? I found it really helpful for tracking metrics.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 4800000,
                      sender: {
                        userId: 'user_003',
                        nickname: 'User 3',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_010',
                      message: 'Yes! It\'s a game changer. The visualization tools are excellent.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 4500000,
                      sender: {
                        userId: 'user_004',
                        nickname: 'User 4',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_011',
                      message: 'I\'m still getting familiar with it. Any tips for getting started?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 4200000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_012',
                      message: 'I can help you with that! Let\'s schedule a quick training session.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3900000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_013',
                      message: 'That would be amazing! Thank you so much.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3600000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_014',
                      message: 'Just a reminder: the deadline for Q1 reports is next Friday. Please submit on time.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_015',
                      message: 'Thanks for the reminder! I\'m almost done with mine.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 1800000,
                      sender: {
                        userId: 'user_003',
                        nickname: 'User 3',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    }
                  ],
                  'channel_support_002': [
                    {
                      messageId: 'msg_sup_002',
                      message: 'Welcome to the Support Channel. How can we assist you today?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 14400000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_003',
                      message: 'I\'m having trouble accessing the dashboard. Getting a 404 error.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 14100000,
                      sender: {
                        userId: 'user_004',
                        nickname: 'User 4',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_004',
                      message: 'Can you try clearing your browser cache and logging in again?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 13800000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_005',
                      message: 'That worked! Thank you so much for the quick help.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 13500000,
                      sender: {
                        userId: 'user_004',
                        nickname: 'User 4',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_006',
                      message: 'You\'re welcome! If you encounter any other issues, feel free to ask.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 13200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_007',
                      message: 'Hi, I\'m unable to upload documents. The upload button seems to be disabled.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 10800000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_008',
                      message: 'Let me check that for you. Can you tell me what browser you\'re using?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 10500000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_009',
                      message: 'I\'m using Chrome version 120. The file size is under 5MB, so that shouldn\'t be the issue.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 10200000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_010',
                      message: 'I see the issue. Please try refreshing the page and make sure you\'re logged in. Sometimes the session expires.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 9900000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_011',
                      message: 'Perfect! That fixed it. Thanks for your help.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 9600000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_012',
                      message: 'Hello, I\'m getting an error when trying to generate reports. Error code: ERR_500.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 7200000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_013',
                      message: 'I\'m looking into this issue. Can you try again in a few minutes? We\'re experiencing some server maintenance.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6900000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_014',
                      message: 'Will do. I\'ll check back in 10 minutes. Thanks!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6600000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_015',
                      message: 'Update: The server maintenance is complete. Reports should be working now. Please try again.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    }
                  ],
                  'channel_private_003': [
                    {
                      messageId: 'msg_priv_002',
                      message: 'This is a private conversation. All messages here are confidential.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 7200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_003',
                      message: 'Understood. I have some sensitive information to discuss.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6900000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_004',
                      message: 'Please proceed. This channel is secure and only visible to us.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_005',
                      message: 'I need to discuss a potential security concern I noticed in the system.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6300000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_006',
                      message: 'Please provide details. I\'ll investigate immediately.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_007',
                      message: 'I noticed some unusual activity in the access logs. Multiple failed login attempts from an unknown IP.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5700000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_008',
                      message: 'Thank you for reporting this. I\'ve blocked that IP address and will monitor for any further suspicious activity.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5400000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_009',
                      message: 'Good to know. I\'ll keep an eye out for anything else unusual.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5100000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_010',
                      message: 'Also, I wanted to discuss the upcoming client meeting. We need to prepare the presentation materials.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_011',
                      message: 'I\'ve already started working on it. I\'ll have a draft ready by tomorrow for your review.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3300000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_012',
                      message: 'Perfect. Let me know if you need any additional resources or information.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 1800000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    }
                  ],
                  'channel_announcements_004': [
                    {
                      messageId: 'msg_ann_002',
                      message: '📢 Important Announcement: New policy updates are now live!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 172800000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_003',
                      message: 'All agents are required to review the updated guidelines by end of week.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 169200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_004',
                      message: 'Q1 reports deadline: Next Friday, 5 PM EST. Please submit on time.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 165600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_005',
                      message: 'Training session scheduled for next Monday at 2 PM. Attendance is mandatory.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 162000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_006',
                      message: '🎉 System Maintenance Complete: All services are now running normally. Thank you for your patience.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 129600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_007',
                      message: '📋 Reminder: Monthly team meeting is scheduled for this Thursday at 3 PM EST. Please mark your calendars.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 86400000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_008',
                      message: '✨ New Feature Release: Enhanced reporting dashboard is now available. Check out the new analytics tools!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 82800000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_009',
                      message: '🔒 Security Update: Please ensure your passwords are updated regularly. Two-factor authentication is now available.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 79200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_010',
                      message: '📊 Performance Review: Q1 metrics are now available in the dashboard. Great work everyone!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 75600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_011',
                      message: '🎓 Professional Development: New training materials have been added to the resources section. Check them out!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 72000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_012',
                      message: '⏰ Final Reminder: Q1 reports are due tomorrow at 5 PM EST. Please ensure all submissions are complete.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 36000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    }
                  ]
                };
                
                // Get messages for the specific channel, or default messages if channel not found
                let demoMessages = channelMessages[currentChannelUrl] || [
                  {
                    messageId: 'msg_def_002',
                    message: 'Welcome to this channel!',
                    messageType: 'user',
                    customType: '',
                    createdAt: now - 3600000,
                    sender: {
                      userId: 'demo_user_123',
                      nickname: 'Demo User',
                      plainProfileUrl: 'assets/images/profile.png',
                      metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                    },
                    data: '',
                    mentionedUsers: [],
                    channelUrl: currentChannelUrl,
                    reactions: []
                  }
                ];
                
                // Merge with messages from localStorage (user-sent messages in demo)
                try {
                  const storageKey = `demo_messages_${currentChannelUrl}`;
                  const storedMessages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  if (storedMessages.length > 0) {
                    // Combine demo messages with stored messages, sort by timestamp (newest first)
                    const allMessages = [...storedMessages, ...demoMessages];
                    allMessages.sort((a, b) => b.createdAt - a.createdAt);
                    demoMessages = allMessages;
                  }
                } catch (e) {
                  console.warn('[DemoDataService] Failed to load messages from localStorage:', e);
                }
                
                // Filter messages based on lastTimestamp if provided
                if (lastTimestamp) {
                  return demoMessages.filter(msg => msg.createdAt < lastTimestamp).slice(0, params?.prevResultSize || 30);
                }
                
                // Return messages up to the limit
                return demoMessages.slice(0, params?.prevResultSize || 30);
              },
              getMessages: async (params: any) => {
                // Mock getMessages method - returns channel-specific demo messages
                // Use a fixed base time for demo messages to ensure consistency
                // Base time is set to 1 hour ago from current time, so demo messages are in the past
                // and new user messages will always appear after them
                const baseTime = Date.now() - 3600000; // 1 hour ago
                const now = baseTime;
                const currentChannelUrl = channelUrl;
                
                // Reuse the same channel messages logic from getMessagesByTimestamp
                const channelMessages: { [key: string]: any[] } = {
                  'channel_general_001': [
                    {
                      messageId: 'msg_gen_002',
                      message: 'Hello everyone! Welcome to the General Discussion channel. Feel free to share ideas and collaborate here.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 7200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_002',
                      message: 'Thanks for the welcome! Looking forward to collaborating with everyone.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6900000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_003',
                      message: 'Has anyone reviewed the latest policy updates? I have some questions about the new guidelines.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6600000,
                      sender: {
                        userId: 'user_003',
                        nickname: 'User 3',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_004',
                      message: 'Yes, I have. The new guidelines are quite comprehensive. @Demo User, what are your thoughts on the implementation timeline?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6300000,
                      sender: {
                        userId: 'user_004',
                        nickname: 'User 4',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: ['demo_user_123'],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_005',
                      message: 'I agree. The updates address several concerns we had. Let\'s schedule a meeting to discuss implementation details.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_006',
                      message: 'Sounds good! I\'ll send out a calendar invite for next week.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5700000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_007',
                      message: 'I\'ve been working on a new proposal for streamlining our workflow. Should I share it here?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5400000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_008',
                      message: 'That would be great! Please share it when you\'re ready.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5100000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_009',
                      message: 'Has anyone tried the new reporting feature? I found it really helpful for tracking metrics.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 4800000,
                      sender: {
                        userId: 'user_003',
                        nickname: 'User 3',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_010',
                      message: 'Yes! It\'s a game changer. The visualization tools are excellent.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 4500000,
                      sender: {
                        userId: 'user_004',
                        nickname: 'User 4',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_011',
                      message: 'I\'m still getting familiar with it. Any tips for getting started?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 4200000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_012',
                      message: 'I can help you with that! Let\'s schedule a quick training session.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3900000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_013',
                      message: 'That would be amazing! Thank you so much.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3600000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_014',
                      message: 'Just a reminder: the deadline for Q1 reports is next Friday. Please submit on time.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_gen_015',
                      message: 'Thanks for the reminder! I\'m almost done with mine.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 1800000,
                      sender: {
                        userId: 'user_003',
                        nickname: 'User 3',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    }
                  ],
                  'channel_support_002': [
                    {
                      messageId: 'msg_sup_016',
                      message: 'Welcome to the Support Channel. How can we assist you today?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 14400000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_017',
                      message: 'I\'m having trouble accessing the dashboard. Getting a 404 error.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 14100000,
                      sender: {
                        userId: 'user_004',
                        nickname: 'User 4',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_018',
                      message: 'Can you try clearing your browser cache and logging in again?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 13800000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_019',
                      message: 'That worked! Thank you so much for the quick help.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 13500000,
                      sender: {
                        userId: 'user_004',
                        nickname: 'User 4',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_020',
                      message: 'You\'re welcome! If you encounter any other issues, feel free to ask.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 13200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_021',
                      message: 'Hi, I\'m unable to upload documents. The upload button seems to be disabled.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 10800000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_022',
                      message: 'Let me check that for you. Can you tell me what browser you\'re using?',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 10500000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_023',
                      message: 'I\'m using Chrome version 120. The file size is under 5MB, so that shouldn\'t be the issue.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 10200000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_024',
                      message: 'I see the issue. Please try refreshing the page and make sure you\'re logged in. Sometimes the session expires.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 9900000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_025',
                      message: 'Perfect! That fixed it. Thanks for your help.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 9600000,
                      sender: {
                        userId: 'user_002',
                        nickname: 'User 2',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_026',
                      message: 'Hello, I\'m getting an error when trying to generate reports. Error code: ERR_500.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 7200000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_027',
                      message: 'I\'m looking into this issue. Can you try again in a few minutes? We\'re experiencing some server maintenance.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6900000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_028',
                      message: 'Will do. I\'ll check back in 10 minutes. Thanks!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6600000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_sup_001',
                      message: 'Update: The server maintenance is complete. Reports should be working now. Please try again.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    }
                  ],
                  'channel_private_003': [
                    {
                      messageId: 'msg_priv_013',
                      message: 'This is a private conversation. All messages here are confidential.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 7200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_014',
                      message: 'Understood. I have some sensitive information to discuss.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6900000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_015',
                      message: 'Please proceed. This channel is secure and only visible to us.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_016',
                      message: 'I need to discuss a potential security concern I noticed in the system.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6300000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_017',
                      message: 'Please provide details. I\'ll investigate immediately.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 6000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_018',
                      message: 'I noticed some unusual activity in the access logs. Multiple failed login attempts from an unknown IP.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5700000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_019',
                      message: 'Thank you for reporting this. I\'ve blocked that IP address and will monitor for any further suspicious activity.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5400000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_020',
                      message: 'Good to know. I\'ll keep an eye out for anything else unusual.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 5100000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_021',
                      message: 'Also, I wanted to discuss the upcoming client meeting. We need to prepare the presentation materials.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_022',
                      message: 'I\'ve already started working on it. I\'ll have a draft ready by tomorrow for your review.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 3300000,
                      sender: {
                        userId: 'user_005',
                        nickname: 'User 5',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'Agent', agency: 'Demo Insurance Agency' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_priv_023',
                      message: 'Perfect. Let me know if you need any additional resources or information.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 1800000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    }
                  ],
                  'channel_announcements_004': [
                    {
                      messageId: 'msg_ann_013',
                      message: '📢 Important Announcement: New policy updates are now live!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 172800000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_014',
                      message: 'All agents are required to review the updated guidelines by end of week.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 169200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_015',
                      message: 'Q1 reports deadline: Next Friday, 5 PM EST. Please submit on time.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 165600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_016',
                      message: 'Training session scheduled for next Monday at 2 PM. Attendance is mandatory.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 162000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_017',
                      message: '🎉 System Maintenance Complete: All services are now running normally. Thank you for your patience.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 129600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_018',
                      message: '📋 Reminder: Monthly team meeting is scheduled for this Thursday at 3 PM EST. Please mark your calendars.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 86400000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_019',
                      message: '✨ New Feature Release: Enhanced reporting dashboard is now available. Check out the new analytics tools!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 82800000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_020',
                      message: '🔒 Security Update: Please ensure your passwords are updated regularly. Two-factor authentication is now available.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 79200000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_021',
                      message: '📊 Performance Review: Q1 metrics are now available in the dashboard. Great work everyone!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 75600000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_022',
                      message: '🎓 Professional Development: New training materials have been added to the resources section. Check them out!',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 72000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    },
                    {
                      messageId: 'msg_ann_023',
                      message: '⏰ Final Reminder: Q1 reports are due tomorrow at 5 PM EST. Please ensure all submissions are complete.',
                      messageType: 'user',
                      customType: '',
                      createdAt: now - 36000000,
                      sender: {
                        userId: 'demo_user_123',
                        nickname: 'Demo User',
                        plainProfileUrl: 'assets/images/profile.png',
                        metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                      },
                      data: '',
                      mentionedUsers: [],
                      channelUrl: currentChannelUrl,
                      reactions: []
                    }
                  ]
                };
                
                // Get messages for the specific channel, or default messages if channel not found
                let demoMessages = channelMessages[currentChannelUrl] || [
                  {
                    messageId: 'msg_def_003',
                    message: 'Welcome to this channel!',
                    messageType: 'user',
                    customType: '',
                    createdAt: now - 3600000,
                    sender: {
                      userId: 'demo_user_123',
                      nickname: 'Demo User',
                      plainProfileUrl: 'assets/images/profile.png',
                      metaData: { role: 'PGI-Admin', agency: 'PGI Corporate' }
                    },
                    data: '',
                    mentionedUsers: [],
                    channelUrl: currentChannelUrl,
                    reactions: []
                  }
                ];
                
                // Merge with messages from localStorage (user-sent messages and reactions in demo)
                try {
                  const storageKey = `demo_messages_${currentChannelUrl}`;
                  const storedMessages = JSON.parse(localStorage.getItem(storageKey) || '[]');
                  if (storedMessages.length > 0) {
                    // Create a map of stored messages by messageId for quick lookup
                    const storedMessagesMap = new Map();
                    storedMessages.forEach((msg: any) => {
                      storedMessagesMap.set(msg.messageId, msg);
                    });
                    
                    // Merge: use stored messages (with reactions) if they exist, otherwise use demo messages
                    const mergedMessages: any[] = [];
                    const processedMessageIds = new Set<string>();
                    
                    // First, add all stored messages (these have reactions and user-sent messages)
                    storedMessages.forEach((msg: any) => {
                      mergedMessages.push(msg);
                      processedMessageIds.add(msg.messageId);
                    });
                    
                    // Then, add demo messages that don't exist in stored messages
                    demoMessages.forEach((demoMsg: any) => {
                      if (!processedMessageIds.has(demoMsg.messageId)) {
                        mergedMessages.push(demoMsg);
                      } else {
                        // If demo message exists in stored, merge reactions from stored into demo
                        const storedMsg = storedMessagesMap.get(demoMsg.messageId);
                        if (storedMsg && storedMsg.reactions && storedMsg.reactions.length > 0) {
                          // Update the stored message with demo message data, but keep reactions
                          const index = mergedMessages.findIndex(m => m.messageId === demoMsg.messageId);
                          if (index !== -1) {
                            mergedMessages[index] = { ...mergedMessages[index], ...demoMsg, reactions: mergedMessages[index].reactions };
                          }
                        }
                      }
                    });
                    
                    // Sort by timestamp (newest first)
                    mergedMessages.sort((a, b) => b.createdAt - a.createdAt);
                    demoMessages = mergedMessages;
                  }
                } catch (e) {
                  console.warn('[DemoDataService] Failed to load messages from localStorage:', e);
                }
                
                return demoMessages.slice(0, params?.prevResultSize || 30);
              }
            };
            resolve(safeChannel);
          } catch (error: any) {
            const errorMessage = error?.message || error?.toString() || 'Unknown error in getSendbirdChannelDemo';
            console.error('[DemoDataService] getSendbirdChannelDemo error:', errorMessage, error);
            // Resolve with null instead of rejecting to prevent unhandled promise rejection
            resolve(null);
          }
        }, 300);
    });
  }

  // ============================================================================
  // FILE UPLOADS
  // ============================================================================

  /**
   * POST /user/image/upload/:userId
   * Upload user image
   */
  uploadUserImage(userId: string, file: any): Observable<any> {
    return of({
      status: true,
      results: {
        imageUrl: 'assets/images/profile.png',
        message: 'Image uploaded successfully'
      }
    }).pipe(delay(500));
  }

  /**
   * POST /carplate/file/create
   * Upload carplate file
   */
  uploadCarplateFile(file: any): Observable<any> {
    return of({
      status: true,
      results: {
        fileUrl: 'assets/images/demo-carplate.jpg',
        message: 'File uploaded successfully'
      }
    }).pipe(delay(500));
  }

  /**
   * Download file (blob)
   */
  downloadFile(url: string): Observable<Blob> {
    // Return empty blob for demo
    const blob = new Blob(['Demo file content'], { type: 'application/octet-stream' });
    return of(blob).pipe(delay(300));
  }

  // ============================================================================
  // HELPER METHODS - Generate demo data
  // ============================================================================

  private generateOpportunitiesBreakdown(dimension: string): any[] {
    return [
      { dimension, value: 45, label: 'Category 1' },
      { dimension, value: 35, label: 'Category 2' },
      { dimension, value: 25, label: 'Category 3' },
      { dimension, value: 20, label: 'Category 4' },
      { dimension, value: 15, label: 'Category 5' }
    ];
  }

  private generateOpportunitiesDetails(type: string, dimension: string, filters?: any): any[] {
    const details = [];
    for (let i = 1; i <= 50; i++) {
      details.push({
        PolicyNumber: `POL-${i}`,
        EzLynxPolicyURL: `https://example.com/policy/${i}`,
        InsuredName: `Customer ${i}`,
        PrimaryPhone: `555-${String(i).padStart(4, '0')}`,
        EmailAddress: `customer${i}@example.com`,
        RatingStateCode: ['CA', 'TX', 'FL', 'NY'][i % 4],
        CarrierName: ['Allied', 'State Farm', 'Progressive', 'Geico'][i % 4],
        WrittenPremium: (50000 + Math.random() * 100000).toFixed(2),
        Term: '12 months',
        PolicyIssuedDate: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
        ExpirationDate: new Date(Date.now() + (365 - i) * 86400000).toISOString().split('T')[0],
        AssignedProducer: ['John Smith', 'Jane Doe', 'Bob Johnson'][i % 3],
        EstimatedDiscount: (Math.random() * 5000).toFixed(2),
        LOB: ['Homeowners', 'Auto', 'Commercial'][i % 3]
      });
    }
    return details;
  }

  private generatePoliciesDetails(dataType: string, dimension: string, selected: any): any[] {
    const details = [];
    for (let i = 1; i <= 100; i++) {
      details.push({
        PolicyNumber: `POL-${i}`,
        EzLynxPolicyURL: `https://example.com/policy/${i}`,
        InsuredName: `Customer ${i}`,
        PrimaryPhone: `555-${String(i).padStart(4, '0')}`,
        EmailAddress: `customer${i}@example.com`,
        RatingStateCode: ['CA', 'TX', 'FL', 'NY'][i % 4],
        CarrierName: ['Allied', 'State Farm', 'Progressive', 'Geico'][i % 4],
        WrittenPremium: (50000 + Math.random() * 100000).toFixed(2),
        Term: '12 months',
        PolicyIssuedDate: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
        ExpirationDate: new Date(Date.now() + (365 - i) * 86400000).toISOString().split('T')[0],
        AssignedProducer: ['John Smith', 'Jane Doe', 'Bob Johnson'][i % 3],
        City: ['Los Angeles', 'Houston', 'Miami', 'New York'][i % 4],
        LOB: ['Homeowners', 'Auto', 'Commercial'][i % 3],
        TransactionType: ['New', 'Renewal', 'Cancellation'][i % 3]
      });
    }
    return details;
  }

  private generateProductionDetails(postData: any): any[] {
    const details = [];
    const type = postData?.type || 'home';
    const characteristics = postData?.characteristics || '';
    const dimension = postData?.dimension || '';
    
    // Generate 75 records
    for (let i = 1; i <= 75; i++) {
      const baseDate = new Date(Date.now() - i * 86400000);
      const expiryDate = new Date(Date.now() + (365 - i) * 86400000);
      
      // Format dates as YYYYMMDD (expected by component)
      const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
      };
      
      const baseRecord: any = {
        PolicyNumber: `POL-${String(i).padStart(6, '0')}`,
        EzLynxPolicyURL: `https://example.com/policy/${i}`,
        InsuredName: `Customer ${i}`,
        PrimaryPhone: `555-${String(i).padStart(4, '0')}`,
        EmailAddress: `customer${i}@example.com`,
        RatingStateCode: ['CA', 'TX', 'FL', 'NY', 'IL', 'PA'][i % 6],
        CarrierName: ['Allied', 'State Farm', 'Progressive', 'Geico', 'Farmers', 'Travelers'][i % 6],
        WrittenPremium: (50000 + Math.random() * 100000).toFixed(2),
        Term: '12 months',
        PolicyIssuedDate: formatDate(baseDate),
        ExpirationDate: formatDate(expiryDate),
        AssignedProducer: ['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown'][i % 5],
        InsuredAddress: `${i} Main St, City, ${['CA', 'TX', 'FL', 'NY'][i % 4]} ${String(i % 10000).padStart(5, '0')}`
      };
      
      // Add type-specific fields
      if (type === 'home') {
        switch (characteristics) {
          case 'price-range':
            baseRecord.HomeValue = (200000 + Math.random() * 500000).toFixed(2);
            break;
          case 'year-built':
            baseRecord.HomeYearBuilt = String(1950 + (i % 75)); // Years from 1950 to 2024
            break;
          case 'coverage-profile':
            baseRecord.CoverageALimit = (100000 + Math.random() * 400000).toFixed(2);
            baseRecord.CoverageB = (20000 + Math.random() * 50000).toFixed(2);
            baseRecord.CoverageC = (50000 + Math.random() * 100000).toFixed(2);
            baseRecord.ReplacementCost = (150000 + Math.random() * 350000).toFixed(2);
            break;
          case 'fire-protection':
            baseRecord.FireProtectionClass = String(1 + (i % 10)); // Classes 1-10
            break;
          case 'roof-age':
            baseRecord.RoofAge = String(1 + (i % 30)); // Ages 1-30 years
            break;
          case 'construction-type':
            const constructionTypes = ['Frame', 'Brick', 'Stucco', 'Concrete', 'Steel'];
            baseRecord.RoofType = constructionTypes[i % constructionTypes.length];
            break;
        }
      } else if (type === 'auto') {
        switch (characteristics) {
          case 'annual-mileage':
            baseRecord.InsuredVehicle = `${['Toyota', 'Honda', 'Ford', 'Chevrolet', 'Nissan'][i % 5]} ${['Camry', 'Accord', 'F-150', 'Silverado', 'Altima'][i % 5]} ${2020 + (i % 5)}`;
            baseRecord.EstimatedAnnualMileage = String(5000 + (i % 20000)); // 5k to 25k miles
            break;
          case 'garaging-mailing':
            baseRecord.GaragingAddress = `${i} Main St, City, ${['CA', 'TX', 'FL', 'NY'][i % 4]} ${String(i % 10000).padStart(5, '0')}`;
            baseRecord.MailingAddress = `${i + 100} Oak Ave, City, ${['CA', 'TX', 'FL', 'NY'][(i + 1) % 4]} ${String((i + 100) % 10000).padStart(5, '0')}`;
            break;
          case 'married':
            const maritalStatuses = ['Married', 'Single', 'Divorced', 'Widowed'];
            baseRecord.MaritalStatusCd = maritalStatuses[i % maritalStatuses.length];
            break;
          case 'rejected':
            const coverages = ['UM/UIM', 'Collision', 'Comprehensive', 'Liability'];
            baseRecord.UMUIMCoverage = coverages[i % coverages.length];
            break;
          case 'minimum-state':
            const limits = ['15/30/5', '25/50/25', '30/60/15', '50/100/50', '100/300/100'];
            baseRecord.MinimumStateLimits = limits[i % limits.length];
            break;
          case 'insurance':
            const insurers = ['State Farm', 'Allstate', 'Progressive', 'Geico', 'Farmers'];
            baseRecord.PriorInsurerName = insurers[i % insurers.length];
            break;
          case 'driver-education':
            const educationLevels = ['High School', 'Some College', 'Bachelor', 'Master', 'Doctorate'];
            baseRecord.Education = educationLevels[i % educationLevels.length];
            break;
          case 'occupation':
            const occupations = ['Engineer', 'Teacher', 'Doctor', 'Lawyer', 'Nurse', 'Manager', 'Sales', 'Driver'];
            baseRecord.Occupation = occupations[i % occupations.length];
            break;
          case 'liability-limits':
            const liabilityLimits = ['15/30/5', '25/50/25', '30/60/15', '50/100/50', '100/300/100', '250/500/100'];
            baseRecord.LiabilityLimits = liabilityLimits[i % liabilityLimits.length];
            break;
          case 'length-prior-insurance':
            // No additional fields needed for this type
            break;
          case 'dial_quick_endorsement':
            const endorsementDate = new Date(Date.now() - (i % 90) * 86400000);
            baseRecord.EndorsementDate = formatDate(endorsementDate);
            const endorsementTypes = ['Change Address', 'Add Vehicle', 'Remove Vehicle', 'Change Coverage', 'Change Driver'];
            baseRecord.EndorsementType = endorsementTypes[i % endorsementTypes.length];
            baseRecord.PremiumChange = ((Math.random() - 0.5) * 500).toFixed(2); // -250 to +250
            break;
        }
      }
      
      details.push(baseRecord);
    }
    return details;
  }

  /**
   * Get demo user object
   */
  getDemoUser(): any {
    return { ...this.DEMO_USER };
  }

  /**
   * Get demo OTP code (for testing)
   */
  getDemoOtpCode(): string {
    return this.DEMO_OTP_CODE;
  }

  /**
   * Get demo SMS code (for testing)
   */
  getDemoSmsCode(): string {
    return this.DEMO_SMS_CODE;
  }

  /**
   * POST /mondayContactsList
   * Get all contacts list (agents, agencies, admins)
   * Returns data directly without status wrapper for this endpoint
   */
  getAllContactList(userType: string = 'agency', email: string = ''): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Return data directly (not wrapped in { status: true, results: {...} })
        // This matches what the component expects
        resolve({
          agents: [
            {
              ContactName: 'John Smith',
              Agency: 'Demo Insurance Agency',
              MainAgencyEmail: 'contact@demoagency.com',
              Role: 'Agent',
              BusinessEmail: 'john.smith.business@demoagency.com',
              PersonalEmail: 'john.smith@example.com',
              Type: 'Agent'
            },
            {
              ContactName: 'Jane Doe',
              Agency: 'Demo Insurance Agency',
              MainAgencyEmail: 'contact@demoagency.com',
              Role: 'Agent',
              BusinessEmail: 'jane.doe.business@demoagency.com',
              PersonalEmail: 'jane.doe@example.com',
              Type: 'Agent'
            },
            {
              ContactName: 'Bob Johnson',
              Agency: 'Agency 2',
              MainAgencyEmail: 'contact@agency2.com',
              Role: 'Agent',
              BusinessEmail: 'bob.johnson.business@agency2.com',
              PersonalEmail: 'bob.johnson@example.com',
              Type: 'Agent'
            },
            {
              ContactName: 'Alice Williams',
              Agency: 'Agency 2',
              MainAgencyEmail: 'contact@agency2.com',
              Role: 'Agent',
              BusinessEmail: 'alice.williams.business@agency2.com',
              PersonalEmail: 'alice.williams@example.com',
              Type: 'Agent'
            },
            {
              ContactName: 'Charlie Brown',
              Agency: 'Agency 3',
              MainAgencyEmail: 'contact@agency3.com',
              Role: 'Agent',
              BusinessEmail: 'charlie.brown.business@agency3.com',
              PersonalEmail: 'charlie.brown@example.com',
              Type: 'Agent'
            },
            {
              ContactName: 'Diana Prince',
              Agency: 'Demo Insurance Agency',
              MainAgencyEmail: 'contact@demoagency.com',
              Role: 'Agent',
              BusinessEmail: 'diana.prince.business@demoagency.com',
              PersonalEmail: 'diana.prince@example.com',
              Type: 'Agent'
            },
            {
              ContactName: 'Edward Norton',
              Agency: 'Agency 4',
              MainAgencyEmail: 'contact@agency4.com',
              Role: 'Agent',
              BusinessEmail: 'edward.norton.business@agency4.com',
              PersonalEmail: 'edward.norton@example.com',
              Type: 'Agent'
            },
            {
              ContactName: 'Frank Miller',
              Agency: 'Agency 5',
              MainAgencyEmail: 'contact@agency5.com',
              Role: 'Agent',
              BusinessEmail: 'frank.miller.business@agency5.com',
              PersonalEmail: 'frank.miller@example.com',
              Type: 'Agent'
            }
          ],
          agencies: [
            {
              Agency: 'Demo Insurance Agency',
              ContactName: 'Michael Johnson',
              MainAgencyEmail: 'contact@demoagency.com',
              BusinessPhone: '(555) 100-0000',
              ContractDate: '2020-01-15',
              FullBusinessAddress: '123 Main Street, Los Angeles, CA 90001',
              AgencyStaffEmails: [
                'staff1@demoagency.com',
                'staff2@demoagency.com',
                'staff3@demoagency.com',
                'staff4@demoagency.com',
                'staff5@demoagency.com',
                'staff6@demoagency.com'
              ],
              PersonalEmail: 'michael.johnson@example.com',
              Role: 'Agency Owner',
              Type: 'Agency'
            },
            {
              Agency: 'Agency 2',
              ContactName: 'Sarah Williams',
              MainAgencyEmail: 'contact@agency2.com',
              BusinessPhone: '(555) 200-0000',
              ContractDate: '2019-06-20',
              FullBusinessAddress: '456 Oak Avenue, Houston, TX 77001',
              AgencyStaffEmails: [
                'staff1@agency2.com',
                'staff2@agency2.com',
                'staff3@agency2.com',
                'staff4@agency2.com'
              ],
              PersonalEmail: 'sarah.williams@example.com',
              Role: 'Agency Owner',
              Type: 'Agency'
            },
            {
              Agency: 'Agency 3',
              ContactName: 'Robert Davis',
              MainAgencyEmail: 'contact@agency3.com',
              BusinessPhone: '(555) 300-0000',
              ContractDate: '2021-03-10',
              FullBusinessAddress: '789 Pine Road, Miami, FL 33101',
              AgencyStaffEmails: [
                'staff1@agency3.com',
                'staff2@agency3.com',
                'staff3@agency3.com'
              ],
              PersonalEmail: 'robert.davis@example.com',
              Role: 'Agency Owner',
              Type: 'Agency'
            },
            {
              Agency: 'Agency 4',
              ContactName: 'Jennifer Martinez',
              MainAgencyEmail: 'contact@agency4.com',
              BusinessPhone: '(555) 400-0000',
              ContractDate: '2018-11-05',
              FullBusinessAddress: '321 Elm Street, New York, NY 10001',
              AgencyStaffEmails: [
                'staff1@agency4.com',
                'staff2@agency4.com'
              ],
              PersonalEmail: 'jennifer.martinez@example.com',
              Role: 'Agency Owner',
              Type: 'Agency'
            },
            {
              Agency: 'Agency 5',
              ContactName: 'David Wilson',
              MainAgencyEmail: 'contact@agency5.com',
              BusinessPhone: '(555) 500-0000',
              ContractDate: '2022-02-28',
              FullBusinessAddress: '654 Maple Drive, Phoenix, AZ 85001',
              AgencyStaffEmails: [
                'staff1@agency5.com',
                'staff2@agency5.com',
                'staff3@agency5.com',
                'staff4@agency5.com',
                'staff5@agency5.com'
              ],
              PersonalEmail: 'david.wilson@example.com',
              Role: 'Agency Owner',
              Type: 'Agency'
            }
          ],
          admins: [
            {
              ContactName: 'Admin User 1',
              Agency: 'Demo Insurance Agency',
              MainAgencyEmail: 'contact@demoagency.com',
              Role: 'Agency Admin',
              BusinessEmail: 'admin1.business@demoagency.com',
              PersonalEmail: 'admin1@example.com',
              Type: 'Admin'
            },
            {
              ContactName: 'Admin User 2',
              Agency: 'Agency 2',
              MainAgencyEmail: 'contact@agency2.com',
              Role: 'Agency Admin',
              BusinessEmail: 'admin2.business@agency2.com',
              PersonalEmail: 'admin2@example.com',
              Type: 'Admin'
            },
            {
              ContactName: 'Admin User 3',
              Agency: 'Agency 3',
              MainAgencyEmail: 'contact@agency3.com',
              Role: 'Agency Admin',
              BusinessEmail: 'admin3.business@agency3.com',
              PersonalEmail: 'admin3@example.com',
              Type: 'Admin'
            },
            {
              ContactName: 'Admin User 4',
              Agency: 'Demo Insurance Agency',
              MainAgencyEmail: 'contact@demoagency.com',
              Role: 'Agency Admin',
              BusinessEmail: 'admin4.business@demoagency.com',
              PersonalEmail: 'admin4@example.com',
              Type: 'Admin'
            },
            {
              ContactName: 'Admin User 5',
              Agency: 'Agency 4',
              MainAgencyEmail: 'contact@agency4.com',
              Role: 'Agency Admin',
              BusinessEmail: 'admin5.business@agency4.com',
              PersonalEmail: 'admin5@example.com',
              Type: 'Admin'
            }
          ]
        });
      }, 300);
    });
  }

  /**
   * Store a file in localStorage for demo purposes
   * Converts file to base64 and stores with metadata
   */
  storeFile(file: File): Promise<{ fileId: string; url: string; name: string; type: string; size: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const fileId = `demo_file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const fileData = {
            fileId: fileId,
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result as string, // base64 data
            createdAt: Date.now()
          };
          
          // Store in localStorage
          localStorage.setItem(`${this.FILE_STORAGE_PREFIX}${fileId}`, JSON.stringify(fileData));
          
          // Create a blob URL for immediate use
          const blob = this.base64ToBlob(fileData.data, fileData.type);
          const blobUrl = URL.createObjectURL(blob);
          
          resolve({
            fileId: fileId,
            url: blobUrl,
            name: file.name,
            type: file.type,
            size: file.size
          });
        } catch (error) {
          console.error('[DemoDataService] Error storing file:', error);
          reject(error);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Retrieve file data by fileId
   */
  getFile(fileId: string): { fileId: string; name: string; type: string; size: number; url: string } | null {
    try {
      const fileDataStr = localStorage.getItem(`${this.FILE_STORAGE_PREFIX}${fileId}`);
      if (!fileDataStr) {
        return null;
      }
      
      const fileData = JSON.parse(fileDataStr);
      // Create blob URL from stored base64 data
      const blob = this.base64ToBlob(fileData.data, fileData.type);
      const blobUrl = URL.createObjectURL(blob);
      
      return {
        fileId: fileData.fileId,
        name: fileData.name,
        type: fileData.type,
        size: fileData.size,
        url: blobUrl
      };
    } catch (error) {
      console.error('[DemoDataService] Error retrieving file:', error);
      return null;
    }
  }

  /**
   * Get file download URL (creates a new blob URL each time)
   */
  getFileDownloadUrl(fileId: string): string | null {
    const file = this.getFile(fileId);
    return file ? file.url : null;
  }

  /**
   * Convert base64 string to Blob
   */
  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64.split(',')[1] || base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * Clean up old files (optional - can be called periodically)
   */
  cleanupOldFiles(maxAge: number = 7 * 24 * 60 * 60 * 1000): void {
    // maxAge in milliseconds, default 7 days
    const now = Date.now();
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.FILE_STORAGE_PREFIX)) {
        try {
          const fileData = JSON.parse(localStorage.getItem(key) || '{}');
          if (fileData.createdAt && (now - fileData.createdAt) > maxAge) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // Invalid data, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`[DemoDataService] Cleaned up ${keysToRemove.length} old files`);
  }

  /**
   * Get file inline token - returns file URL and metadata for preview
   * Used when clicking on files in chat to open file viewer
   */
  getFileInlineToken(fileurl: string, fileId?: string): Promise<{ results: string; fileInfo: any }> {
    return new Promise((resolve, reject) => {
      try {
        // Strategy 1: Use provided fileId if available (most reliable)
        let foundFileId: string | null = fileId || null;
        
        // Strategy 2: Try to extract fileId from URL if not provided
        if (!foundFileId) {
          const fileIdMatch = fileurl.match(/demo_file_[a-zA-Z0-9_]+/);
          if (fileIdMatch) {
            foundFileId = fileIdMatch[0];
          }
        }
        
        // Strategy 3: Search through stored messages to find file by URL
        if (!foundFileId) {
          // Look through all channel message storages
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('demo_messages_')) {
              try {
                const messages = JSON.parse(localStorage.getItem(key) || '[]');
                // Search for message with matching URL (exact match or partial match for blob URLs)
                const message = messages.find((msg: any) => {
                  if (!msg || msg.messageType !== 'file') return false;
                  
                  // Check exact URL matches
                  if (msg.url === fileurl || msg.plainUrl === fileurl) {
                    return true;
                  }
                  
                  // For blob URLs, they might have been revoked, so check data field
                  if (fileurl.startsWith('blob:') && msg.data) {
                    try {
                      const msgData = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
                      // If we have fileId in data, this might be our file
                      if (msgData && msgData.fileId) {
                        return true;
                      }
                    } catch (e) {
                      // Not JSON, skip
                    }
                  }
                  
                  return false;
                });
                
                if (message) {
                  // Try to extract fileId from message data
                  try {
                    const msgData = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;
                    if (msgData && msgData.fileId) {
                      foundFileId = msgData.fileId;
                      break;
                    }
                  } catch (e) {
                    // data is not JSON, continue
                  }
                }
              } catch (e) {
                // Skip invalid entries
              }
            }
          }
        }
        
        // Strategy 4: If we have a fileId, get the file from storage
        if (foundFileId) {
          const storedFile = this.getFile(foundFileId);
          if (storedFile) {
            const mimeType = storedFile.type || 'application/octet-stream';
            const filename = storedFile.name || 'file';
            const isExcel = mimeType.includes('spreadsheet') || mimeType.includes('excel') || filename.toLowerCase().endsWith('.xls') || filename.toLowerCase().endsWith('.xlsx');
            const isPowerPoint = mimeType.includes('presentation') || mimeType.includes('powerpoint') || filename.toLowerCase().endsWith('.ppt') || filename.toLowerCase().endsWith('.pptx');
            const isWord = mimeType.includes('msword') || mimeType.includes('wordprocessingml') || filename.toLowerCase().endsWith('.doc') || filename.toLowerCase().endsWith('.docx');
            
            // For Office documents, convert blob URL to data URL so Microsoft Viewer can access it
            let fileUrl = storedFile.url;
            if ((isWord || isExcel || isPowerPoint) && fileUrl.startsWith('blob:')) {
              // Get the base64 data from storage and create a data URL
              try {
                const fileDataStr = localStorage.getItem(`${this.FILE_STORAGE_PREFIX}${foundFileId}`);
                if (fileDataStr) {
                  const fileData = JSON.parse(fileDataStr);
                  // fileData.data is already a base64 data URL (from FileReader.readAsDataURL)
                  if (fileData.data && fileData.data.startsWith('data:')) {
                    fileUrl = fileData.data;
                    console.log('[DemoDataService] Converted blob URL to data URL for Office document');
                  }
                }
              } catch (e) {
                console.warn('[DemoDataService] Could not convert blob to data URL:', e);
                // Fall back to blob URL - will show error in viewer
              }
            }
            
            resolve({
              results: fileUrl,
              fileInfo: {
                filename: filename,
                mimeType: mimeType,
                fileSize: storedFile.size || 0,
                isImage: mimeType.startsWith('image/'),
                isPdf: mimeType === 'application/pdf',
                isDocument: isWord || isExcel || isPowerPoint || mimeType.includes('document'),
                isWord: isWord,
                isExcel: isExcel,
                isPowerPoint: isPowerPoint
              }
            });
            return;
          }
        }
        
        // Strategy 5: If fileurl is a blob URL and we still don't have a fileId, 
        // we can't reliably match it, so reject with an error
        if (fileurl.startsWith('blob:') && !foundFileId) {
          console.warn('[DemoDataService] Cannot match blob URL without fileId:', fileurl);
          reject(new Error('Cannot find file: blob URL requires fileId to match'));
          return;
        }
        
        // Fallback: Return the URL as-is with generic info
        // Try to infer file type from URL extension
        const urlLower = fileurl.toLowerCase();
        const filename = fileurl.split('/').pop() || 'file';
        let inferredMimeType = 'application/octet-stream';
        let isImage = false;
        let isPdf = false;
        let isExcel = false;
        let isPowerPoint = false;
        let isWord = false;
        
        if (urlLower.includes('.jpg') || urlLower.includes('.jpeg') || urlLower.includes('.png') || urlLower.includes('.gif')) {
          inferredMimeType = 'image/jpeg';
          isImage = true;
        } else if (urlLower.includes('.pdf')) {
          inferredMimeType = 'application/pdf';
          isPdf = true;
        } else if (urlLower.includes('.xls') || urlLower.includes('.xlsx')) {
          inferredMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          isExcel = true;
        } else if (urlLower.includes('.ppt') || urlLower.includes('.pptx')) {
          inferredMimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
          isPowerPoint = true;
        } else if (urlLower.includes('.doc') || urlLower.includes('.docx')) {
          inferredMimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          isWord = true;
        }
        
        resolve({
          results: fileurl,
          fileInfo: {
            filename: filename,
            mimeType: inferredMimeType,
            fileSize: 0,
            isImage: isImage,
            isPdf: isPdf,
            isDocument: isWord || isExcel || isPowerPoint,
            isWord: isWord,
            isExcel: isExcel,
            isPowerPoint: isPowerPoint
          }
        });
      } catch (error) {
        console.error('[DemoDataService] Error getting file inline token:', error);
        reject(error);
      }
    });
  }
}
